/**
 * MA-UI-056 / MA-UI-057 / MA-UI-058 / MA-UI-061-A: Profit Path Builder
 * MA-UI-072: Guided Entry Layer integration
 * 
 * Phase 1: UI shell with pixel-perfect parity to Funnel Builder (COMPLETE)
 * Phase 2A: State management, persistence, Foundation step (THIS FILE)
 * 
 * PIXEL-PERFECT PARITY with Funnel Builder (FunnelStepsPanel + FunnelPropertiesPanel)
 * Layout MUST match VisualFunnelBuilder.jsx exactly.
 * 
 * DO NOT modify layout, spacing, or styling - UI is locked.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, useBlocker } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Save,
  Compass,
  Users,
  Gift,
  MessageSquare,
  Image,
  Filter,
  FileText,
  CheckCircle,
  Layers,
  MousePointerClick,
  Search,
  ListTree,
  Target,
  Sparkles,
  Palette,
  Wrench,
  Loader2,
  Check,
  Building2,
  ChevronRight,
  Lock,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  ExternalLink,
  Plug,
  Shield,
  Calendar,
  Type,
  Settings,
  ShieldCheck,
  Send,
  Zap,
  Play,
  ArrowRight,
  Globe,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import {
  draftSyncOptions,
  shouldMarkBookingPatched,
} from "@/pages/profitPathDraftSync";
import {
  adoptFunnelDraftVersion,
  forgetFunnelDraftVersion,
  saveFunnelDraftVersioned,
} from "@/hooks/useFunnels";
import useActivation from "@/hooks/useActivation";
import { useEditorMode } from "@/contexts/PageTitleContext";
import { toast } from "@/hooks/use-toast";
import { useModuleAccessLevel } from "@/hooks/usePermissions";
// LandingPagePerformanceStrip moved to InlineLPEditor
import LPMiniStep from "@/components/landing-pages/LPMiniStep";
import { AIFieldAssist } from "@/components/builder/AIFieldAssist";
import { buildStrategyContext } from "@/lib/strategyContext";
import { CompactFunnelPreview } from "@/components/builder/CompactFunnelPreview";
import { FunnelLivePreview } from "@/components/builder/FunnelLivePreview";
import { BusinessKnowledge } from "@/components/builder/BusinessKnowledge";
import { KnowledgeStalenessIndicator, KnowledgeStrengthWarning } from "@/components/builder/KnowledgeStaleness";
import { ConsentEditor } from "@/components/shared/ConsentEditor";
import { getActiveTokens, setActiveBrand } from "@/components/lp-builder/LPSectionPreview";
import { extractLPTheme } from "@/lib/resolveThemeSource";
import { resolveTheme, PRESETS, themeToFunnelAppearance } from "@/lib/themeEngine";
import { convertThemeToLpTokens } from "@/lib/themeAdapters";

// =============================================================================
// Step configuration - matches NODE_TYPE_CONFIG pattern from FunnelStepsPanel
// =============================================================================
const BUILDER_STEPS = [
  {
    id: "context",
    label: "Context",
    icon: Building2,
    color: "text-teal-400",
    bgColor: "bg-teal-500/15",
    description: "Define where this Profit Path will live and what it is associated with.",
  },
  {
    id: "foundation",
    label: "Foundation",
    icon: Compass,
    color: "text-blue-400",
    bgColor: "bg-blue-500/15",
    description: "Define the core strategy and positioning.",
  },
  {
    id: "audience",
    label: "Audience",
    icon: Users,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/15",
    description: "Identify target audience segments.",
  },
  {
    id: "offer",
    label: "Offer",
    icon: Gift,
    color: "text-amber-400",
    bgColor: "bg-amber-500/15",
    description: "Define the value proposition.",
  },
  {
    id: "messaging",
    label: "Messaging",
    icon: MessageSquare,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/15",
    description: "Create coordinated messaging.",
  },
  {
    id: "creative",
    label: "Creative",
    icon: Image,
    color: "text-pink-400",
    bgColor: "bg-pink-500/15",
    description: "Generate visual assets.",
  },
  {
    id: "funnel",
    label: "Funnel",
    icon: Filter,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/15",
    description: "Build the lead capture flow.",
  },
  {
    id: "script",
    label: "Script",
    icon: FileText,
    color: "text-orange-400",
    bgColor: "bg-orange-500/15",
    description: "Create sales scripts.",
  },
  {
    id: "landing_page",
    label: "Landing Page",
    icon: Layers,
    color: "text-rose-400",
    bgColor: "bg-rose-500/15",
    description: "Generate conversion landing page.",
  },
  {
    id: "review",
    label: "Review",
    icon: CheckCircle,
    color: "text-green-400",
    bgColor: "bg-green-500/15",
    description: "Review and finalize.",
  },
];

// =============================================================================
// Default Draft State
// =============================================================================
const createEmptyDraft = () => ({
  name: "Untitled Profit Path",
  vertical: "",
  status: "draft",
  builder_data: {
    context: {
      workspace: "",
      vertical: "",
      channel: "",
      platform: "",
      campaign_name: "",
      campaign_ref: "",
    },
    foundation: {
      strategic_objective: "",
      core_strategy: "",
    },
    audience: {
      primary_audience: "",
      situation: "",
      core_problem: "",
      emotional_driver: "",
      awareness_stage: "",
      trigger_event: "",
    },
    offer: {
      offer_name: "",
      offer_type: "",
      core_promise: "",
      primary_benefit: "",
      differentiation: "",
      trust_proof: "",
      primary_cta: "",
      urgency: "",
    },
    messaging: {
      angle: "",
      thesis: "",
      unique_mechanism: "",
      message_enemy: "",
      primary_hook: "",
      scroll_stopper: "",
      primary_emotion: "",
      emotional_outcome: "",
      emotional_narrative: "",
      tone: "",
      voice_notes: "",
    },
    creative: {
      concepts: [],
      generation_status: "idle",
    },
    landing_page: {
      page_type: null,
      hero: {},
      problem: {},
      mechanism: {},
      social_proof: {},
      cta: {},
      seo: {},
      personalization: { enabled: false, mappings: [] },
      generation_status: "idle",
      generation_source: null,
    },
    theme: null,
    theme_source: null,
  },
});

// =============================================================================
// MA-UI-072: Entry Profile Biasing Logic
// MA-UI-074: Entry Bias System Integrity Refinements
// MA-UI-075: Data Access + Bias Integrity Hardening
// =============================================================================

/**
 * MA-UI-074: Helper to create a biased field with source tracking
 * All biased fields include metadata: { value, source: "entry_bias" | "user" }
 * MA-UI-075: ONLY use on leaf-level primitive fields (string, select values)
 */
const createBiasedField = (value, source = "entry_bias") => ({
  value,
  source,
  _biased_at: source === "entry_bias" ? new Date().toISOString() : undefined,
});

/**
 * MA-UI-075: CENTRALIZED FIELD ACCESS UTILITY
 * Single source of truth for reading field values
 * 
 * Handles both data shapes:
 * 1. Raw values: "string"
 * 2. Biased values: { value: "string", source: "entry_bias" | "user" }
 * 
 * @param {*} field - The field to extract value from
 * @param {string} defaultValue - Default value if field is null/undefined
 * @returns {string} The raw value
 */
const getFieldValue = (field, defaultValue = "") => {
  if (field === null || field === undefined) {
    return defaultValue;
  }
  if (typeof field === "object" && "value" in field) {
    return field.value ?? defaultValue;
  }
  return field;
};

/**
 * MA-UI-075: Get field source (for internal tracking)
 * @param {*} field - The field to check
 * @returns {"entry_bias" | "user" | "raw"} The source
 */
const getFieldSource = (field) => {
  if (field && typeof field === "object" && "source" in field) {
    return field.source;
  }
  return "raw"; // Not a biased field
};

/**
 * MA-UI-074: Check if a field was set by entry bias
 */
const isEntryBiased = (field) => {
  return field && typeof field === "object" && field.source === "entry_bias";
};

/**
 * MA-UI-075: Normalize a single field for API calls
 * Converts { value, source } → raw value
 */
const normalizeField = (field) => {
  return getFieldValue(field);
};

/**
 * MA-UI-075 / MA-UI-075-A: Deep Recursive normalizeForAPI()
 * 
 * CRITICAL: Fully strips { value, source } wrappers from ALL data structures
 * Works recursively across nested objects and arrays.
 * 
 * Handles:
 * 1. Primitives (string/number/boolean) → return as-is
 * 2. Biased fields { value, source } → return value
 * 3. Arrays → iterate and normalize each element recursively
 * 4. Nested objects → traverse ALL keys and normalize recursively
 * 
 * @param {*} input - Any value to normalize
 * @returns {*} Normalized value with no { value, source } wrappers
 */
const normalizeForAPI = (input) => {
  // Handle null/undefined
  if (input === null || input === undefined) {
    return input;
  }

  // Handle primitives (string, number, boolean)
  if (typeof input !== "object") {
    return input;
  }

  // CRITICAL: Check for biased field BEFORE array check
  // Biased field has exactly { value, source } structure
  if (
    typeof input === "object" &&
    input !== null &&
    "value" in input &&
    "source" in input &&
    (input.source === "entry_bias" || input.source === "user")
  ) {
    // Return the unwrapped value (may need further normalization if value is object/array)
    return normalizeForAPI(input.value);
  }

  // Handle arrays - recurse into each element
  if (Array.isArray(input)) {
    return input.map(item => normalizeForAPI(item));
  }

  // Handle regular objects - recurse into ALL properties
  if (typeof input === "object") {
    const result = {};
    for (const key in input) {
      // Skip internal metadata fields (prefixed with _)
      if (key.startsWith("_")) continue;
      // Skip prototype properties
      if (!Object.prototype.hasOwnProperty.call(input, key)) continue;
      
      result[key] = normalizeForAPI(input[key]);
    }
    return result;
  }

  // Fallback - return as-is
  return input;
};

/**
 * MA-UI-075: Source-Aware Validation (Internal Tracking)
 * Counts fields by source for future AI/optimization layers
 * DOES NOT affect validation pass/fail logic
 */
const collectSourceCounts = (builderData) => {
  const counts = {
    entry_bias: 0,
    user: 0,
    raw: 0,
  };
  
  const countSources = (obj, path = "") => {
    if (obj === null || obj === undefined) return;
    
    if (Array.isArray(obj)) {
      obj.forEach((item, i) => countSources(item, `${path}[${i}]`));
      return;
    }
    
    if (typeof obj === "object") {
      // Check if this is a biased field
      if ("value" in obj && "source" in obj) {
        counts[obj.source] = (counts[obj.source] || 0) + 1;
        return;
      }
      
      // Recurse into object properties
      for (const [key, value] of Object.entries(obj)) {
        if (key.startsWith("_")) continue;
        countSources(value, `${path}.${key}`);
      }
    }
  };
  
  countSources(builderData);
  return counts;
};

/**
 * MA-UI-072 + MA-UI-074: Apply Entry Profile Biasing
 * 
 * CRITICAL RULES (MA-UI-074):
 * 1. Runs ONLY ONCE on initialization - never reapplied
 * 2. All biased fields include { value, source } metadata
 * 3. Funnel type is a RECOMMENDATION, not locked
 * 4. Messaging bias is DIRECTIONAL, not hard assignment
 * 5. Script CTA respects execution_intent > entry_bias hierarchy
 */
const applyEntryProfileBias = (draft, entryProfile) => {
  if (!entryProfile) return draft;

  const { goal, offer_type, conversion_style, traffic_temperature } = entryProfile;

  // Clone draft to avoid mutation
  const biasedDraft = JSON.parse(JSON.stringify(draft));

  // ==========================================================================
  // MA-UI-074: Store entry_profile as IMMUTABLE reference
  // This object must NOT be mutated across steps
  // ==========================================================================
  biasedDraft.builder_data.entry_profile = {
    ...entryProfile,
    _applied_at: new Date().toISOString(),
    _bias_version: "1.0",
  };

  // ==========================================================================
  // MA-UI-074: Mark that bias has been applied (prevents reapplication)
  // ==========================================================================
  biasedDraft.builder_data._entry_bias_applied = true;

  // ==========================================================================
  // Foundation Step Biasing (with source tracking)
  // ==========================================================================
  const strategicObjectiveValue = {
    generate_leads: "Capture qualified leads through value-first engagement",
    book_appointments: "Convert interest into scheduled consultations",
    close_sales: "Drive immediate purchase decisions",
  }[goal] || "";

  const coreStrategyValue = (() => {
    if (goal === "generate_leads") {
      return conversion_style === "educate_first" 
        ? "Lead with education to build trust, then capture contact info"
        : "Quick qualification and immediate lead capture";
    }
    if (goal === "book_appointments") {
      return conversion_style === "educate_first"
        ? "Demonstrate expertise first, then invite to consultation"
        : "Direct booking path with clear value proposition";
    }
    if (goal === "close_sales") {
      return conversion_style === "educate_first"
        ? "Build case for purchase through guided discovery"
        : "Streamlined purchase path with urgency triggers";
    }
    return "";
  })();

  biasedDraft.builder_data.foundation = {
    ...biasedDraft.builder_data.foundation,
    strategic_objective: createBiasedField(strategicObjectiveValue),
    core_strategy: createBiasedField(coreStrategyValue),
  };

  // ==========================================================================
  // Offer Step Biasing (with source tracking)
  // ==========================================================================
  const offerTypeValue = {
    education_review: "Education",
    consultation: "Consultation",
    direct_sale: "Direct Sale",
  }[offer_type] || "";

  if (offerTypeValue) {
    biasedDraft.builder_data.offer = {
      ...biasedDraft.builder_data.offer,
      offer_type: createBiasedField(offerTypeValue),
    };
  }

  // ==========================================================================
  // Audience Step Biasing (with source tracking)
  // ==========================================================================
  if (traffic_temperature) {
    const awarenessStageValue = {
      cold: "unaware",
      warm: "problem_aware",
      hot: "solution_aware",
    }[traffic_temperature] || "";

    if (awarenessStageValue) {
      biasedDraft.builder_data.audience = {
        ...biasedDraft.builder_data.audience,
        awareness_stage: createBiasedField(awarenessStageValue),
      };
    }
  }

  // ==========================================================================
  // Messaging Step Biasing (DIRECTIONAL - not hard assignment)
  // MA-UI-074: These are initial suggestions, user edits override permanently
  // ==========================================================================
  const toneValue = (() => {
    if (offer_type === "education_review") return "consultative";
    if (offer_type === "consultation") return "consultative";
    if (offer_type === "direct_sale") return conversion_style === "fast_conversion" ? "direct" : "authoritative";
    return "";
  })();

  const primaryEmotionValue = {
    generate_leads: "curiosity",
    book_appointments: "security",
    close_sales: "urgency",
  }[goal] || "";

  const emotionalOutcomeValue = {
    generate_leads: "confidence",
    book_appointments: "certainty",
    close_sales: "relief",
  }[goal] || "";

  biasedDraft.builder_data.messaging = {
    ...biasedDraft.builder_data.messaging,
    tone: toneValue ? createBiasedField(toneValue) : biasedDraft.builder_data.messaging.tone,
    primary_emotion: primaryEmotionValue ? createBiasedField(primaryEmotionValue) : biasedDraft.builder_data.messaging.primary_emotion,
    emotional_outcome: emotionalOutcomeValue ? createBiasedField(emotionalOutcomeValue) : biasedDraft.builder_data.messaging.emotional_outcome,
  };

  // ==========================================================================
  // Funnel Step Biasing (RECOMMENDATION - not locked)
  // MA-UI-074: User can freely change funnel type, system does NOT re-force
  // ==========================================================================
  const funnelTypeValue = {
    generate_leads: "diagnostic",
    book_appointments: "transformation",
    close_sales: "direct_conversion",
  }[goal] || "diagnostic";

  biasedDraft.builder_data.funnel = {
    ...biasedDraft.builder_data.funnel,
    inferred_type: createBiasedField(funnelTypeValue),
    is_recommendation: true, // MA-UI-074: Mark as recommendation, not locked
  };

  // ==========================================================================
  // Script Step Biasing (SECONDARY to execution_intent)
  // MA-UI-074: Execution intent is PRIMARY, entry bias is SECONDARY
  // ==========================================================================
  const ctaPressureValue = conversion_style === "fast_conversion" ? "high" : "low";

  biasedDraft.builder_data.script = {
    ...biasedDraft.builder_data.script,
    tone: toneValue ? createBiasedField(toneValue) : biasedDraft.builder_data.script.tone,
    cta_pressure: createBiasedField(ctaPressureValue),
    // MA-UI-074: execution_intent always takes priority over entry bias for CTA
    _entry_bias_priority: "secondary",
  };

  // ==========================================================================
  // Creative Step Biasing (with source tracking)
  // ==========================================================================
  const hookStyleValue = {
    generate_leads: "curiosity",
    book_appointments: "trust",
    close_sales: "urgency",
  }[goal] || "curiosity";

  biasedDraft.builder_data.creative = {
    ...biasedDraft.builder_data.creative,
    hook_style: createBiasedField(hookStyleValue),
    emotional_driver: primaryEmotionValue ? createBiasedField(primaryEmotionValue) : biasedDraft.builder_data.creative.emotional_driver,
  };

  return biasedDraft;
};

// =============================================================================
// Awareness Stage Options
// =============================================================================
// MA-UI-112: Display formatting for snake_case enum values
function toTitleCase(str) {
  if (!str) return "";
  return str.replace(/[_-]/g, " ").split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

const AWARENESS_STAGES = [
  { value: "unaware", label: "Unaware", description: "Don't know they have a problem" },
  { value: "problem_aware", label: "Problem Aware", description: "Know the problem, not the solutions" },
  { value: "solution_aware", label: "Solution Aware", description: "Know solutions exist, evaluating options" },
  { value: "product_aware", label: "Product Aware", description: "Know your product, need convincing" },
];

// =============================================================================
// Primary Emotion Options (MA-UI-064)
// =============================================================================
const PRIMARY_EMOTIONS = [
  { value: "fear", label: "Fear", description: "Fear of loss or making mistakes" },
  { value: "relief", label: "Relief", description: "Relief from stress or burden" },
  { value: "curiosity", label: "Curiosity", description: "Curiosity to learn more" },
  { value: "urgency", label: "Urgency", description: "Urgency to act now" },
  { value: "security", label: "Security", description: "Desire for safety and stability" },
];

// =============================================================================
// Emotional Outcome Options (MA-UI-065)
// =============================================================================
const EMOTIONAL_OUTCOMES = [
  { value: "relief", label: "Relief", description: "Freedom from worry or burden" },
  { value: "confidence", label: "Confidence", description: "Certainty in their decision" },
  { value: "certainty", label: "Certainty", description: "Clear understanding of the path forward" },
  { value: "security", label: "Security", description: "Peace of mind and stability" },
];

// =============================================================================
// Communication Tone Options (MA-UI-064)
// =============================================================================
const COMMUNICATION_TONES = [
  { value: "educational", label: "Educational", description: "Informative and teaching" },
  { value: "consultative", label: "Consultative", description: "Advisory and guiding" },
  { value: "urgent", label: "Urgent", description: "Time-sensitive and pressing" },
  { value: "reassuring", label: "Reassuring", description: "Calming and confidence-building" },
];

// =============================================================================
// Phase 5: Staleness Detection — Indicator Component
// =============================================================================
function StalenessIndicator({ draft, stepKey, onRegenerate, onKeep, isGenerating }) {
  const messaging = draft?.builder_data?.messaging || {};
  const stepData = draft?.builder_data?.[stepKey] || {};
  const currentHash = messaging.messaging_hash;
  const storedHash = stepData.messaging_hash;

  // No staleness if no hashes exist (output not generated yet, or messaging not synthesized)
  if (!currentHash || !storedHash) return null;
  // Not stale
  if (currentHash === storedHash) return null;

  return (
    <div
      data-testid={`staleness-indicator-${stepKey}`}
      className="flex items-center justify-between gap-3 px-3 py-2 mb-3 rounded-md bg-amber-900/30 border border-amber-700/50"
    >
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0" />
        <span className="text-xs text-amber-300 truncate">Out of sync with strategy</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          data-testid={`staleness-regenerate-${stepKey}`}
          onClick={onRegenerate}
          disabled={isGenerating}
          className="text-[10px] font-medium px-2 py-1 rounded bg-amber-600/40 hover:bg-amber-600/60 text-amber-200 disabled:opacity-50 transition-colors"
        >
          Regenerate
        </button>
        <button
          data-testid={`staleness-keep-${stepKey}`}
          onClick={() => {
            // MA-ARCH-189: Persist dismissal metadata for analytics
            onKeep();
          }}
          className="text-[10px] font-medium px-2 py-1 rounded bg-slate-700/60 hover:bg-slate-700/80 text-slate-400 transition-colors"
        >
          Keep
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Left Rail: Steps Panel (UI LOCKED - DO NOT MODIFY)
// =============================================================================
// =============================================================================
// Standalone step completion checker (used by sidebar + work area)
// =============================================================================
function checkStepCompletion(stepId, draft, lpApiContext = null) {
    if (stepId === "context") {
      const ctx = draft?.builder_data?.context;
      const workspace = ctx?.workspace?.trim();
      const vertical = ctx?.vertical?.trim();
      const channel = ctx?.channel?.trim();
      const platform = ctx?.platform?.trim();
      const campaignName = ctx?.campaign_name?.trim();
      return !!(workspace && vertical && channel && platform && campaignName);
    }
    if (stepId === "foundation") {
      const name = draft?.name?.trim();
      const objective = getFieldValue(draft?.builder_data?.foundation?.strategic_objective, "").trim();
      const strategy = getFieldValue(draft?.builder_data?.foundation?.core_strategy, "").trim();
      return !!(name && objective && strategy);
    }
    if (stepId === "audience") {
      const primaryAudience = draft?.builder_data?.audience?.primary_audience?.trim();
      const coreProblem = draft?.builder_data?.audience?.core_problem?.trim();
      const awarenessStage = getFieldValue(draft?.builder_data?.audience?.awareness_stage, "").trim();
      return !!(primaryAudience && coreProblem && awarenessStage);
    }
    if (stepId === "offer") {
      const offer = draft?.builder_data?.offer;
      const offerName = offer?.offer_name?.trim();
      const offerType = getFieldValue(offer?.offer_type, "").trim();
      const corePromise = offer?.core_promise?.trim();
      const primaryBenefit = offer?.primary_benefit?.trim();
      const differentiation = offer?.differentiation?.trim();
      const primaryCta = offer?.primary_cta?.trim();
      return !!(offerName && offerType && corePromise && primaryBenefit && differentiation && primaryCta);
    }
    if (stepId === "messaging") {
      const messaging = draft?.builder_data?.messaging;
      const angle = messaging?.angle?.trim();
      const thesis = messaging?.thesis?.trim();
      const uniqueMechanism = messaging?.unique_mechanism?.trim();
      const messageEnemy = messaging?.message_enemy?.trim();
      const primaryHook = messaging?.primary_hook?.trim();
      const primaryEmotion = getFieldValue(messaging?.primary_emotion, "").trim();
      const emotionalOutcome = getFieldValue(messaging?.emotional_outcome, "").trim();
      const tone = getFieldValue(messaging?.tone, "").trim();
      return !!(angle && thesis && uniqueMechanism && messageEnemy && primaryHook && primaryEmotion && emotionalOutcome && tone);
    }
    if (stepId === "creative") {
      const concepts = draft?.builder_data?.creative?.concepts || [];
      return concepts.some(c => c.is_selected);
    }
    if (stepId === "funnel") {
      return !!draft?.builder_data?.funnel?.funnel_instance_id;
    }
    if (stepId === "script") {
      return !!draft?.builder_data?.script?.script_instance_id;
    }
    if (stepId === "landing_page") {
      // MA-LP-PROD-029 P2-5: Read LP completion from LP API context (single source of truth).
      // LP is complete if it has a linked LP with a usable version snapshot.
      if (lpApiContext?.linked && lpApiContext?.current_version?.snapshot_json) {
        const snapshot = lpApiContext.current_version.snapshot_json;
        // Support both flat format (snapshot.hero) and sections[] format
        const sections = snapshot.sections || [];
        const heroSection = sections.find(s => s.type === "hero");
        const heroData = heroSection?.data || snapshot.hero || {};
        const hasHero = !!(heroData.headline?.trim());
        return hasHero;
      }
      return false;
    }
    if (stepId === "review") {
      // MA-PPB-STATE-058: Review is only complete when user explicitly approves.
      return !!draft?.builder_data?.review?.approved_at;
    }
    return false;
}

function BuilderStepsPanel({ steps, activeStep, onStepClick, draft, lpApiContext }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const showSearch = steps.length > 7;

  // MA-UI-075: Use centralized getFieldValue for all field access

  const filteredSteps = useMemo(() => {
    if (!searchQuery.trim()) return steps;
    const query = searchQuery.toLowerCase();
    return steps.filter((s) => s.label.toLowerCase().includes(query));
  }, [steps, searchQuery]);

  // Calculate completion status for each step (MA-UI-075: use getFieldValue)
  const getStepCompletion = useCallback((stepId) => checkStepCompletion(stepId, draft, lpApiContext), [draft, lpApiContext]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header - EXACT: FunnelStepsPanel */}
      <div className="flex-shrink-0 p-3 border-b border-white/10">
        <div className="flex items-center justify-between mb-0">
          <div className="flex items-center gap-2">
            <ListTree className="h-4 w-4 text-indigo-400" />
            <h3 className="text-sm font-medium text-slate-200">Steps</h3>
            <span className="text-xs text-slate-500">({steps.length})</span>
          </div>
          {showSearch && !searchOpen && (
            <button
              onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 50); }}
              className="p-1 rounded hover:bg-white/10 text-slate-500 hover:text-slate-300 transition-colors"
              data-testid="steps-search-toggle"
            >
              <Search className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {showSearch && searchOpen && (
          <div className="relative mt-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <Input
              ref={searchRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onBlur={() => { if (!searchQuery.trim()) setSearchOpen(false); }}
              placeholder="Search steps..."
              className="h-7 pl-7 text-xs bg-slate-800/50 border-slate-700/50"
            />
          </div>
        )}
      </div>

      {/* Steps List - EXACT: FunnelStepsPanel */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        <div className="space-y-0.5">
          {filteredSteps.map((step) => {
            const Icon = step.icon;
            const isSelected = activeStep === step.id;
            const stepNumber = steps.findIndex((s) => s.id === step.id) + 1;
            const isComplete = getStepCompletion(step.id);

            // Progressive unlock: step is accessible only if ALL prior steps are complete
            const isLocked = stepNumber > 1 && steps.slice(0, stepNumber - 1).some(s => !getStepCompletion(s.id));

            return (
              <div key={step.id} className="group">
                <div
                  onClick={() => !isLocked && onStepClick(step.id)}
                  data-testid={`builder-step-${step.id}`}
                  className={cn(
                    "flex items-center gap-2 h-9 px-2 rounded-lg",
                    "text-left transition-colors duration-150",
                    isLocked && "opacity-40 cursor-not-allowed",
                    !isLocked && "cursor-pointer",
                    isSelected && !isLocked && [
                      "bg-indigo-500/15",
                      "border-l-2 border-l-indigo-400 -ml-0.5 pl-[9px]",
                    ],
                    !isSelected && !isLocked && "hover:bg-white/5"
                  )}
                >
                  {/* Step Number */}
                  <span className="w-5 text-xs text-white/40 text-center flex-shrink-0">
                    {stepNumber}
                  </span>

                  {/* Step Icon */}
                  <div
                    className="w-6 h-6 flex items-center justify-center flex-shrink-0"
                  >
                    <Icon className={cn("h-3.5 w-3.5", step.color)} />
                  </div>

                  {/* Label */}
                  <span
                    className={cn(
                      "flex-1 text-sm truncate",
                      isSelected ? "text-slate-100 font-medium" : "text-slate-300"
                    )}
                  >
                    {step.label}
                  </span>

                  {/* Completion indicator — only show on unlocked steps */}
                  {isComplete && !isLocked && (
                    <Check className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
                  )}
                  {isLocked && (
                    <Lock className="h-3 w-3 text-slate-600 flex-shrink-0" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Center Panel: Foundation Step (MA-UI-061-A - REAL CONTENT)
// =============================================================================
// =============================================================================
// Center Panel: Context Step (MA-UI-076-V3: Association Layer)
// =============================================================================
const CHANNEL_OPTIONS = [
  { value: "paid_traffic", label: "Paid Traffic" },
  { value: "organic_traffic", label: "Organic Traffic" },
  { value: "referral_traffic", label: "Referral Traffic" },
];

const PLATFORM_OPTIONS = [
  { value: "meta", label: "Meta (Facebook / Instagram)" },
  { value: "google", label: "Google" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "tiktok", label: "TikTok" },
  { value: "direct", label: "Direct" },
];

function ContextStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete }) {
  const ctx = draft?.builder_data?.context || {};
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCampaignsLoading(true);
    api.get("/campaigns")
      .then((res) => {
        if (!cancelled) setCampaigns(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setCampaigns([]);
      })
      .finally(() => {
        if (!cancelled) setCampaignsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="context" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-5 space-y-6">
          {/* A. Business Context */}
          <div className="pb-3 border-b border-white/[0.06]">
            <h3 className="text-sm font-medium text-slate-200">Business Context</h3>
            <p className="text-xs text-slate-500 mt-1">
              Anchor this Profit Path to a business entity and market
            </p>
          </div>

          {/* Workspace */}
          <div className="space-y-1.5">
            <Label htmlFor="ctx-workspace" className="text-xs font-medium text-slate-400">
              Workspace <span className="text-red-400">*</span>
            </Label>
            <p className="text-[11px] text-slate-600 mb-1.5">
              The business or account this Profit Path belongs to
            </p>
            <Input
              id="ctx-workspace"
              value={ctx.workspace || ""}
              onChange={(e) => onFieldChange("builder_data.context.workspace", e.target.value)}
              placeholder="e.g., Jones Insurance Agency"
              className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
              data-testid="context-workspace-input"
            />
          </div>

          {/* Vertical */}
          <div className="space-y-1.5">
            <Label htmlFor="ctx-vertical" className="text-xs font-medium text-slate-400">
              Vertical <span className="text-red-400">*</span>
            </Label>
            <p className="text-[11px] text-slate-600 mb-1.5">
              The specific product line or market this Profit Path is for
            </p>
            <Input
              id="ctx-vertical"
              value={ctx.vertical || ""}
              onChange={(e) => onFieldChange("builder_data.context.vertical", e.target.value)}
              placeholder="e.g., Medicare, Mortgage Protection, Annuities"
              className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
              data-testid="context-vertical-input"
            />
          </div>

          {/* B. Traffic Context */}
          <div className="pb-3 border-b border-white/[0.06]">
            <h3 className="text-sm font-medium text-slate-200">Traffic Context</h3>
            <p className="text-xs text-slate-500 mt-1">
              Define the traffic strategy and platform for this path
            </p>
          </div>

          {/* Channel */}
          <div className="space-y-1.5">
            <Label htmlFor="ctx-channel" className="text-xs font-medium text-slate-400">
              Channel <span className="text-red-400">*</span>
            </Label>
            <p className="text-[11px] text-slate-600 mb-1.5">
              The type of traffic strategy driving this Profit Path
            </p>
            <Select
              value={ctx.channel || undefined}
              onValueChange={(val) => onFieldChange("builder_data.context.channel", val)}
            >
              <SelectTrigger
                id="ctx-channel"
                className="w-full h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 focus:ring-indigo-500/40"
                data-testid="context-channel-select"
              >
                <SelectValue placeholder="Select channel..." />
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Platform */}
          <div className="space-y-1.5">
            <Label htmlFor="ctx-platform" className="text-xs font-medium text-slate-400">
              Platform <span className="text-red-400">*</span>
            </Label>
            <p className="text-[11px] text-slate-600 mb-1.5">
              The platform where this traffic originates
            </p>
            <Select
              value={ctx.platform || undefined}
              onValueChange={(val) => onFieldChange("builder_data.context.platform", val)}
            >
              <SelectTrigger
                id="ctx-platform"
                className="w-full h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 focus:ring-indigo-500/40"
                data-testid="context-platform-select"
              >
                <SelectValue placeholder="Select platform..." />
              </SelectTrigger>
              <SelectContent>
                {PLATFORM_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* C. Campaign Association */}
          <div className="pb-3 border-b border-white/[0.06]">
            <h3 className="text-sm font-medium text-slate-200">Campaign Association</h3>
            <p className="text-xs text-slate-500 mt-1">
              Link this path to a specific campaign
            </p>
          </div>

          {/* Campaign Name */}
          <div className="space-y-1.5">
            <Label htmlFor="ctx-campaign-name" className="text-xs font-medium text-slate-400">
              Campaign <span className="text-red-400">*</span>
            </Label>
            <p className="text-[11px] text-slate-600 mb-1.5">
              The specific campaign this Profit Path is supporting
            </p>
            <Select
              value={ctx.campaign_name || undefined}
              onValueChange={(val) => {
                onFieldChange("builder_data.context.campaign_name", val);
                const match = campaigns.find((c) => c.name === val);
                if (match?.external_id) {
                  onFieldChange("builder_data.context.campaign_ref", match.external_id);
                }
              }}
            >
              <SelectTrigger
                id="ctx-campaign-name"
                className="w-full h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 focus:ring-indigo-500/40"
                data-testid="context-campaign-name-select"
              >
                <SelectValue placeholder={campaignsLoading ? "Loading campaigns..." : "Select campaign..."} />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.name}>
                    <span className="flex items-center gap-2">
                      <span className="truncate">{c.name}</span>
                      {c.platform && (
                        <span className={cn(
                          "shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium leading-none",
                          c.platform === "meta" ? "bg-blue-500/15 text-blue-400" :
                          c.platform === "google" ? "bg-emerald-500/15 text-emerald-400" :
                          c.platform === "tiktok" ? "bg-pink-500/15 text-pink-400" :
                          c.platform === "linkedin" ? "bg-sky-500/15 text-sky-400" :
                          "bg-slate-500/15 text-slate-400"
                        )}>
                          {c.platform === "meta" ? "Meta" : c.platform === "google" ? "Google" : c.platform === "tiktok" ? "TikTok" : c.platform === "linkedin" ? "LinkedIn" : c.platform}
                        </span>
                      )}
                      {c.status && (
                        <span className={cn(
                          "shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium leading-none",
                          c.status === "active" ? "bg-green-500/15 text-green-400" :
                          c.status === "paused" ? "bg-amber-500/15 text-amber-400" :
                          "bg-slate-500/15 text-slate-400"
                        )}>
                          {c.status}
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
                {!campaignsLoading && campaigns.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">No campaigns found</div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Campaign Ref (Optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="ctx-campaign-ref" className="text-xs font-medium text-slate-400">
              Campaign ID / External Reference
            </Label>
            <p className="text-[11px] text-slate-600 mb-1.5">
              Optional reference ID from your ad platform or CRM
            </p>
            <Input
              id="ctx-campaign-ref"
              value={ctx.campaign_ref || ""}
              onChange={(e) => onFieldChange("builder_data.context.campaign_ref", e.target.value)}
              placeholder="e.g., fb_campaign_2384729384"
              className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
              data-testid="context-campaign-ref-input"
            />
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

// =============================================================================
// MA-THEME-ROLLOUT-075 Phase 5: Foundation Theme Section (compact)
// =============================================================================
const THEME_SWATCHES = [
  { hex: '#6366F1', label: 'Indigo' }, { hex: '#3B82F6', label: 'Blue' },
  { hex: '#14B8A6', label: 'Teal' }, { hex: '#10B981', label: 'Emerald' },
  { hex: '#8B5CF6', label: 'Purple' }, { hex: '#F97316', label: 'Orange' },
  { hex: '#F59E0B', label: 'Amber' }, { hex: '#EC4899', label: 'Pink' },
];

function FoundationThemeSection({ themeConfig, brandConfig, onFieldChange }) {
  const mode = themeConfig?.styling_mode || 'inherit_brand';
  const isCustom = mode === 'custom';
  // MA-THEME-OVERRIDE-097: FIX - Read from flat schema (not nested custom_theme)
  const custom = themeConfig || {};

  // Hydrate brand theme for display when inherit_brand
  const brandTheme = useMemo(() => {
    if (!brandConfig) return resolveTheme({ preset: 'professional' });
    const preset = brandConfig.default_preset || 'professional';
    const modeOverride = brandConfig.mode_override;
    const anchor = brandConfig.default_anchor_color || brandConfig.brand_primary || brandConfig.accent;
    const presetDef = PRESETS[preset] || PRESETS.professional;
    const effectiveMode = modeOverride || presetDef.mode;
    return resolveTheme({ preset, anchor_color: anchor, mode: effectiveMode });
  }, [brandConfig]);

  const previewTheme = isCustom
    ? resolveTheme({
        preset: custom.preset || 'professional',
        anchor_color: custom.anchor_color,
        mode: custom.mode,
      })
    : brandTheme;

  const setThemeConfig = useCallback((updates) => {
    const current = themeConfig || {};
    const next = { ...current, ...updates };
    onFieldChange('builder_data.foundation.theme_config', next);
  }, [themeConfig, onFieldChange]);

  // MA-THEME-OVERRIDE-097: FIX - Write to flat schema (not nested custom_theme)
  const setCustomField = useCallback((field, value) => {
    setThemeConfig({ [field]: value });
  }, [setThemeConfig]);

  // When switching to Customize, seed custom fields from brand config
  const handleSwitchToCustom = useCallback(() => {
    const seed = {};
    if (brandConfig) {
      seed.preset = brandConfig.default_preset || 'professional';
      seed.anchor_color = brandConfig.default_anchor_color || brandConfig.brand_primary || brandConfig.accent || null;
      seed.mode = brandConfig.mode_override || null;
    } else {
      seed.preset = 'professional';
    }
    // MA-THEME-OVERRIDE-097: FIX - Set flat fields, not nested custom_theme
    setThemeConfig({ styling_mode: 'custom', ...seed });
  }, [brandConfig, setThemeConfig]);

  return (
    <div className="pt-4 border-t border-white/[0.06]">
      <div className="pb-2">
        <h3 className="text-sm font-medium text-slate-200">Theme</h3>
        <p className="text-xs text-slate-500 mt-0.5">Visual theme for generated landing pages</p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-3" data-testid="theme-mode-toggle">
        <button
          data-testid="theme-inherit-btn"
          onClick={() => setThemeConfig({ styling_mode: 'inherit_brand', custom_theme: null })}
          className="flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all border"
          style={{
            backgroundColor: !isCustom ? 'rgba(99,102,241,0.08)' : 'transparent',
            borderColor: !isCustom ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
            color: !isCustom ? '#c7d2fe' : '#71717a',
          }}
        >
          Use brand theme
        </button>
        <button
          data-testid="theme-custom-btn"
          onClick={handleSwitchToCustom}
          className="flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all border"
          style={{
            backgroundColor: isCustom ? 'rgba(99,102,241,0.08)' : 'transparent',
            borderColor: isCustom ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
            color: isCustom ? '#c7d2fe' : '#71717a',
          }}
        >
          Customize
        </button>
      </div>

      {/* Inherit brand info — hydrated from tenant brand config */}
      {!isCustom && (
        <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-white/[0.02] border border-white/[0.06]" data-testid="theme-inherit-info">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: previewTheme.tokens.primary }} />
          <span className="text-xs text-slate-400">
            Using brand default — {previewTheme.name} ({previewTheme.mode})
          </span>
        </div>
      )}

      {/* Custom theme controls */}
      {isCustom && (
        <div className="space-y-3 py-2 px-3 rounded-lg bg-white/[0.02] border border-white/[0.06]" data-testid="theme-custom-controls">
          {/* Preset selector */}
          <div>
            <label className="text-[11px] text-slate-500 font-medium mb-1.5 block">Preset</label>
            <div className="grid grid-cols-3 gap-1.5">
              {Object.entries(PRESETS).map(([key, pd]) => (
                <button
                  key={key}
                  data-testid={`theme-preset-${key}`}
                  onClick={() => setCustomField('preset', key)}
                  className="py-1.5 px-2 rounded-md text-[11px] font-medium transition-all border"
                  style={{
                    backgroundColor: custom.preset === key ? 'rgba(99,102,241,0.1)' : 'transparent',
                    borderColor: custom.preset === key ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
                    color: custom.preset === key ? '#e0e7ff' : '#71717a',
                  }}
                >
                  {pd.name}
                </button>
              ))}
            </div>
          </div>

          {/* Accent color */}
          <div>
            <label className="text-[11px] text-slate-500 font-medium mb-1.5 block">Accent</label>
            {brandConfig?.extracted_colors?.length > 0 && (
              <div className="mb-2">
                <p className="text-[9px] text-slate-600 mb-1">From your logo</p>
                <div className="flex gap-1.5 flex-wrap">
                  {brandConfig.extracted_colors.map((c, i) => {
                    const hex = c.normalized_hex || c.hex;
                    const sel = (custom.anchor_color || '#6366F1') === hex;
                    return (
                      <button key={`logo-${i}`} title={hex}
                        onClick={() => setCustomField('anchor_color', hex)}
                        className="w-6 h-6 rounded-md transition-all"
                        style={{
                          backgroundColor: hex,
                          border: sel ? '2px solid #fff' : '1.5px solid rgba(255,255,255,0.08)',
                          transform: sel ? 'scale(1.1)' : 'scale(1)',
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            <p className="text-[9px] text-slate-600 mb-1">Preset colors</p>
            <div className="flex gap-1.5 flex-wrap">
              {THEME_SWATCHES.map(({ hex, label }) => {
                const sel = (custom.anchor_color || '#6366F1') === hex;
                return (
                  <button key={hex} title={label}
                    onClick={() => setCustomField('anchor_color', hex)}
                    className="w-6 h-6 rounded-md transition-all"
                    style={{
                      backgroundColor: hex,
                      border: sel ? '2px solid #fff' : '1.5px solid rgba(255,255,255,0.08)',
                      transform: sel ? 'scale(1.1)' : 'scale(1)',
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Mode */}
          <div>
            <label className="text-[11px] text-slate-500 font-medium mb-1.5 block">Mode</label>
            <div className="flex gap-1 bg-white/[0.04] rounded-lg p-0.5 border border-white/[0.06]">
              {[{ v: null, l: 'Auto' }, { v: 'light', l: 'Light' }, { v: 'dark', l: 'Dark' }].map(({ v, l }) => {
                const on = (custom.mode ?? null) === v;
                return (
                  <button key={l} onClick={() => setCustomField('mode', v)}
                    className="flex-1 py-1 rounded-md text-[11px] font-medium transition-colors"
                    style={{ backgroundColor: on ? 'rgba(255,255,255,0.08)' : 'transparent', color: on ? '#e4e4e7' : '#71717a' }}>
                    {l}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Center Panel: Foundation Step (MA-UI-061-A)
// =============================================================================
function FoundationStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete, tenantBrandConfig }) {
  const name = draft?.name || "";
  const contextVertical = draft?.builder_data?.context?.vertical || "";
  const strategicObjective = getFieldValue(draft?.builder_data?.foundation?.strategic_objective, "");
  const coreStrategy = getFieldValue(draft?.builder_data?.foundation?.core_strategy, "");
  const strategyCtx = useMemo(() => buildStrategyContext(draft), [draft]);

  const [aiHintDismissed, setAiHintDismissed] = useState(() => {
    try { return localStorage.getItem('ppb_ai_assist_seen') === '1'; } catch { return false; }
  });
  const dismissAiHint = useCallback(() => {
    setAiHintDismissed(true);
    try { localStorage.setItem('ppb_ai_assist_seen', '1'); } catch {}
  }, []);

  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="foundation" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />

      {/* Content Area - Form Fields */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-5 space-y-6">
          {/* Section Header - MA-UI-061-A-POLISH: Updated title */}
          <div className="pb-3 border-b border-white/[0.06]">
            <h3 className="text-sm font-medium text-slate-200">Foundation</h3>
            <p className="text-xs text-slate-500 mt-1">
              Define the strategic core of this profit path
            </p>
          </div>

          {/* AI Assist first-time guidance */}
          {!aiHintDismissed && (
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-indigo-500/[0.06] border border-indigo-500/20" data-testid="ai-assist-hint">
              <p className="text-xs text-indigo-300/90">
                <Wand2 className="w-3 h-3 inline mr-1.5 -mt-0.5" />
                Let AI build this for you — generate answers instantly or refine what you've written
              </p>
              <button onClick={dismissAiHint} className="text-indigo-400/50 hover:text-indigo-300 ml-3 text-xs">dismiss</button>
            </div>
          )}

          {/* Name Field - MA-UI-061-A-POLISH: Consistent spacing hierarchy */}
          <div className="space-y-1.5">
            <Label htmlFor="pp-name" className="text-xs font-medium text-slate-400">
              Name <span className="text-red-400">*</span>
            </Label>
            <Input
              id="pp-name"
              value={name}
              onChange={(e) => onFieldChange("name", e.target.value)}
              placeholder="e.g., Medicare AEP Cost Savings Campaign"
              className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
              data-testid="foundation-name-input"
            />
          </div>

          {/* MA-THEME-ROLLOUT-075 Phase 5: Theme Selection */}
          <FoundationThemeSection
            themeConfig={draft?.builder_data?.foundation?.theme_config || {}}
            brandConfig={tenantBrandConfig}
            onFieldChange={onFieldChange}
          />

          {/* Strategic Objective Field */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="pp-objective" className="text-xs font-medium text-slate-400">
                Strategic Objective <span className="text-red-400">*</span>
              </Label>
              <AIFieldAssist
                field="foundation.strategic_objective"
                currentValue={strategicObjective}
                strategyContext={strategyCtx}
                onAccept={(val) => onFieldChange("builder_data.foundation.strategic_objective", val)}
              />
            </div>
            <p className="text-[11px] text-slate-600 mb-1.5">
              What is the primary goal of this profit path?
            </p>
            <Textarea
              id="pp-objective"
              value={strategicObjective}
              onChange={(e) => onFieldChange("builder_data.foundation.strategic_objective", e.target.value)}
              placeholder="e.g., Capture leads seeking to reduce healthcare costs… or click ✨ Generate"
              className="min-h-[80px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
              data-testid="foundation-objective-input"
            />
          </div>

          {/* Vertical Field - Read-only, sourced from Context step */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-400">
              Vertical <span className="text-xs font-normal text-slate-600 ml-1">(from Context)</span>
            </Label>
            <div
              className="h-9 flex items-center px-3 text-sm rounded-md bg-slate-800/30 border border-slate-700/30 text-slate-300"
              data-testid="foundation-vertical-display"
            >
              {contextVertical || <span className="text-slate-600 italic">Set in Context step</span>}
            </div>
          </div>

          {/* Core Strategy / Positioning Field */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="pp-strategy" className="text-xs font-medium text-slate-400">
                Core Strategy / Positioning <span className="text-red-400">*</span>
              </Label>
              <AIFieldAssist
                field="foundation.core_strategy"
                currentValue={coreStrategy}
                strategyContext={strategyCtx}
                onAccept={(val) => onFieldChange("builder_data.foundation.core_strategy", val)}
              />
            </div>
            <p className="text-[11px] text-slate-600 mb-1.5">
              How is this positioned differently from typical solutions?
            </p>
            <Textarea
              id="pp-strategy"
              value={coreStrategy}
              onChange={(e) => onFieldChange("builder_data.foundation.core_strategy", e.target.value)}
              placeholder="e.g., Position as cost-conscious advisors who help navigate… or click ✨ Generate"
              className="min-h-[100px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
              data-testid="foundation-strategy-input"
            />
          </div>

          {/* MA-KNOWLEDGE-ARCH-001: Business Knowledge subsection */}
          {draft?.id && <BusinessKnowledge profitPathId={draft.id} />}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Center Panel: Audience Step (MA-UI-062)
// =============================================================================
function AudienceStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete }) {
  // Get audience data from draft
  const audience = draft?.builder_data?.audience || {};
  const primaryAudience = audience.primary_audience || "";
  const situation = audience.situation || "";
  const coreProblem = audience.core_problem || "";
  const emotionalDriver = audience.emotional_driver || "";
  const awarenessStage = getFieldValue(audience.awareness_stage, "");
  const triggerEvent = audience.trigger_event || "";
  const strategyCtx = useMemo(() => buildStrategyContext(draft), [draft]);

  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="audience" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />

      {/* Content Area - Form Fields */}
      <ScrollArea className="flex-1">
        <div className="p-5 space-y-6">
          {/* Section Header */}
          <div className="pb-3 border-b border-white/[0.06]">
            <h3 className="text-sm font-medium text-slate-200">Audience</h3>
            <p className="text-xs text-slate-500 mt-1">
              Define who you're targeting and what drives them
            </p>
          </div>

          {/* Section 1: Target Profile */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              Target Profile
            </h4>
            
            {/* Primary Audience */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-primary-audience" className="text-xs font-medium text-slate-400">
                  Primary Audience <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist
                  field="audience.primary_audience"
                  currentValue={primaryAudience}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.audience.primary_audience", val)}
                />
              </div>
              <Input
                id="pp-primary-audience"
                value={primaryAudience}
                onChange={(e) => onFieldChange("builder_data.audience.primary_audience", e.target.value)}
                placeholder="e.g., Adults 65+ approaching Medicare eligibility"
                className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
                data-testid="audience-primary-input"
              />
            </div>

            {/* Life / Financial Situation */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-situation" className="text-xs font-medium text-slate-400">
                  Life / Financial Situation
                </Label>
                <AIFieldAssist
                  field="audience.situation"
                  currentValue={situation}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.audience.situation", val)}
                />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                What stage of life are they in? What's their financial context?
              </p>
              <Textarea
                id="pp-situation"
                value={situation}
                onChange={(e) => onFieldChange("builder_data.audience.situation", e.target.value)}
                placeholder="e.g., Recently retired or approaching retirement, living on fixed income, concerned about rising healthcare costs"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="audience-situation-input"
              />
            </div>
          </div>

          {/* Section 2: Problem & Pain */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              Problem & Pain
            </h4>
            
            {/* Core Problem */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-core-problem" className="text-xs font-medium text-slate-400">
                  Core Problem <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist
                  field="audience.core_problem"
                  currentValue={coreProblem}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.audience.core_problem", val)}
                />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                What problem are they trying to solve?
              </p>
              <Textarea
                id="pp-core-problem"
                value={coreProblem}
                onChange={(e) => onFieldChange("builder_data.audience.core_problem", e.target.value)}
                placeholder="e.g., Confused by Medicare options, worried about making the wrong choice, fear of unexpected medical bills"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="audience-problem-input"
              />
            </div>

            {/* Emotional Driver */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-emotional-driver" className="text-xs font-medium text-slate-400">
                  Emotional Driver
                </Label>
                <AIFieldAssist
                  field="audience.emotional_driver"
                  currentValue={emotionalDriver}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.audience.emotional_driver", val)}
                />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                What emotion motivates them to act?
              </p>
              <Textarea
                id="pp-emotional-driver"
                value={emotionalDriver}
                onChange={(e) => onFieldChange("builder_data.audience.emotional_driver", e.target.value)}
                placeholder="e.g., Fear of financial ruin from health issues, desire to protect family, anxiety about the unknown"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="audience-emotional-input"
              />
            </div>
          </div>

          {/* Section 3: Awareness */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              Awareness
            </h4>
            
            {/* Awareness Stage */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-awareness" className="text-xs font-medium text-slate-400">
                  Awareness Stage <span className="text-red-400">*</span>
                </Label>
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                How aware is your audience of the problem and solutions?
              </p>
              <Select
                value={awarenessStage || undefined}
                onValueChange={(val) => onFieldChange("builder_data.audience.awareness_stage", val)}
              >
                <SelectTrigger
                  id="pp-awareness"
                  className="w-full h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 focus:ring-indigo-500/40"
                  data-testid="audience-awareness-select"
                >
                  <SelectValue placeholder="Select awareness stage..." />
                </SelectTrigger>
                <SelectContent>
                  {AWARENESS_STAGES.map((stage) => (
                    <SelectItem key={stage.value} value={stage.value}>
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {awarenessStage && (
                <p className="text-[10px] text-slate-500 mt-1">
                  {AWARENESS_STAGES.find(s => s.value === awarenessStage)?.description}
                </p>
              )}
            </div>
          </div>

          {/* Section 4: Trigger & Intent */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              Trigger & Intent
            </h4>
            
            {/* Trigger Event / Timing */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-trigger" className="text-xs font-medium text-slate-400">
                  Trigger Event / Timing
                </Label>
                <AIFieldAssist
                  field="audience.trigger_event"
                  currentValue={triggerEvent}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.audience.trigger_event", val)}
                />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                What event or timing makes them ready to act now?
              </p>
              <Textarea
                id="pp-trigger"
                value={triggerEvent}
                onChange={(e) => onFieldChange("builder_data.audience.trigger_event", e.target.value)}
                placeholder="e.g., Annual Enrollment Period (AEP), turning 65, losing employer coverage, moving to new state"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="audience-trigger-input"
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

// =============================================================================
// Center Panel: Offer Step (MA-UI-063)
// =============================================================================
function OfferStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete }) {
  // Get offer data from draft
  const offer = draft?.builder_data?.offer || {};
  const offerName = offer.offer_name || "";
  const offerType = getFieldValue(offer.offer_type, "");
  const corePromise = offer.core_promise || "";
  const primaryBenefit = offer.primary_benefit || "";
  const differentiation = offer.differentiation || "";
  const trustProof = offer.trust_proof || "";
  const primaryCta = offer.primary_cta || "";
  const urgency = offer.urgency || "";
  const strategyCtx = useMemo(() => buildStrategyContext(draft), [draft]);

  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="offer" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />

      {/* Content Area - Form Fields */}
      <ScrollArea className="flex-1">
        <div className="p-5 space-y-6">
          {/* Section Header */}
          <div className="pb-3 border-b border-white/[0.06]">
            <h3 className="text-sm font-medium text-slate-200">Offer</h3>
            <p className="text-xs text-slate-500 mt-1">
              Define what you're offering and why it's compelling
            </p>
          </div>

          {/* Section 1: Offer Core */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              Offer Core
            </h4>
            
            {/* Offer Name */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-offer-name" className="text-xs font-medium text-slate-400">
                  Offer Name <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist
                  field="offer.offer_name"
                  currentValue={offerName}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.offer.offer_name", val)}
                />
              </div>
              <Input
                id="pp-offer-name"
                value={offerName}
                onChange={(e) => onFieldChange("builder_data.offer.offer_name", e.target.value)}
                placeholder="e.g., Medicare Cost Savings Review"
                className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
                data-testid="offer-name-input"
              />
            </div>

            {/* Offer Type */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-offer-type" className="text-xs font-medium text-slate-400">
                  Offer Type <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist
                  field="offer.offer_type"
                  currentValue={offerType}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.offer.offer_type", val)}
                />
              </div>
              <Input
                id="pp-offer-type"
                value={offerType}
                onChange={(e) => onFieldChange("builder_data.offer.offer_type", e.target.value)}
                placeholder="e.g., Free Consultation, Quote Review, Savings Analysis"
                className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
                data-testid="offer-type-input"
              />
            </div>
          </div>

          {/* Section 2: Value Proposition */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              Value Proposition
            </h4>
            
            {/* Core Promise */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-core-promise" className="text-xs font-medium text-slate-400">
                  Core Promise <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist
                  field="offer.core_promise"
                  currentValue={corePromise}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.offer.core_promise", val)}
                />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                What promise are you making to the prospect?
              </p>
              <Textarea
                id="pp-core-promise"
                value={corePromise}
                onChange={(e) => onFieldChange("builder_data.offer.core_promise", e.target.value)}
                placeholder="e.g., Help prospects identify Medicare options that may reduce monthly costs"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="offer-promise-input"
              />
            </div>

            {/* Primary Benefit */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-primary-benefit" className="text-xs font-medium text-slate-400">
                  Primary Benefit <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist
                  field="offer.primary_benefit"
                  currentValue={primaryBenefit}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.offer.primary_benefit", val)}
                />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                What does the prospect get from taking this offer?
              </p>
              <Textarea
                id="pp-primary-benefit"
                value={primaryBenefit}
                onChange={(e) => onFieldChange("builder_data.offer.primary_benefit", e.target.value)}
                placeholder="e.g., Greater confidence and potential savings during enrollment"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="offer-benefit-input"
              />
            </div>
          </div>

          {/* Section 3: Differentiation */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              Differentiation
            </h4>
            
            {/* Why Different */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-differentiation" className="text-xs font-medium text-slate-400">
                  Why This Offer Is Different <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist
                  field="offer.differentiation"
                  currentValue={differentiation}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.offer.differentiation", val)}
                />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                What makes this offer stand out from alternatives?
              </p>
              <Textarea
                id="pp-differentiation"
                value={differentiation}
                onChange={(e) => onFieldChange("builder_data.offer.differentiation", e.target.value)}
                placeholder="e.g., Leads with savings analysis instead of product-first selling"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="offer-differentiation-input"
              />
            </div>

            {/* Trust / Proof Element */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-trust-proof" className="text-xs font-medium text-slate-400">
                  Trust / Proof Element
                </Label>
                <AIFieldAssist
                  field="offer.trust_proof"
                  currentValue={trustProof}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.offer.trust_proof", val)}
                />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                What builds credibility or trust?
              </p>
              <Textarea
                id="pp-trust-proof"
                value={trustProof}
                onChange={(e) => onFieldChange("builder_data.offer.trust_proof", e.target.value)}
                placeholder="e.g., Licensed advisors with personalized plan comparisons"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="offer-trust-input"
              />
            </div>
          </div>

          {/* Section 4: CTA */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              Call to Action
            </h4>
            
            {/* Primary CTA */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-primary-cta" className="text-xs font-medium text-slate-400">
                  Primary CTA <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist
                  field="offer.primary_cta"
                  currentValue={primaryCta}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.offer.primary_cta", val)}
                />
              </div>
              <Input
                id="pp-primary-cta"
                value={primaryCta}
                onChange={(e) => onFieldChange("builder_data.offer.primary_cta", e.target.value)}
                placeholder="e.g., Book Your Medicare Savings Review"
                className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
                data-testid="offer-cta-input"
              />
            </div>

            {/* Urgency */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-urgency" className="text-xs font-medium text-slate-400">
                  Urgency / Reason to Act Now
                </Label>
                <AIFieldAssist
                  field="offer.urgency"
                  currentValue={urgency}
                  strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.offer.urgency", val)}
                />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">
                Why should the prospect act now instead of later?
              </p>
              <Textarea
                id="pp-urgency"
                value={urgency}
                onChange={(e) => onFieldChange("builder_data.offer.urgency", e.target.value)}
                placeholder="e.g., AEP is time-sensitive and delaying could mean missing better-fit options"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="offer-urgency-input"
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

// =============================================================================
// Center Panel: Messaging Step (MA-UI-064)
// =============================================================================
function MessagingStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete }) {
  // Read messaging data — support both flat and versioned formats
  const rawMsg = draft?.builder_data?.messaging || {};
  const msgFields = rawMsg.fields || rawMsg; // versioned: rawMsg.fields, flat: rawMsg itself
  const angle = msgFields.angle || "";
  const thesis = msgFields.thesis || "";
  const uniqueMechanism = msgFields.unique_mechanism || "";
  const messageEnemy = msgFields.message_enemy || "";
  const primaryHook = msgFields.primary_hook || "";
  const scrollStopper = msgFields.scroll_stopper || "";
  const primaryEmotion = getFieldValue(msgFields.primary_emotion, "");
  const emotionalOutcome = getFieldValue(msgFields.emotional_outcome, "");
  const emotionalNarrative = msgFields.emotional_narrative || "";
  const tone = getFieldValue(msgFields.tone, "");
  const voiceNotes = msgFields.voice_notes || "";
  // New canonical fields
  const corePromise = msgFields.core_promise || "";
  const primaryPain = msgFields.primary_pain || "";
  const desiredOutcome = msgFields.desired_outcome || "";
  const proofAngle = msgFields.proof_angle || "";
  const ctaFraming = msgFields.cta_framing || "";

  const strategyCtx = useMemo(() => buildStrategyContext(draft), [draft]);
  const msgVersion = rawMsg.version || 0;
  const msgSource = rawMsg.source || "user";

  // Auto-synthesis state
  const [synthesizing, setSynthesizing] = useState(false);
  const [regenSection, setRegenSection] = useState(null);
  const synthTriggered = useRef(false);

  // Check if upstream steps are complete enough for synthesis
  const audienceComplete = !!(
    draft?.builder_data?.audience?.primary_audience?.trim() &&
    draft?.builder_data?.audience?.core_problem?.trim() &&
    getFieldValue(draft?.builder_data?.audience?.awareness_stage, "")
  );
  const offerComplete = !!(
    draft?.builder_data?.offer?.offer_name?.trim() &&
    draft?.builder_data?.offer?.core_promise?.trim() &&
    draft?.builder_data?.offer?.primary_cta?.trim()
  );
  const messagingEmpty = !angle && !thesis && !primaryHook;

  // Synthesize handler (declared before auto-synthesis effect that calls it)
  const handleSynthesize = useCallback(async () => {
    setSynthesizing(true);
    try {
      // Phase 5: Push current messaging to history before overwriting
      const currentMessaging = draft?.builder_data?.messaging || {};
      const currentFields = {
        angle: currentMessaging.angle, thesis: currentMessaging.thesis,
        unique_mechanism: currentMessaging.unique_mechanism, message_enemy: currentMessaging.message_enemy,
        primary_hook: currentMessaging.primary_hook, scroll_stopper: currentMessaging.scroll_stopper,
        primary_emotion: currentMessaging.primary_emotion, emotional_outcome: currentMessaging.emotional_outcome,
        emotional_narrative: currentMessaging.emotional_narrative, tone: currentMessaging.tone,
        voice_notes: currentMessaging.voice_notes, core_promise: currentMessaging.core_promise,
        primary_pain: currentMessaging.primary_pain, desired_outcome: currentMessaging.desired_outcome,
        proof_angle: currentMessaging.proof_angle, cta_framing: currentMessaging.cta_framing,
      };
      const hasExistingFields = Object.values(currentFields).some(v => {
        const s = getFieldValue(v, "");
        return typeof s === "string" && s.trim() !== "";
      });
      if (hasExistingFields) {
        const history = Array.isArray(currentMessaging.messaging_history)
          ? [...currentMessaging.messaging_history]
          : [];
        history.push({
          version: currentMessaging.version || 0,
          fields: currentFields,
          generated_at: currentMessaging.generated_at || new Date().toISOString(),
          source: currentMessaging.source || "unknown",
        });
        // Max 5 versions
        while (history.length > 5) history.shift();
        onFieldChange("builder_data.messaging.messaging_history", history);
      }

      const res = await api.post("/ai/synthesize-messaging", {
        strategy_context: strategyCtx,
        profit_path_id: draft?.id,
      });
      if (res.data?.success && res.data?.messaging) {
        const m = res.data.messaging;
        const fields = m.fields || {};
        // Write version metadata
        onFieldChange("builder_data.messaging.version", m.version || 1);
        onFieldChange("builder_data.messaging.source", m.source || "ai");
        onFieldChange("builder_data.messaging.generated_at", m.generated_at || "");
        // Phase 5: Store messaging hash
        if (res.data.messaging_hash) {
          onFieldChange("builder_data.messaging.messaging_hash", res.data.messaging_hash);
        }
        // MA-KNOWLEDGE-INJECTION-004: Store knowledge hash for staleness detection
        if (res.data.knowledge_hash) {
          onFieldChange("builder_data.messaging.knowledge_hash", res.data.knowledge_hash);
        }
        // Write all 16 fields
        const fieldKeys = [
          "angle", "thesis", "unique_mechanism", "message_enemy",
          "primary_hook", "scroll_stopper", "primary_emotion",
          "emotional_outcome", "emotional_narrative", "tone",
          "voice_notes", "core_promise", "primary_pain",
          "desired_outcome", "proof_angle", "cta_framing",
        ];
        fieldKeys.forEach((k) => {
          if (fields[k]) onFieldChange(`builder_data.messaging.${k}`, fields[k]);
        });
      }
    } catch (err) {
      console.error("[Messaging] Auto-synthesis failed:", err);
    }
    setSynthesizing(false);
  }, [strategyCtx, onFieldChange, draft?.builder_data?.messaging]);

  // Auto-synthesize on step entry when messaging is empty and upstream is complete
  // Uses proper dependencies instead of empty [] to handle both navigation methods:
  //   1. Step card click (direct mount)
  //   2. "Next" button from Offer (mount during batched state update)
  // The synthTriggered ref prevents duplicate calls within the same step entry.
  useEffect(() => {
    if (audienceComplete && offerComplete && messagingEmpty && !synthTriggered.current && !synthesizing) {
      synthTriggered.current = true;
      handleSynthesize();
    }
  }, [audienceComplete, offerComplete, messagingEmpty, synthesizing, handleSynthesize]);

  const handleSectionRegen = useCallback(async (section) => {
    setRegenSection(section);
    try {
      const res = await api.post("/ai/regenerate-messaging-section", {
        section,
        strategy_context: strategyCtx,
        current_messaging: { fields: msgFields },
      });
      if (res.data?.success && res.data?.fields) {
        // Phase 5: Push current version to history before section update
        const currentMessaging = draft?.builder_data?.messaging || {};
        const hasExistingFields = Object.values(msgFields).some(v => {
          const s = getFieldValue(v, "");
          return typeof s === "string" && s.trim() !== "";
        });
        if (hasExistingFields) {
          const history = Array.isArray(currentMessaging.messaging_history)
            ? [...currentMessaging.messaging_history]
            : [];
          history.push({
            version: currentMessaging.version || 0,
            fields: { ...msgFields },
            generated_at: currentMessaging.generated_at || new Date().toISOString(),
            source: currentMessaging.source || "unknown",
          });
          while (history.length > 5) history.shift();
          onFieldChange("builder_data.messaging.messaging_history", history);
        }
        // Apply updated fields
        Object.entries(res.data.fields).forEach(([k, v]) => {
          if (v) onFieldChange(`builder_data.messaging.${k}`, v);
        });
        onFieldChange("builder_data.messaging.source", "hybrid");
        // Phase 5: Update messaging hash
        if (res.data.messaging_hash) {
          onFieldChange("builder_data.messaging.messaging_hash", res.data.messaging_hash);
        }
        // Increment version
        onFieldChange("builder_data.messaging.version", (currentMessaging.version || 1) + 1);
        onFieldChange("builder_data.messaging.generated_at", new Date().toISOString());
      }
    } catch { /* silent */ }
    setRegenSection(null);
  }, [strategyCtx, msgFields, onFieldChange, draft?.builder_data?.messaging]);

  // Section header with regeneration button
  const SectionHead = ({ title, desc, section }) => (
    <div className="flex items-center justify-between">
      <div>
        <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{title}</h4>
        {desc && <p className="text-[11px] text-slate-600 mt-0.5">{desc}</p>}
      </div>
      <button
        type="button"
        onClick={() => handleSectionRegen(section)}
        disabled={regenSection !== null}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] text-slate-500 hover:text-indigo-400 hover:bg-indigo-500/10 transition-colors disabled:opacity-40"
        data-testid={`regen-section-${section}`}
      >
        <RefreshCw className={`w-3 h-3 ${regenSection === section ? "animate-spin" : ""}`} />
        {regenSection === section ? "..." : "Regen"}
      </button>
    </div>
  );

  // Synthesis overlay
  if (synthesizing) {
    return (
      <div className="h-full flex flex-col">
        <StepHeader stepId="messaging" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />
        <div className="flex-1 flex items-center justify-center" data-testid="messaging-synthesis-overlay">
          <div className="text-center space-y-4 max-w-xs">
            <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto" />
            <div>
              <p className="text-sm font-medium text-slate-200">Synthesizing messaging strategy</p>
              <p className="text-xs text-slate-500 mt-1">
                Building from your audience profile, offer, and foundation...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="messaging" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />

      <ScrollArea className="flex-1">
        <div className="p-5 space-y-6">
          {/* Header with version info + re-synthesize control */}
          <div className="pb-3 border-b border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-slate-200">Messaging</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Canonical strategy layer — drives all downstream generation
                </p>
              </div>
              {audienceComplete && offerComplete && (
                <button
                  type="button"
                  onClick={handleSynthesize}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 transition-colors"
                  data-testid="messaging-resynthesize-btn"
                >
                  <RefreshCw className="w-3 h-3" />
                  {msgVersion > 0 ? "Re-synthesize" : "Synthesize"}
                </button>
              )}
            </div>
            {msgVersion > 0 && (
              <p className="text-[10px] text-slate-600 mt-1.5">
                v{msgVersion} — {msgSource === "ai" ? "AI generated" : msgSource === "hybrid" ? "AI + manual edits" : "Manual"}
              </p>
            )}
            {/* MA-KNOWLEDGE-INJECTION-004: Knowledge staleness indicator */}
            {draft?.id && rawMsg.knowledge_hash && (
              <div className="mt-2">
                <KnowledgeStalenessIndicator
                  profitPathId={draft.id}
                  storedHash={rawMsg.knowledge_hash}
                  stepLabel="Messaging"
                  onRegenerate={audienceComplete && offerComplete ? handleSynthesize : undefined}
                />
              </div>
            )}
          </div>

          {/* Section 1: Core Angle */}
          <div className="space-y-4">
            <SectionHead title="Core Angle" section="core_angle" />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-angle" className="text-xs font-medium text-slate-400">
                  Message Angle <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist field="messaging.angle" currentValue={angle} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.angle", val)} />
              </div>
              <Input id="pp-angle" value={angle}
                onChange={(e) => onFieldChange("builder_data.messaging.angle", e.target.value)}
                placeholder="e.g., Cost Reduction Focus"
                className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
                data-testid="messaging-angle-input" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-thesis" className="text-xs font-medium text-slate-400">
                  Message Thesis <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist field="messaging.thesis" currentValue={thesis} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.thesis", val)} />
              </div>
              <Textarea id="pp-thesis" value={thesis}
                onChange={(e) => onFieldChange("builder_data.messaging.thesis", e.target.value)}
                placeholder="e.g., Most seniors are overpaying because they don't understand their options"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-thesis-input" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-mechanism" className="text-xs font-medium text-slate-400">
                  Unique Mechanism <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist field="messaging.unique_mechanism" currentValue={uniqueMechanism} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.unique_mechanism", val)} />
              </div>
              <Textarea id="pp-mechanism" value={uniqueMechanism}
                onChange={(e) => onFieldChange("builder_data.messaging.unique_mechanism", e.target.value)}
                placeholder="e.g., Side-by-side Medicare cost comparison with personalized plan mapping"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-mechanism-input" />
            </div>
          </div>

          {/* Section 2: Positioning Contrast */}
          <div className="space-y-4">
            <SectionHead title="Positioning" desc="What you're pushing against" section="positioning" />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-enemy" className="text-xs font-medium text-slate-400">
                  Message Enemy <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist field="messaging.message_enemy" currentValue={messageEnemy} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.message_enemy", val)} />
              </div>
              <Textarea id="pp-enemy" value={messageEnemy}
                onChange={(e) => onFieldChange("builder_data.messaging.message_enemy", e.target.value)}
                placeholder="e.g., Most advisors push plans instead of helping people understand their options"
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-enemy-input" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-proof-angle" className="text-xs font-medium text-slate-400">
                  Proof Angle
                </Label>
                <AIFieldAssist field="messaging.proof_angle" currentValue={proofAngle} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.proof_angle", val)} />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">Credibility statement with differentiation</p>
              <Textarea id="pp-proof-angle" value={proofAngle}
                onChange={(e) => onFieldChange("builder_data.messaging.proof_angle", e.target.value)}
                placeholder="e.g., Licensed advisors comparing every available plan, not just the highest-commission ones"
                className="min-h-[60px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-proof-input" />
            </div>
          </div>

          {/* Section 3: Hook Strategy */}
          <div className="space-y-4">
            <SectionHead title="Hook Strategy" section="hook_strategy" />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-hook" className="text-xs font-medium text-slate-400">
                  Primary Hook <span className="text-red-400">*</span>
                </Label>
                <AIFieldAssist field="messaging.primary_hook" currentValue={primaryHook} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.primary_hook", val)} />
              </div>
              <Textarea id="pp-hook" value={primaryHook}
                onChange={(e) => onFieldChange("builder_data.messaging.primary_hook", e.target.value)}
                placeholder="e.g., Most seniors don't realize they could be saving hundreds on Medicare — and the enrollment window closes soon."
                className="min-h-[70px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-hook-input" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-scroll-stopper" className="text-xs font-medium text-slate-400">
                  Scroll Stopper Idea
                </Label>
                <AIFieldAssist field="messaging.scroll_stopper" currentValue={scrollStopper} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.scroll_stopper", val)} />
              </div>
              <Textarea id="pp-scroll-stopper" value={scrollStopper}
                onChange={(e) => onFieldChange("builder_data.messaging.scroll_stopper", e.target.value)}
                placeholder="e.g., Side-by-side cost comparison showing potential savings"
                className="min-h-[60px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-stopper-input" />
            </div>
          </div>

          {/* Section 4: Emotional Framing */}
          <div className="space-y-4">
            <SectionHead title="Emotional Layer" section="emotional_layer" />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-emotion" className="text-xs font-medium text-slate-400">
                  Primary Emotion <span className="text-red-400">*</span>
                </Label>
              </div>
              <Select value={primaryEmotion || undefined}
                onValueChange={(val) => onFieldChange("builder_data.messaging.primary_emotion", val)}>
                <SelectTrigger id="pp-emotion"
                  className="w-full h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 focus:ring-indigo-500/40"
                  data-testid="messaging-emotion-select">
                  <SelectValue placeholder="Select primary emotion..." />
                </SelectTrigger>
                <SelectContent>
                  {PRIMARY_EMOTIONS.map((emotion) => (
                    <SelectItem key={emotion.value} value={emotion.value}>{emotion.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-outcome" className="text-xs font-medium text-slate-400">
                  Emotional Outcome <span className="text-red-400">*</span>
                </Label>
              </div>
              <Select value={emotionalOutcome || undefined}
                onValueChange={(val) => onFieldChange("builder_data.messaging.emotional_outcome", val)}>
                <SelectTrigger id="pp-outcome"
                  className="w-full h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 focus:ring-indigo-500/40"
                  data-testid="messaging-outcome-select">
                  <SelectValue placeholder="Select emotional outcome..." />
                </SelectTrigger>
                <SelectContent>
                  {EMOTIONAL_OUTCOMES.map((outcome) => (
                    <SelectItem key={outcome.value} value={outcome.value}>{outcome.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-narrative" className="text-xs font-medium text-slate-400">
                  Emotional Narrative
                </Label>
                <AIFieldAssist field="messaging.emotional_narrative" currentValue={emotionalNarrative} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.emotional_narrative", val)} />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">Entry → turning point → resolution</p>
              <Textarea id="pp-narrative" value={emotionalNarrative}
                onChange={(e) => onFieldChange("builder_data.messaging.emotional_narrative", e.target.value)}
                placeholder="e.g., The prospect enters feeling anxious about making a costly Medicare mistake. Through the savings analysis, they gain clarity. They leave feeling confident."
                className="min-h-[80px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-narrative-input" />
            </div>
          </div>

          {/* Section 5: Communication Style */}
          <div className="space-y-4">
            <SectionHead title="Communication Style" section="communication_style" />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-tone" className="text-xs font-medium text-slate-400">
                  Tone <span className="text-red-400">*</span>
                </Label>
              </div>
              <Select value={tone || undefined}
                onValueChange={(val) => onFieldChange("builder_data.messaging.tone", val)}>
                <SelectTrigger id="pp-tone"
                  className="w-full h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 focus:ring-indigo-500/40"
                  data-testid="messaging-tone-select">
                  <SelectValue placeholder="Select tone..." />
                </SelectTrigger>
                <SelectContent>
                  {COMMUNICATION_TONES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-voice" className="text-xs font-medium text-slate-400">
                  Voice Notes
                </Label>
                <AIFieldAssist field="messaging.voice_notes" currentValue={voiceNotes} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.voice_notes", val)} />
              </div>
              <Textarea id="pp-voice" value={voiceNotes}
                onChange={(e) => onFieldChange("builder_data.messaging.voice_notes", e.target.value)}
                placeholder="e.g., Speak simply, avoid jargon, position as advisor not salesperson"
                className="min-h-[60px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-voice-input" />
            </div>
          </div>

          {/* Section 6: Synthesized Strategy Fields */}
          <div className="space-y-4">
            <SectionHead title="Synthesized Strategy" desc="Derived from Audience + Offer" section="synthesized" />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-core-promise" className="text-xs font-medium text-slate-400">
                  Core Promise
                </Label>
                <AIFieldAssist field="messaging.core_promise" currentValue={corePromise} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.core_promise", val)} />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">Prospect-facing promise language</p>
              <Textarea id="pp-core-promise" value={corePromise}
                onChange={(e) => onFieldChange("builder_data.messaging.core_promise", e.target.value)}
                placeholder="e.g., We'll show you exactly which Medicare plan fits your needs and budget"
                className="min-h-[60px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-promise-input" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-primary-pain" className="text-xs font-medium text-slate-400">
                  Primary Pain
                </Label>
                <AIFieldAssist field="messaging.primary_pain" currentValue={primaryPain} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.primary_pain", val)} />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">Concise emotional + factual pain statement</p>
              <Input id="pp-primary-pain" value={primaryPain}
                onChange={(e) => onFieldChange("builder_data.messaging.primary_pain", e.target.value)}
                placeholder="e.g., Feeling overwhelmed by Medicare plan options and terrified of choosing wrong coverage"
                className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
                data-testid="messaging-pain-input" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-desired-outcome" className="text-xs font-medium text-slate-400">
                  Desired Outcome
                </Label>
                <AIFieldAssist field="messaging.desired_outcome" currentValue={desiredOutcome} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.desired_outcome", val)} />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">Positive "after" state</p>
              <Input id="pp-desired-outcome" value={desiredOutcome}
                onChange={(e) => onFieldChange("builder_data.messaging.desired_outcome", e.target.value)}
                placeholder="e.g., Clear understanding of your best Medicare option with confidence"
                className="h-9 text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600"
                data-testid="messaging-outcome-text-input" />
            </div>

            {/* Section 7: Conversion Layer */}
            <SectionHead title="Conversion" section="conversion_layer" />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="pp-cta-framing" className="text-xs font-medium text-slate-400">
                  CTA Framing
                </Label>
                <AIFieldAssist field="messaging.cta_framing" currentValue={ctaFraming} strategyContext={strategyCtx}
                  onAccept={(val) => onFieldChange("builder_data.messaging.cta_framing", val)} />
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">How the CTA should be positioned in copy</p>
              <Textarea id="pp-cta-framing" value={ctaFraming}
                onChange={(e) => onFieldChange("builder_data.messaging.cta_framing", e.target.value)}
                placeholder="e.g., Position as zero-risk first step: 'Get Your Free Medicare Savings Review'"
                className="min-h-[60px] text-sm bg-slate-800/50 border-slate-700/50 text-slate-100 placeholder:text-slate-600 resize-none"
                data-testid="messaging-cta-input" />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

// =============================================================================
// Center Panel: Creative Step (MA-UI-066 - Message → Creative Translation Engine)
// =============================================================================
function CreativeStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [editingConceptId, setEditingConceptId] = useState(null);
  const [generatingImagesFor, setGeneratingImagesFor] = useState(null); // concept index or null

  // Get creative data from draft
  const creative = draft?.builder_data?.creative || { concepts: [], generation_status: "idle" };
  const concepts = creative.concepts || [];
  const selectedConcept = concepts.find(c => c.is_selected);
  
  // Get messaging data for generation
  const messaging = draft?.builder_data?.messaging || {};

  // Phase 4: Build strategy context for GeneratorInput contract
  const strategyCtx = useMemo(() => buildStrategyContext(draft), [draft]);

  // Check if messaging is complete enough for generation
  const canGenerate = !!(
    messaging.angle?.trim() &&
    messaging.thesis?.trim() &&
    messaging.unique_mechanism?.trim() &&
    messaging.message_enemy?.trim() &&
    messaging.primary_hook?.trim() &&
    getFieldValue(messaging.primary_emotion, "").trim() &&
    getFieldValue(messaging.emotional_outcome, "").trim() &&
    getFieldValue(messaging.tone, "").trim()
  );

  // Generate creative concepts from messaging
  // MA-UI-066-E: Locked schema for downstream consumption (Funnel, Script, Review)
  // ==========================================================================
  // MA-AI-071: AI-Powered Creative Generation with Deterministic Fallback
  // ==========================================================================
  const generateConcepts = useCallback(async () => {
    if (!canGenerate || isGenerating) return;
    
    setIsGenerating(true);
    onFieldChange("builder_data.creative.generation_status", "generating");

    // Get messaging inputs for concept generation
    const messagingAngle = messaging.angle?.trim() || "";
    const mechanism = messaging.unique_mechanism?.trim() || "";
    const enemy = messaging.message_enemy?.trim() || "";
    const baseHook = messaging.primary_hook?.trim() || "";

    // ==========================================================================
    // MA-UI-066-E: DETERMINISTIC FALLBACK AXES
    // HARD RULES: No duplicate hook_type, primary_driver, or angle
    // ==========================================================================
    const CONCEPT_AXES = [
      {
        angle: "Cost Savings Focus",
        hook_type: "question",
        emotional_start: "Concern",
        emotional_end: "Relief",
        visual_type: "comparison",
        pain_point: "overpaying without awareness"
      },
      {
        angle: "Mistake Prevention",
        hook_type: "warning",
        emotional_start: "Fear",
        emotional_end: "Safety",
        visual_type: "documentary",
        pain_point: "making the wrong choice"
      },
      {
        angle: "Industry Callout",
        hook_type: "contrarian",
        emotional_start: "Frustration",
        emotional_end: "Clarity",
        visual_type: "disruption",
        pain_point: "being misled"
      },
      {
        angle: "Transformation Story",
        hook_type: "scenario",
        emotional_start: "Overwhelm",
        emotional_end: "Confidence",
        visual_type: "lifestyle",
        pain_point: "feeling stuck"
      }
    ];

    // ==========================================================================
    // MA-AI-071: AI-Powered Creative Generation with retry (no template fallback)
    // MA-UI-075: Normalize data before API calls (remove {value, source} wrappers)
    // ==========================================================================
    const maxAttempts = 3;
    let lastError = "";

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await api.post("/ai/generate-creative", {
          strategy_context: strategyCtx,
          messaging: normalizeForAPI(messaging),
          step_params: {},
          profit_path_id: draft?.id,
        });

        if (response.data?.success && response.data?.concepts?.length === 4) {
          const hasValidConcepts = response.data.validation_passed || response.data.is_fallback;
          if (!hasValidConcepts) {
            lastError = "AI validation failed";
            console.warn(`[MA-AI-071] Attempt ${attempt}/${maxAttempts}: validation failed, retrying...`);
            continue;
          }

          const hookTypes = new Set(response.data.concepts.map(c => c.hook_type));
          if (hookTypes.size !== 4) {
            lastError = "Duplicate hook_types";
            console.warn(`[MA-AI-071] Attempt ${attempt}/${maxAttempts}: duplicate hook_types, retrying...`);
            continue;
          }

          // Transform AI response to match UI schema
          const aiConcepts = response.data.concepts.map((c, index) => ({
            id: `concept_${Date.now()}_${index}`,
            angle: c.angle,
            hook: c.hook,
            hook_type: c.hook_type,
            primary_text: c.primary_text || `${c.angle} — Simplified.`,
            visual_direction: c.visual_direction || "",
            emotional_start: c.emotional_start,
            emotional_end: c.emotional_end,
            primary_driver: c.primary_driver,
            mechanism: c.mechanism,
            execution_intent: c.execution_intent,
            why_this_works: c.why_this_works,
            formats: { "1:1": null, "4:5": null, "9:16": null },
            is_selected: index === 0
          }));

          onFieldChange("builder_data.creative.concepts", aiConcepts);
          onFieldChange("builder_data.creative.selected_concept_id", aiConcepts[0].id);
          onFieldChange("builder_data.creative.generation_status", "complete");
          onFieldChange("builder_data.creative.generation_source", response.data.is_fallback ? "fallback" : "ai");
          if (response.data.messaging_hash) {
            onFieldChange("builder_data.creative.messaging_hash", response.data.messaging_hash);
          }
          if (response.data.generation_meta) {
            onFieldChange("builder_data.creative.generation_meta", response.data.generation_meta);
          }
          setIsGenerating(false);
          return;
        }

        lastError = "Invalid response structure";
        console.warn(`[MA-AI-071] Attempt ${attempt}/${maxAttempts}: invalid response, retrying...`);
      } catch (error) {
        lastError = error.message;
        console.warn(`[MA-AI-071] Attempt ${attempt}/${maxAttempts} failed: ${error.message}`);
      }
    }

    // All attempts exhausted — notify user instead of showing bad templates
    console.error(`[MA-AI-071] Creative generation failed after ${maxAttempts} attempts: ${lastError}`);
    onFieldChange("builder_data.creative.generation_status", "failed");
    setIsGenerating(false);
  }, [canGenerate, isGenerating, messaging, draft, onFieldChange]);

  // Toggle concept selection - MA-UI-066-E: Only ONE concept can be selected
  const toggleConceptSelection = useCallback((conceptId) => {
    const concept = concepts.find(c => c.id === conceptId);
    if (!concept) return;
    
    // If already selected, don't allow deselection (must always have one selected)
    if (concept.is_selected) return;
    
    // Select the new concept and deselect all others
    const updatedConcepts = concepts.map(c => ({
      ...c,
      is_selected: c.id === conceptId
    }));
    
    onFieldChange("builder_data.creative.concepts", updatedConcepts);
    onFieldChange("builder_data.creative.selected_concept_id", conceptId);
  }, [concepts, onFieldChange]);

  // Update concept field
  const updateConceptField = useCallback((conceptId, field, value) => {
    const updatedConcepts = concepts.map(c => 
      c.id === conceptId ? { ...c, [field]: value } : c
    );
    onFieldChange("builder_data.creative.concepts", updatedConcepts);
  }, [concepts, onFieldChange]);

  // Delete concept
  const deleteConcept = useCallback((conceptId) => {
    const updatedConcepts = concepts.filter(c => c.id !== conceptId);
    onFieldChange("builder_data.creative.concepts", updatedConcepts);
  }, [concepts, onFieldChange]);

  // Regenerate single concept - MA-UI-066-E: Use alternate axes with new schema
  const regenerateConcept = useCallback((conceptId) => {
    const conceptIndex = concepts.findIndex(c => c.id === conceptId);
    if (conceptIndex === -1) return;

    // Get messaging inputs
    const baseHook = messaging.primary_hook?.trim() || "";
    const enemy = messaging.message_enemy?.trim() || "";
    const mechanism = messaging.unique_mechanism?.trim() || "";

    // Get currently used hook_types and angles to ensure no duplicates
    const usedHookTypes = concepts.filter(c => c.id !== conceptId).map(c => c.hook_type);
    const usedAngles = concepts.filter(c => c.id !== conceptId).map(c => c.angle);
    const usedDrivers = concepts.filter(c => c.id !== conceptId).map(c => c.primary_driver);

    // Alternate regeneration axes (different from the 4 main ones)
    const REGEN_AXES = [
      {
        angle: "Urgency Focus",
        hook_type: "warning",
        emotional_start: "Complacency",
        emotional_end: "Action",
        visual_type: "deadline",
        pain_point: "missing critical window"
      },
      {
        angle: "Social Proof",
        hook_type: "scenario",
        emotional_start: "Isolation",
        emotional_end: "Belonging",
        visual_type: "community",
        pain_point: "feeling alone"
      },
      {
        angle: "Simplicity Promise",
        hook_type: "contrarian",
        emotional_start: "Overwhelm",
        emotional_end: "Peace",
        visual_type: "minimal",
        pain_point: "drowning in information"
      },
      {
        angle: "Value Revelation",
        hook_type: "question",
        emotional_start: "Ignorance",
        emotional_end: "Empowerment",
        visual_type: "discovery",
        pain_point: "missing hidden value"
      }
    ];

    // Find an axis that doesn't duplicate existing concepts
    let axis = REGEN_AXES.find(a => 
      !usedHookTypes.includes(a.hook_type) && 
      !usedAngles.includes(a.angle) &&
      !usedDrivers.includes(a.emotional_end)
    ) || REGEN_AXES[Math.floor(Math.random() * REGEN_AXES.length)];

    // Generate new hook based on type — complete standalone sentences
    const painPoint = axis.pain_point || "uncertainty";
    let newHook = "";
    switch (axis.hook_type) {
      case "question":
        newHook = `How much is ${painPoint} actually costing you every month?`;
        break;
      case "warning":
        newHook = `Most people don't realize they're ${painPoint} until it's too late to fix.`;
        break;
      case "scenario":
        newHook = `Others already made the switch and stopped ${painPoint} — the only question is when you will.`;
        break;
      case "contrarian":
        newHook = `The common advice about ${painPoint.split(" ").slice(0, 3).join(" ")} is wrong — and it's costing you.`;
        break;
      default:
        newHook = `There's a better way to handle ${painPoint} than what you've been told.`;
    }

    // Visual direction as structured object
    const visualMap = {
      deadline: { line1: "Calendar with circled date", line2: "Ticking clock, urgency", line3: "Professional, focused energy" },
      community: { line1: "Group of confident seniors", line2: "Smiling together, shared success", line3: "Warm gathering, social proof" },
      minimal: { line1: "Single clear document on desk", line2: "Minimalist composition", line3: "Breathing room, simplicity" },
      discovery: { line1: "Moment of realization", line2: "Eyes widening with understanding", line3: "Light bulb moment captured" }
    };

    const shortMechanism = mechanism.split(" ").slice(0, 4).join(" ");

    const updatedConcepts = concepts.map((c) => {
      if (c.id === conceptId) {
        const newId = `concept_${Date.now()}_regen`;
        return {
          id: newId,
          angle: axis.angle,
          hook: newHook,
          hook_type: axis.hook_type,
          primary_text: `${axis.angle} — Your Next Step.`,
          visual_direction: visualMap[axis.visual_type] || { line1: "Professional scene", line2: "Warm lighting", line3: "Expressions of understanding" },
          emotional_start: axis.emotional_start,
          emotional_end: axis.emotional_end,
          primary_driver: axis.emotional_end,
          mechanism: mechanism,
          execution_intent: `Addresses ${axis.pain_point} by reframing the decision from ${axis.emotional_start.toLowerCase()} toward ${axis.emotional_end.toLowerCase()}.`,
          why_this_works: `Addresses ${axis.pain_point} through ${shortMechanism || "a structured approach"}, moving the audience from ${axis.emotional_start.toLowerCase()} to ${axis.emotional_end.toLowerCase()}.`,
          formats: c.formats || { "1:1": null, "4:5": null, "9:16": null },
          is_selected: c.is_selected
        };
      }
      return c;
    });
    onFieldChange("builder_data.creative.concepts", updatedConcepts);
  }, [concepts, messaging, onFieldChange]);

  // MA-ARCH-204: Generate ad images for a concept (async with polling)
  const generateConceptImages = useCallback(async (conceptIndex) => {
    const concept = concepts[conceptIndex];
    if (!concept) return;

    setGeneratingImagesFor(conceptIndex);
    try {
      // Start generation — use axios (api) instead of fetch to avoid body stream issues
      const startResp = await api.post("/ai/generate-concept-images", {
        profit_path_id: draft?.id,
        concept_index: conceptIndex,
        concept,
        strategy_context: strategyCtx,
      });
      const startData = startResp.data;
      if (!startData.success || !startData.task_id) {
        throw new Error(startData.error || "Failed to start image generation");
      }

      // Poll for completion
      const taskId = startData.task_id;
      let attempts = 0;
      const maxAttempts = 60; // 5 minutes max (5s intervals)
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 5000));
        const statusResp = await api.get(`/ai/image-status/${taskId}`);
        const status = statusResp.data;

        if (status.status === "complete") {
          const updatedConcepts = concepts.map((c, i) =>
            i === conceptIndex ? { ...c, images: { square_url: status.square_url, portrait_url: status.portrait_url, landscape_url: status.landscape_url } } : c
          );
          onFieldChange("builder_data.creative.concepts", updatedConcepts);
          break;
        } else if (status.status === "error") {
          throw new Error(status.error || "Image generation failed");
        }
        attempts++;
      }
      if (attempts >= maxAttempts) {
        throw new Error("Image generation timed out");
      }
    } catch (err) {
      console.error("[IMAGE_GEN] Error:", err);
      alert(`Image generation error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGeneratingImagesFor(null);
    }
  }, [concepts, draft?.id, strategyCtx, onFieldChange]);

  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="creative" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />

      {/* Content Area */}
      <ScrollArea className="flex-1">
        <div className="p-5 space-y-6">
          {/* Phase 5: Staleness Indicator */}
          <StalenessIndicator
            draft={draft}
            stepKey="creative"
            onRegenerate={() => handleGenerate()}
            onKeep={() => {
              onFieldChange("builder_data.creative.messaging_hash", draft?.builder_data?.messaging?.messaging_hash);
              onFieldChange("builder_data.creative.dismissed_staleness_at", new Date().toISOString());
              onFieldChange("builder_data.creative.dismissed_version", draft?.builder_data?.messaging?.version || 0);
            }}
            isGenerating={isGenerating}
          />

          {/* Section Header */}
          <div className="pb-3 border-b border-white/[0.06]">
            <h3 className="text-sm font-medium text-slate-200">Creative</h3>
            <p className="text-xs text-slate-500 mt-1">
              Translate your messaging into high-performing ad concepts
            </p>
          </div>

          {/* Generation Control */}
          <div className="space-y-4">
            {!canGenerate && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-xs text-amber-300">
                  Complete the Messaging step first to generate creative concepts.
                </p>
              </div>
            )}

            {creative.generation_status === "failed" && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-xs text-red-300">
                  Generation failed after multiple attempts. Click below to try again.
                </p>
              </div>
            )}

            <Button
              onClick={generateConcepts}
              disabled={!canGenerate || isGenerating}
              className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
              data-testid="generate-concepts-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating optimized concepts...
                </>
              ) : concepts.length > 0 ? (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Regenerate All Concepts
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Creative Concepts
                </>
              )}
            </Button>
          </div>

          {/* Concept Cards */}
          {concepts.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
                Creative Concepts ({concepts.length})
              </h4>

              {concepts.map((concept, index) => (
                <div
                  key={concept.id}
                  onClick={(e) => {
                    toggleConceptSelection(concept.id);
                    // Prevent scroll jump from DOM height change when Ad Images section moves
                    const el = e.currentTarget;
                    requestAnimationFrame(() => {
                      el.scrollIntoView({ block: "nearest", behavior: "instant" });
                    });
                  }}
                  className={cn(
                    "p-4 rounded-xl border transition-all cursor-pointer",
                    concept.is_selected
                      ? "border-indigo-500/70 bg-indigo-500/10 ring-1 ring-indigo-500/20 shadow-lg shadow-indigo-500/5"
                      : "border-slate-700/40 bg-slate-800/20 opacity-75 hover:opacity-100 hover:border-slate-600/50"
                  )}
                  data-testid={`concept-card-${index}`}
                >
                  {/* Concept Header - MA-UI-066-E: angle + primary_driver badges */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-slate-300">
                        Concept #{index + 1}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-slate-700/50 text-slate-400">
                        {concept.angle || concept.angle_variant}
                      </span>
                      {concept.primary_driver && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300">
                          {concept.primary_driver}
                        </span>
                      )}
                      {concept.is_selected && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/30 text-emerald-300 font-medium">
                          Selected
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => setEditingConceptId(editingConceptId === concept.id ? null : concept.id)}
                      >
                        <Wrench className="h-3.5 w-3.5 text-slate-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => regenerateConcept(concept.id)}
                      >
                        <Sparkles className="h-3.5 w-3.5 text-slate-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 hover:text-red-400"
                        onClick={() => deleteConcept(concept.id)}
                      >
                        <span className="text-xs">×</span>
                      </Button>
                    </div>
                  </div>

                  {/* Concept Content - Editable or Display Mode */}
                  {editingConceptId === concept.id ? (
                    <div className="space-y-3">
                      {/* Editable Hook (max 12 words) */}
                      <div className="space-y-1">
                        <label className="text-[10px] text-slate-500 uppercase">Hook (max 12 words)</label>
                        <Input
                          value={concept.hook}
                          onChange={(e) => {
                            const words = e.target.value.split(/\s+/).filter(w => w);
                            if (words.length <= 12) updateConceptField(concept.id, "hook", e.target.value);
                          }}
                          className="h-8 text-xs bg-slate-800/50 border-slate-700/50"
                        />
                      </div>
                      {/* Editable Primary Text (max 8 words) */}
                      <div className="space-y-1">
                        <label className="text-[10px] text-slate-500 uppercase">Primary Text (max 8 words)</label>
                        <Input
                          value={concept.primary_text || concept.copy_overlay || ""}
                          onChange={(e) => {
                            const words = e.target.value.split(/\s+/).filter(w => w);
                            if (words.length <= 8) updateConceptField(concept.id, "primary_text", e.target.value);
                          }}
                          className="h-8 text-xs bg-slate-800/50 border-slate-700/50"
                        />
                      </div>
                      {/* Editable Visual Direction (3 lines) */}
                      <div className="space-y-1">
                        <label className="text-[10px] text-slate-500 uppercase">Visual Direction (3 lines)</label>
                        {typeof concept.visual_direction === 'object' ? (
                          <div className="space-y-1">
                            <Input
                              value={concept.visual_direction?.line1 || ""}
                              onChange={(e) => updateConceptField(concept.id, "visual_direction", { ...concept.visual_direction, line1: e.target.value })}
                              placeholder="Line 1"
                              className="h-7 text-xs bg-slate-800/50 border-slate-700/50"
                            />
                            <Input
                              value={concept.visual_direction?.line2 || ""}
                              onChange={(e) => updateConceptField(concept.id, "visual_direction", { ...concept.visual_direction, line2: e.target.value })}
                              placeholder="Line 2"
                              className="h-7 text-xs bg-slate-800/50 border-slate-700/50"
                            />
                            <Input
                              value={concept.visual_direction?.line3 || ""}
                              onChange={(e) => updateConceptField(concept.id, "visual_direction", { ...concept.visual_direction, line3: e.target.value })}
                              placeholder="Line 3"
                              className="h-7 text-xs bg-slate-800/50 border-slate-700/50"
                            />
                          </div>
                        ) : (
                          <Textarea
                            value={concept.visual_direction || ""}
                            onChange={(e) => updateConceptField(concept.id, "visual_direction", e.target.value)}
                            className="min-h-[60px] text-xs bg-slate-800/50 border-slate-700/50 resize-none"
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Hook with type badge */}
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] text-slate-500 uppercase">Hook</span>
                          {(concept.hook_type || concept.hook_structure) && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 uppercase">
                              {concept.hook_type || concept.hook_structure}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-200">"{concept.hook}"</p>
                      </div>
                      {/* Primary Text - moved up for prominence */}
                      <div>
                        <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Primary Text</span>
                        <p className="text-sm text-indigo-300 font-medium">{concept.primary_text || concept.copy_overlay}</p>
                      </div>
                      {/* Visual Direction - MA-UI-066-E: Structured 3 lines */}
                      <div>
                        <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Visual Direction</span>
                        <div className="text-xs text-slate-400 space-y-0.5">
                          {typeof concept.visual_direction === 'object' ? (
                            <>
                              <p className="leading-tight">{concept.visual_direction.line1}</p>
                              <p className="leading-tight">{concept.visual_direction.line2}</p>
                              <p className="leading-tight">{concept.visual_direction.line3}</p>
                            </>
                          ) : (
                            concept.visual_direction?.split('\n').map((line, i) => (
                              <p key={i} className="leading-tight">{line}</p>
                            ))
                          )}
                        </div>
                      </div>
                      {/* Execution Intent - MA-UI-066-E NEW */}
                      {concept.execution_intent && (
                        <div>
                          <span className="text-[10px] text-amber-500/80 uppercase block mb-0.5">Execution Intent</span>
                          <p className="text-xs text-amber-300/70">{concept.execution_intent}</p>
                        </div>
                      )}
                      {/* Emotional Path + Mechanism row - compact */}
                      <div className="flex items-start gap-4 pt-1">
                        <div className="shrink-0">
                          <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Emotion</span>
                          <p className="text-xs text-purple-300">
                            {concept.emotional_start && concept.emotional_end 
                              ? `${concept.emotional_start} → ${concept.emotional_end}`
                              : concept.emotional_path}
                          </p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Mechanism</span>
                          <p className="text-xs text-slate-400 line-clamp-1">{concept.mechanism || concept.mechanism_highlight}</p>
                        </div>
                      </div>
                      {/* Why This Works (STRICT FORMAT) */}
                      {concept.why_this_works && (
                        <div className="pt-2 mt-2 border-t border-dashed border-slate-700/40">
                          <span className="text-[10px] text-emerald-500/80 uppercase block mb-0.5">Why This Works</span>
                          <p className="text-xs text-emerald-300/70 italic">{concept.why_this_works}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* MA-ARCH-204: Ad Image Generation & Preview */}
                  {concept.is_selected && (
                    <div className="mt-3 pt-3 border-t border-slate-700/30" data-testid={`concept-images-${index}`} onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-col items-center gap-3 mb-3">
                        <span className="text-[10px] text-slate-500 uppercase">Ad Images</span>
                        <Button
                          className={cn(
                            "h-10 px-6 text-sm font-medium rounded-lg transition-all",
                            concept.images?.square_url
                              ? "bg-slate-700/60 hover:bg-slate-600/60 text-slate-300 border border-slate-600/40"
                              : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-500/20"
                          )}
                          onClick={() => generateConceptImages(index)}
                          disabled={generatingImagesFor !== null}
                          data-testid={`generate-images-btn-${index}`}
                        >
                          {generatingImagesFor === index ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating Images...</>
                          ) : concept.images?.square_url ? (
                            <><RefreshCw className="h-4 w-4 mr-2" /> Regenerate Images</>
                          ) : (
                            <><Image className="h-4 w-4 mr-2" /> Generate Ad Images</>
                          )}
                        </Button>
                      </div>

                      {generatingImagesFor === index && (
                        <div className="flex items-center justify-center py-8 text-slate-500">
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          <span className="text-xs">Generating 3 ad images (square, portrait, landscape)...</span>
                        </div>
                      )}

                      {concept.images?.square_url && generatingImagesFor !== index && (
                        <div className="grid grid-cols-3 gap-2" data-testid={`concept-image-grid-${index}`}>
                          {[
                            { key: "square_url", label: "1:1 Feed", aspect: "aspect-square" },
                            { key: "portrait_url", label: "4:5 Mobile", aspect: "aspect-[4/5]" },
                            { key: "landscape_url", label: "1.91:1 Desktop", aspect: "aspect-[1.91/1]" },
                          ].map(({ key, label, aspect }) => (
                            <div key={key} className="space-y-1" data-testid={`concept-image-${key}-${index}`}>
                              <div className={cn("rounded-lg overflow-hidden border border-slate-700/40 bg-slate-800/30", aspect)}>
                                <img
                                  src={concept.images[key]}
                                  alt={`${label} ad creative`}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              </div>
                              <p className="text-[9px] text-slate-500 text-center">{label}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {concepts.length === 0 && canGenerate && (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-pink-500/10 flex items-center justify-center">
                <Image className="h-6 w-6 text-pink-400" />
              </div>
              <p className="text-sm text-slate-400 mb-1">No concepts generated yet</p>
              <p className="text-xs text-slate-600">
                Click "Generate Creative Concepts" to create ad concepts from your messaging
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// =============================================================================
// MA-UI-067-B: Canonical Funnel Step Types (Future Architecture Scaffolding)
// These internal IDs enable future AI Assist Layer, analytics, and cross-funnel optimization
// DO NOT use for UI rendering yet - use FUNNEL_STEP_LABELS for display
// =============================================================================
const CANONICAL_FUNNEL_STEP_TYPES = [
  "hook_alignment",  // Step 1: Entry point matching ad intent
  "problem",         // Step 2: Surface awareness / pain discovery
  "mechanism",       // Step 3: Deliver mechanism value
  "insight",         // Step 4: Create belief shift
  "cta"              // Step 5: Drive aligned action
];

// MA-UI-067-B: Dynamic label mapping per funnel type (scaffolded for future use)
// This enables consistent internal schema with funnel-type-specific display labels
const FUNNEL_STEP_LABELS_BY_TYPE = {
  diagnostic: {
    hook_alignment: "Hook Alignment",
    problem: "Problem Discovery",
    mechanism: "Mechanism Delivery",
    insight: "Insight Reveal",
    cta: "Call to Action"
  },
  risk_reduction: {
    hook_alignment: "Hook Alignment",
    problem: "Risk Amplification",
    mechanism: "Mistake Identification",
    insight: "Mechanism Protection",
    cta: "Call to Action"
  },
  belief_break: {
    hook_alignment: "Pattern Interrupt",
    problem: "Current Belief Exposure",
    mechanism: "Truth Reveal",
    insight: "Mechanism Explanation",
    cta: "Call to Action"
  },
  transformation: {
    hook_alignment: "Scenario Entry",
    problem: "Current State Reflection",
    mechanism: "Desired State Visualization",
    insight: "Mechanism Bridge",
    cta: "Call to Action"
  }
};

// MA-UI-067-B: Step purpose descriptions (canonical, used for skeleton preview)
const CANONICAL_STEP_PURPOSES = {
  hook_alignment: "Match entry intent from ad",
  problem: "Surface user awareness",
  mechanism: "Deliver mechanism value",
  insight: "Create insight + shift belief",
  cta: "Drive aligned action"
};

// MA-UI-067-B: Helper to get display label (for future migration)
// Currently unused - existing STEP_PURPOSES used in FunnelStepPanel
const getFunnelStepLabel = (funnelType, canonicalType) => {
  return FUNNEL_STEP_LABELS_BY_TYPE[funnelType]?.[canonicalType] || 
         FUNNEL_STEP_LABELS_BY_TYPE.diagnostic[canonicalType] || 
         canonicalType;
};

// =============================================================================
// MA-UI-067-B: Funnel Skeleton Preview (Pre-Generation Visual Expectation Layer)
// Shows non-interactive preview of funnel structure before generation
// Helps set user expectation and reduces cognitive load
// =============================================================================
function FunnelSkeletonPreview() {
  const skeletonSteps = [
    { id: 1, label: "Hook Alignment", icon: "🎯", purpose: CANONICAL_STEP_PURPOSES.hook_alignment },
    { id: 2, label: "Problem Discovery", icon: "❓", purpose: CANONICAL_STEP_PURPOSES.problem },
    { id: 3, label: "Mechanism Delivery", icon: "💡", purpose: CANONICAL_STEP_PURPOSES.mechanism },
    { id: 4, label: "Insight Reveal", icon: "🌉", purpose: CANONICAL_STEP_PURPOSES.insight },
    { id: 5, label: "Call to Action", icon: "🚀", purpose: CANONICAL_STEP_PURPOSES.cta }
  ];

  return (
    <div className="space-y-3 opacity-50 pointer-events-none select-none" data-testid="funnel-skeleton-preview">
      {/* Skeleton Header */}
      <div className="p-2 rounded-lg bg-slate-800/30 border border-slate-700/20">
        <div className="flex items-center gap-2">
          <div className="w-16 h-3 bg-slate-700/40 rounded animate-pulse" />
          <Badge className="h-4 text-[9px] bg-slate-700/20 text-slate-600 border-0">
            Preview
          </Badge>
        </div>
      </div>

      {/* Skeleton Steps */}
      <div className="space-y-1.5">
        {skeletonSteps.map((step) => (
          <div
            key={step.id}
            className="p-2 rounded-lg bg-slate-800/20 border border-slate-700/20"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm opacity-60">{step.icon}</span>
              <div className="flex-1">
                <p className="text-xs text-slate-500 font-medium">
                  {step.id}. {step.label}
                </p>
                <p className="text-[9px] text-slate-600">
                  {step.purpose}
                </p>
              </div>
              <div className="w-3 h-3 rounded-full bg-slate-700/30" />
            </div>
          </div>
        ))}
      </div>

      {/* Skeleton Footer Hint */}
      <p className="text-[10px] text-slate-600 text-center">
        Click "Generate Funnel" to create your conversion flow
      </p>
    </div>
  );
}

// =============================================================================
// Center Panel: Funnel Step (MA-UI-067 / MA-UI-067-A - Hardened Execution System)
// =============================================================================
function FunnelStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete, resolvedLpTokens, tenantBrandConfig }) {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [showConfigEdit, setShowConfigEdit] = useState(false);
  const [bookingAtOpen, setBookingAtOpen] = useState(null);
  const [showBookingPrompt, setShowBookingPrompt] = useState(false);
  const navigate = useNavigate();

  // Core data
  const funnelData = draft?.builder_data?.funnel || { steps: [], funnel_type: null };
  const funnelSteps = funnelData.steps || [];
  const funnelInstanceId = funnelData.funnel_instance_id || null;
  const messaging = draft?.builder_data?.messaging || {};
  const concepts = draft?.builder_data?.creative?.concepts || [];
  const selectedConcept = concepts.find(c => c.is_selected);
  const canGenerate = !!selectedConcept;
  const strategyCtx = useMemo(() => buildStrategyContext(draft), [draft]);

  // Pre-generation config (stored at builder_data.funnel.config)
  const funnelConfig = funnelData.config || {};
  const updateConfig = useCallback((key, value) => {
    onFieldChange(`builder_data.funnel.config.${key}`, value);
  }, [onFieldChange]);

  // Config state
  const bookingEnabled = funnelConfig.booking_enabled ?? false;
  const bookingCalendar = funnelConfig.booking_calendar || "";
  const trustedFormMode = funnelConfig.trusted_form || "off";
  const routingEnabled = funnelConfig.routing_enabled ?? false;
  const dataEnrichmentEnabled = funnelConfig.data_enrichment_enabled ?? false;
  const attributionEnabled = funnelConfig.attribution_enabled ?? true;

  // TrustedForm helper text
  const TRUSTED_FORM_HELPERS = {
    off: "TrustedForm is disabled. No consent certificates will be captured.",
    capture_only: "Certificates are captured silently in the background. No user-facing changes. Good for compliance readiness.",
    force_compliance: "Users must actively consent before submission. A consent checkbox will appear on the CTA step. Required for TCPA-regulated verticals."
  };

  // Config wiring — fetch calendars & destinations when config panel opens
  const [calendars, setCalendars] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [defaultDestination, setDefaultDestination] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const routingMode = funnelConfig.routing_mode || "default";
  const routingDestinationId = funnelConfig.routing_destination_id || null;
  const enrichmentMode = funnelConfig.enrichment_mode || "standard";

  useEffect(() => {
    // MA-PPB-FUNNEL-CONFIG: Fetch config data on mount so both pre-gen and post-gen screens are wired
    // MA-PPB-CALENDAR-NAMES: Fetch calendar names from GHL API to display in dropdown
    setConfigLoading(true);
    Promise.all([
      api.get("/admin/calendar-routes").catch(() => ({ data: [] })),
      api.get("/booking/calendars").catch(() => ({ data: { calendars: [] } })),
      api.get("/outbound-destinations").catch(() => ({ data: { destinations: [], destination: null } })),
      api.get("/outbound-destinations/default").catch(() => ({ data: { destination: null } })),
    ]).then(([calRoutesRes, calNamesRes, destRes, defaultRes]) => {
      const routes = Array.isArray(calRoutesRes.data) ? calRoutesRes.data : calRoutesRes.data?.routes || calRoutesRes.data?.data || [];
      const calendarNames = calNamesRes.data?.calendars || [];
      
      // MA-PPB-CALENDAR-NAMES: Enrich calendar routes with names from GHL
      const enrichedCalendars = routes.map(route => {
        const ghlCalendar = calendarNames.find(c => c.id === route.calendar_id);
        return {
          ...route,
          name: ghlCalendar?.name || route.calendar_id, // Use name if available, fallback to ID
        };
      });
      
      setCalendars(enrichedCalendars);
      setDestinations(destRes.data?.destinations || []);
      setDefaultDestination(defaultRes.data?.destination || null);
    }).finally(() => setConfigLoading(false));
  }, []);

  // Extract concept data for generation
  const mechanism = selectedConcept?.mechanism || "";

  // ═══ SINGLE ACTION: Generate Funnel via FunnelAssist ═══
  const handleGenerateFunnel = useCallback(async () => {
    if (!selectedConcept || isGenerating) return;
    setIsGenerating(true);
    setShowRegenConfirm(false);
    onFieldChange("builder_data.funnel.generation_status", "generating");

    try {
      // Build FunnelBrief from Profit Path context
      const context = draft?.builder_data?.context || {};
      const audience = draft?.builder_data?.audience || {};
      const offer = draft?.builder_data?.offer || {};
      const ppName = draft?.name || "Funnel";
      const slug = ppName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + "-" + Date.now().toString(36);

      const brief = {
        purpose: offer.core_promise || offer.offer_name || `Lead qualification for ${context.vertical || "insurance"}`,
        target_audience: audience.primary_audience || "Prospective clients",
        qualification_criteria: [
          audience.core_problem ? `Problem: ${audience.core_problem}` : null,
          audience.awareness_stage ? `Awareness: ${audience.awareness_stage}` : null,
          context.vertical ? `Vertical: ${context.vertical}` : null,
          selectedConcept.hook ? `Hook: ${selectedConcept.hook}` : null,
          selectedConcept.mechanism ? `Mechanism: ${selectedConcept.mechanism}` : null,
        ].filter(Boolean).join(". "),
        include_booking: bookingEnabled,
        appointment_duration: funnelConfig.appointment_duration || null,
        tone: getFieldValue(messaging.tone, "friendly"),
        traffic_source: context.channel || null,
        profit_path_id: draft?.id,
        // MA-PPB-CONSENT-114: Include consent language in generation request
        consent_language: funnelConfig.consent_language || null,
        // MA-PPB-PUBLISH-064 P0.1: Attribution fields for publish readiness
        vertical: context.vertical || null,
        channel: context.channel || null,
        profit_path_name: draft?.name || null,
      };

      // Call existing FunnelAssist endpoint
      const response = await api.post("/ai-builder/generate-funnel", {
        brief,
        funnel_name: `${ppName} Funnel`,
        funnel_slug: slug,
      });

      const funnelId = response.data.funnel_id;

      // P4-R2C: the funnel was just created asynchronously, so establish its
      // authoritative draft version BEFORE any of the draft writes below.
      // `expected_draft_version` is required, and seeding it from the
      // creation response (when present) saves a round trip; when absent the
      // writer reads it from the funnel itself. Either way the value is
      // server-supplied -- never assumed to be 1.
      forgetFunnelDraftVersion(funnelId);
      adoptFunnelDraftVersion(funnelId, response.data?.draft_version);

      // MA-302C: Sync Profit Path theme to funnel appearance
      // FIX: Use getEffectiveTheme() which reads foundation.theme_config correctly
      let funnelAppearanceSync;
      let effectiveThemeForFunnel;
      
      try {
        // MA-302C FIX: Read from correct path (foundation.theme_config via getEffectiveTheme)
        effectiveThemeForFunnel = getEffectiveTheme();
        
        console.log('[MA-302C][DEBUG] Effective theme for funnel:', {
          theme_id: effectiveThemeForFunnel?.theme_id || effectiveThemeForFunnel?.name,
          has_tokens: !!effectiveThemeForFunnel?.tokens,
          surface_base: effectiveThemeForFunnel?.tokens?.surface_base,
          text_primary: effectiveThemeForFunnel?.tokens?.text_primary
        });
        
        if (effectiveThemeForFunnel?.tokens) {
          // Use effective theme (from foundation.theme_config or brand)
          funnelAppearanceSync = {
            ...themeToFunnelAppearance(effectiveThemeForFunnel),
            iconBackgroundColor: effectiveThemeForFunnel.tokens.primary || effectiveThemeForFunnel.tokens.accent_primary,
          };
          
          console.log('[MA-302C][DEBUG] Funnel appearance colors:', {
            threadColor: funnelAppearanceSync.threadColor,
            containerColor: funnelAppearanceSync.containerColor,
            userColor: funnelAppearanceSync.userColor,
            botTextColor: funnelAppearanceSync.botTextColor
          });
        } else {
          // Only fallback if getEffectiveTheme() returns null
          console.warn('[MA-302C][FALLBACK] No effective theme found, using minimal fallback');
          const accent = resolvedLpTokens?.accent || "#0891b2";
          funnelAppearanceSync = { userColor: accent };
          effectiveThemeForFunnel = null;
        }
      } catch (err) {
        console.error("[MA-302C] Theme resolution failed:", err.message);
        const accent = resolvedLpTokens?.accent || "#0891b2";
        funnelAppearanceSync = { userColor: accent };
        effectiveThemeForFunnel = null;
      }
      
      try {
        // MA-302C: Save both appearance AND builder_data.theme to funnel
        const updatePayload = {
          appearance: funnelAppearanceSync,
        };
        
        // MA-302C FIX: Save effective theme to builder_data.theme
        if (effectiveThemeForFunnel?.tokens) {
          updatePayload.builder_data = {
            theme: effectiveThemeForFunnel
          };
          console.log('[MA-302C][SAVE] Saving effective theme to funnel.builder_data.theme:', {
            theme_id: effectiveThemeForFunnel.theme_id || effectiveThemeForFunnel.name,
            mode: effectiveThemeForFunnel.mode
          });
        } else {
          console.warn('[MA-302C][SAVE] No theme to save to builder_data.theme');
        }
        
        console.log('[MA-302C][SAVE] Full update payload:', {
          appearance_keys: Object.keys(funnelAppearanceSync),
          has_builder_data: !!updatePayload.builder_data,
          has_theme: !!updatePayload.builder_data?.theme
        });
        
        // P4-R2C: versioned, serialized, and NO LONGER SILENT. This `catch`
        // previously logged and moved on, so a failed theme sync looked like
        // a success to the operator.
        const themeResult = await saveFunnelDraftVersioned(
          funnelId, updatePayload, { ...draftSyncOptions("theme") });
        if (themeResult.ok) {
          console.log('[MA-302C][SUCCESS] Theme synced to funnel');
        } else {
          console.error("[MA-302C] Theme sync failed:",
                        themeResult.conflict ? "version conflict" : themeResult.error?.message);
        }
      } catch (err) {
        console.error("[MA-302C] Theme sync failed:", err.message);
        toast({
          title: "Funnel theme not saved",
          description: err.message || "The theme could not be synced to the funnel.",
          variant: "destructive",
        });
      }

      // MA-PPB-FUNNEL-060: Patch consent step with user-configured language + links
      const consentLang = funnelConfig.consent_language;
      if (consentLang) {
        try {
          // Fetch funnel to find the consent node
          const funnelRes = await api.get(`/funnels/${funnelId}`);
          const nodes = funnelRes.data?.draft?.nodes || [];
          const consentNode = nodes.find(n => n.node_type === "consent");
          if (consentNode) {
            const patchedNodes = nodes.map(n => {
              if (n.id !== consentNode.id) return n;
              return {
                ...n,
                content: {
                  ...n.content,
                  consent_text: consentLang,
                  privacy_policy_url: funnelConfig.privacy_policy_url || "",
                  terms_of_use_url: funnelConfig.terms_of_use_url || "",
                },
              };
            });
            // P4-R2C: consent language is a compliance surface -- a silent
            // failure here meant the configured text simply never reached the
            // funnel, with nothing shown to the operator.
            const consentResult = await saveFunnelDraftVersioned(
              funnelId, { nodes: patchedNodes },
              { ...draftSyncOptions("consent") });
            if (!consentResult.ok) {
              console.warn("[FUNNEL] Consent patch failed:",
                           consentResult.conflict ? "version conflict"
                                                  : consentResult.error?.message);
            }
          }
        } catch (err) {
          console.warn("[FUNNEL] Consent patch failed:", err.message);
          toast({
            title: "Consent language not saved",
            description: err.message
              || "The consent language could not be written to the funnel.",
            variant: "destructive",
          });
        }
      }

      // MA-PPB-FUNNEL-062 + P0.3: Patch booking node with calendar + duration
      if (bookingEnabled) {
        const patchCalendarId = funnelConfig.booking_calendar || null;
        const patchDuration = funnelConfig.appointment_duration || null;
        
        if (patchCalendarId || patchDuration) {
          try {
            const funnelRes = await api.get(`/funnels/${funnelId}`);
            const nodes = funnelRes.data?.draft?.nodes || [];
            const bookingNode = nodes.find(n => n.node_type === "booking");
            if (bookingNode) {
              const patchedConfig = { ...(bookingNode.content?.config || {}) };
              if (patchCalendarId) patchedConfig.calendar_id = patchCalendarId;
              if (patchDuration) {
                patchedConfig.duration_minutes = patchDuration;
                patchedConfig.show_duration = true;
              }
              // P4-R2C: versioned and surfaced.
              const bookingResult = await saveFunnelDraftVersioned(
                funnelId,
                {
                  nodes: nodes.map(n => {
                    if (n.id !== bookingNode.id) return n;
                    return {
                      ...n,
                      content: { ...n.content, config: patchedConfig },
                    };
                  }),
                },
                { ...draftSyncOptions("booking") });
              if (!bookingResult.ok) {
                console.warn("[FUNNEL] Booking patch failed:",
                             bookingResult.conflict ? "version conflict"
                                                    : bookingResult.error?.message);
              }
              // MA-PPB-PUBLISH-064: Mark calendar as patched for readiness
              // evaluation -- ONLY when the write actually committed.
              //
              // P4-R2C-002 (B2). This flag was set unconditionally, one line
              // below the failure check: a rejected booking save still
              // persisted a "patched" claim into the Profit Path, and
              // publish-readiness reads that flag. The consequence was masked
              // because `config.booking_calendar` happens to be set on this
              // path, but the stored record asserted something untrue, and
              // "masked today" is not a guarantee.
              if (shouldMarkBookingPatched(patchCalendarId, bookingResult)) {
                onFieldChange("builder_data.funnel.booking_calendar_patched", true);
              }
            }
          } catch (err) {
            // P4-R2C-002 (B3). This covered a failed `GET /funnels/{id}` --
            // the version read -- and only logged, so the booking
            // configuration silently never reached the funnel. The consent
            // site's equivalent catch was given a toast and this one was not;
            // that inconsistency is corrected here. Local edits are untouched,
            // so the operator can retry.
            console.warn("[FUNNEL] Booking patch failed:", err.message);
            toast({
              title: "Booking configuration not saved",
              description: err.message
                || "The booking calendar and duration could not be written to the funnel. Your settings are unchanged - try again.",
              variant: "destructive",
            });
          }
        }
      }

      // Store funnel instance reference
      onFieldChange("builder_data.funnel.funnel_instance_id", funnelId);
      onFieldChange("builder_data.funnel.generation_status", "complete");
      onFieldChange("builder_data.funnel.generation_source", "funnel_assist");
      onFieldChange("builder_data.funnel.source_concept_id", selectedConcept.id);
      onFieldChange("builder_data.funnel.mechanism", selectedConcept.mechanism || "");
      onFieldChange("builder_data.funnel.config", normalizeForAPI(funnelConfig));

      // Persist to DB
      try {
        const currentBd = normalizeForAPI(draft?.builder_data || {});
        await api.patch(`/profit-paths/${draft?.id}`, {
          builder_data: {
            ...currentBd,
            funnel: {
              ...(currentBd.funnel || {}),
              funnel_instance_id: funnelId,
              generation_status: "complete",
              generation_source: "funnel_assist",
              source_concept_id: selectedConcept.id,
              mechanism: selectedConcept.mechanism || "",
              config: normalizeForAPI(funnelConfig),
            }
          }
        });
      } catch (err) {
        console.warn("[FUNNEL] Persist failed:", err.message);
      }

      toast({ title: "Funnel generated", description: "Review your funnel steps in the preview." });

    } catch (err) {
      console.error("[FUNNEL] Generation failed:", err);
      onFieldChange("builder_data.funnel.generation_status", "error");
      
      // MA-PPB-CONSENT-114: Handle structured error response
      const errorDetail = err.response?.data?.detail;
      let errorMessage = "Please try again.";
      
      if (typeof errorDetail === "object" && errorDetail !== null) {
        // Structured error from backend (e.g., consent validation)
        errorMessage = errorDetail.guidance || errorDetail.error || "Validation failed";
      } else if (typeof errorDetail === "string") {
        errorMessage = errorDetail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast({
        title: "Funnel generation failed",
        description: errorMessage,
        variant: "destructive",
      });
    }

    setIsGenerating(false);
    setActiveStepIndex(0);
  }, [selectedConcept, isGenerating, messaging, onFieldChange, draft, funnelConfig, bookingEnabled]);

  // Fetch actual funnel nodes from funnels_v2 when instance exists
  const [funnelNodes, setFunnelNodes] = useState([]);
  const [funnelAppearanceRaw, setFunnelAppearanceRaw] = useState({});
  const [funnelStylingMode, setFunnelStylingMode] = useState('legacy');
  const [funnelHeader, setFunnelHeader] = useState({});
  const [loadingFunnel, setLoadingFunnel] = useState(false);

  const fetchFunnelData = useCallback(() => {
    if (!funnelInstanceId) { setFunnelNodes([]); return; }
    setLoadingFunnel(true);
    api.get(`/funnels/${funnelInstanceId}`).then(res => {
      const draftContent = res.data?.draft || {};
      setFunnelNodes(draftContent.nodes || []);
      // MA-175: Prefer top-level header (correct AI value) over draft.header (stale)
      setFunnelHeader(res.data.header || draftContent.header || {});
      // MA-THEME-PHASE4: Resolve appearance through adapter for inherit_brand
      const stylingMode = res.data?.styling_mode || 'legacy';
      setFunnelStylingMode(stylingMode);
      if (stylingMode === 'inherit_brand' && tenantBrandConfig) {
        const preset = tenantBrandConfig.default_preset || 'professional';
        const pd = PRESETS[preset] || PRESETS.professional;
        const theme = resolveTheme({
          preset,
          anchor_color: tenantBrandConfig.default_anchor_color || tenantBrandConfig.brand_primary || tenantBrandConfig.accent,
          mode: tenantBrandConfig.mode_override || pd.mode,
        });
        setFunnelAppearanceRaw(themeToFunnelAppearance(theme) || {});
      } else {
        setFunnelAppearanceRaw(res.data?.appearance || draftContent.appearance || {});
      }
    }).catch(err => {
      console.warn("[FUNNEL] Fetch funnel failed:", err.message);
    }).finally(() => setLoadingFunnel(false));
  }, [funnelInstanceId, tenantBrandConfig]);

  useEffect(() => { fetchFunnelData(); }, [fetchFunnelData]);

  // MA-THEME-PHASE4: Derived appearance — recomputes when brand config loads
  // MA-THEME-OVERRIDE-095: Fixed custom mode to use profit path theme
  // MA-THEME-OVERRIDE-097: FIXED - Strict priority: PPB custom > funnel inherit > funnel custom > fallback
  const funnelAppearance = useMemo(() => {
    const profitPathStylingMode = draft?.builder_data?.foundation?.theme_config?.styling_mode;
    const profitPathTheme = draft?.builder_data?.theme;
    
    console.log("═══════════════════════════════════════════════════");
    console.log("🎨 APPEARANCE RESOLUTION (MA-THEME-OVERRIDE-097)");
    console.log("Priority 1: Profit Path Custom?", profitPathStylingMode === 'custom', "Theme exists?", !!profitPathTheme?.tokens);
    console.log("Priority 2: Funnel Inherit Brand?", funnelStylingMode === 'inherit_brand', "Brand exists?", !!tenantBrandConfig);
    console.log("Priority 3: Funnel Custom?", funnelStylingMode === 'custom');
    
    // PRIORITY 1: Profit Path Custom Theme (highest in PPB context)
    if (profitPathStylingMode === 'custom' && profitPathTheme?.tokens) {
      console.log("✅ Using Profit Path Custom Theme:", profitPathTheme.name);
      const appearance = themeToFunnelAppearance(profitPathTheme);
      console.log("Converted appearance:", JSON.stringify(appearance, null, 2));
      console.log("═══════════════════════════════════════════════════");
      return appearance || {};
    }
    
    // PRIORITY 2: Funnel Inherit Brand Theme
    if (funnelStylingMode === 'inherit_brand' && tenantBrandConfig) {
      console.log("✅ Using Funnel Inherit Brand Theme");
      const preset = tenantBrandConfig.default_preset || 'professional';
      const pd = PRESETS[preset] || PRESETS.professional;
      const theme = resolveTheme({
        preset,
        anchor_color: tenantBrandConfig.default_anchor_color || tenantBrandConfig.brand_primary || tenantBrandConfig.accent,
        mode: tenantBrandConfig.mode_override || pd.mode,
      });
      const appearance = themeToFunnelAppearance(theme);
      console.log("Converted appearance:", JSON.stringify(appearance, null, 2));
      console.log("═══════════════════════════════════════════════════");
      return appearance || {};
    }
    
    // PRIORITY 3: Funnel Custom Theme (if funnel has its own custom styling)
    if (funnelStylingMode === 'custom') {
      console.log("✅ Using Funnel Custom Appearance");
      if (funnelAppearanceRaw && Object.keys(funnelAppearanceRaw).length > 0) {
        console.log("Funnel appearance:", JSON.stringify(funnelAppearanceRaw, null, 2));
        console.log("═══════════════════════════════════════════════════");
        return funnelAppearanceRaw;
      }
      // Fallback: try profit path theme even if funnel is custom but has no appearance
      if (profitPathTheme?.tokens) {
        console.log("⚠️  Funnel custom but no appearance, using profit path theme as fallback");
        const appearance = themeToFunnelAppearance(profitPathTheme);
        console.log("═══════════════════════════════════════════════════");
        return appearance || {};
      }
    }
    
    // PRIORITY 4: Fallback (raw funnel appearance or empty)
    console.log("⚠️  Using Fallback (raw appearance or empty)");
    console.log("Fallback appearance:", JSON.stringify(funnelAppearanceRaw, null, 2));
    console.log("═══════════════════════════════════════════════════");
    return funnelAppearanceRaw || {};
  }, [
    funnelStylingMode, 
    tenantBrandConfig, 
    funnelAppearanceRaw, 
    draft?.builder_data?.theme, 
    draft?.builder_data?.foundation?.theme_config?.styling_mode
  ]);

  // Re-fetch funnel data when user returns from Funnel Builder tab
  useEffect(() => {
    if (!funnelInstanceId) return;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchFunnelData();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [funnelInstanceId, fetchFunnelData]);

  const activeNode = funnelNodes[activeStepIndex] || null;

  // ═══ PRE-GENERATION STATE ═══
  if (!funnelInstanceId) {
    return (
      <div className="h-full flex flex-col">
        <StepHeader stepId="funnel" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-5">
            {/* Staleness indicator */}
            <StalenessIndicator draft={draft} stepKey="funnel" onRegenerate={() => handleGenerateFunnel()} onKeep={() => {
              onFieldChange("builder_data.funnel.messaging_hash", draft?.builder_data?.messaging?.messaging_hash);
            }} isGenerating={isGenerating} />

            {/* Dependency check */}
            {!canGenerate && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-300">Select a creative concept first to generate your funnel.</p>
              </div>
            )}

            {canGenerate && (
              <>
                {/* ═══ Phase B: Pre-Generation Configuration ═══ */}
                <div className="space-y-4">
                  <p className="text-xs text-slate-400">Configure your funnel behavior before generating.</p>

                  {configLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
                      <span className="ml-2 text-xs text-slate-400">Loading config...</span>
                    </div>
                  ) : (
                  <>
                  {/* Booking Step */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-indigo-400" />
                        <div>
                          <p className="text-xs font-medium text-slate-300">Booking Step</p>
                          <p className="text-[10px] text-slate-500">Add a calendar booking step after the CTA</p>
                        </div>
                      </div>
                      <Switch checked={bookingEnabled} onCheckedChange={(v) => updateConfig("booking_enabled", v)} data-testid="booking-toggle" />
                    </div>
                    {bookingEnabled && (
                      <div className="pt-2 border-t border-white/[0.06] space-y-2">
                        <Label className="text-[10px] text-slate-500 uppercase">Calendar</Label>
                        {calendars.length > 0 ? (
                          <Select value={bookingCalendar} onValueChange={(v) => updateConfig("booking_calendar", v)}>
                            <SelectTrigger className="mt-1 h-8 text-xs bg-slate-900/50 border-slate-700/50" data-testid="booking-calendar-select">
                              <SelectValue placeholder="Select calendar..." />
                            </SelectTrigger>
                            <SelectContent>
                              {calendars.map((cal) => (
                                <SelectItem key={cal.id} value={cal.calendar_id || cal.id}>
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-3 w-3 text-slate-400" />
                                    <span>{cal.name || cal.calendar_id}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="p-2 rounded bg-slate-800/30 border border-dashed border-white/10 text-center">
                            <p className="text-[10px] text-slate-500">No calendars connected</p>
                          </div>
                        )}
                        <a href="/app/admin?tab=calendars" target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300">
                          <ExternalLink className="h-2.5 w-2.5" /> Manage in Admin
                        </a>

                        {/* MA-PPB-FUNNEL-062: Appointment Duration */}
                        <div className="pt-2 border-t border-white/[0.04]">
                          <Label className="text-[10px] text-slate-500 uppercase">Appointment Duration (minutes)</Label>
                          <Input
                            type="number"
                            min="5"
                            max="120"
                            value={funnelConfig.appointment_duration || ""}
                            onChange={(e) => updateConfig("appointment_duration", e.target.value ? parseInt(e.target.value) : null)}
                            placeholder="e.g. 15"
                            className="mt-1 h-8 text-xs bg-slate-900/50 border-slate-700/50 w-24"
                            data-testid="appointment-duration-input"
                          />
                          <p className="text-[10px] text-slate-500 mt-1">Shown on booking slots and used in the generated header</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* TrustedForm */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                        <p className="text-xs font-medium text-slate-300">TrustedForm</p>
                      </div>
                      <Switch checked={trustedFormMode !== "off"} onCheckedChange={(v) => updateConfig("trusted_form", v ? "capture_only" : "off")} data-testid="trustedform-toggle" />
                    </div>
                    {trustedFormMode !== "off" && (
                      <div className="pt-2 border-t border-white/[0.06] space-y-2">
                        <div className="space-y-1.5">
                          <button onClick={() => updateConfig("trusted_form", "capture_only")}
                            className={cn("w-full p-2 rounded-lg text-left text-xs border transition-all",
                              trustedFormMode === "capture_only" ? "bg-blue-500/10 border-blue-500/30 text-slate-200" : "bg-white/[0.02] border-white/[0.06] text-slate-400 hover:border-white/10"
                            )} data-testid="pregen-trustedform-capture">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-2 rounded-full bg-blue-400" />
                              <span className="font-medium">Capture Only</span>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-0.5 ml-4">Certificates captured silently. Leads not restricted.</p>
                          </button>
                          <button onClick={() => updateConfig("trusted_form", "force_compliance")}
                            className={cn("w-full p-2 rounded-lg text-left text-xs border transition-all",
                              trustedFormMode === "force_compliance" ? "bg-emerald-500/10 border-emerald-500/30 text-slate-200" : "bg-white/[0.02] border-white/[0.06] text-slate-400 hover:border-white/10"
                            )} data-testid="pregen-trustedform-enforce">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-2 rounded-full bg-emerald-400" />
                              <span className="font-medium">Enforce Compliance</span>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-0.5 ml-4">Leads without verified certificate will be quarantined.</p>
                          </button>
                        </div>
                        <a href="/app/admin?tab=trusted-form" target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300">
                          <ExternalLink className="h-2.5 w-2.5" /> Manage in Admin
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Routing */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Send className="h-3.5 w-3.5 text-indigo-400" />
                        <div>
                          <p className="text-xs font-medium text-slate-300">Routing</p>
                          <p className="text-[10px] text-slate-500">Route leads based on funnel responses</p>
                        </div>
                      </div>
                      <Switch checked={routingEnabled} onCheckedChange={(v) => updateConfig("routing_enabled", v)} data-testid="routing-toggle" />
                    </div>
                    {routingEnabled && (
                      <div className="pt-2 border-t border-white/[0.06] space-y-2">
                        {destinations.length > 0 ? (
                          <>
                            <div className="space-y-1.5">
                              <button onClick={() => updateConfig("routing_mode", "default")}
                                className={cn("w-full p-2 rounded-lg text-left text-xs border transition-all",
                                  routingMode === "default" ? "bg-indigo-500/10 border-indigo-500/30 text-slate-200" : "bg-white/[0.02] border-white/[0.06] text-slate-400 hover:border-white/10"
                                )} data-testid="pregen-routing-default">
                                <span className="font-medium">Use Default</span>
                                <p className="text-[10px] text-slate-500 mt-0.5">Route to the tenant's default destination</p>
                              </button>
                              <button onClick={() => updateConfig("routing_mode", "specific")}
                                className={cn("w-full p-2 rounded-lg text-left text-xs border transition-all",
                                  routingMode === "specific" ? "bg-indigo-500/10 border-indigo-500/30 text-slate-200" : "bg-white/[0.02] border-white/[0.06] text-slate-400 hover:border-white/10"
                                )} data-testid="pregen-routing-specific">
                                <span className="font-medium">Use Specific Destination</span>
                                <p className="text-[10px] text-slate-500 mt-0.5">Route to a specific destination for this funnel</p>
                              </button>
                            </div>
                            {routingMode === "specific" && (
                              <Select value={routingDestinationId || ""} onValueChange={(v) => updateConfig("routing_destination_id", v)}>
                                <SelectTrigger className="h-8 text-xs bg-slate-900/50 border-slate-700/50" data-testid="pregen-routing-dest-select">
                                  <SelectValue placeholder="Select destination..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {destinations.map((dest) => (
                                    <SelectItem key={dest.id} value={dest.id}>
                                      <span>{dest.name}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </>
                        ) : (
                          <div className="p-2 rounded bg-slate-800/30 border border-dashed border-white/10 text-center">
                            <p className="text-[10px] text-slate-500">No outbound destinations configured</p>
                          </div>
                        )}
                        <a href="/app/admin?tab=outbound-setup" target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300">
                          <ExternalLink className="h-2.5 w-2.5" /> Manage in Admin
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Data Enrichment */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="h-3.5 w-3.5 text-amber-400" />
                        <div>
                          <p className="text-xs font-medium text-slate-300">Data Enrichment</p>
                          <p className="text-[10px] text-slate-500">Enrich lead data with third-party sources on capture</p>
                        </div>
                      </div>
                      <Switch checked={dataEnrichmentEnabled} onCheckedChange={(v) => updateConfig("data_enrichment_enabled", v)} data-testid="enrichment-toggle" />
                    </div>
                    {dataEnrichmentEnabled && (
                      <div className="pt-2 border-t border-white/[0.06] space-y-2">
                        <div className="grid grid-cols-3 gap-1.5">
                          {[
                            { key: "standard", label: "Standard", desc: "All enrichments" },
                            { key: "strict", label: "Strict", desc: "Collected only" },
                            { key: "minimal", label: "Minimal", desc: "State & timezone" },
                          ].map((mode) => (
                            <button key={mode.key} onClick={() => updateConfig("enrichment_mode", mode.key)}
                              className={cn("p-2 rounded-lg text-center border transition-all",
                                enrichmentMode === mode.key
                                  ? "border-indigo-500/40 bg-indigo-500/10"
                                  : "border-white/10 bg-white/[0.02] hover:border-white/20"
                              )} data-testid={`pregen-enrichment-${mode.key}`}>
                              <span className={cn("text-[10px] font-semibold block", enrichmentMode === mode.key ? "text-indigo-300" : "text-slate-300")}>{mode.label}</span>
                              <span className="text-[9px] text-slate-500 block mt-0.5">{mode.desc}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Attribution */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-slate-300">Attribution</p>
                        <p className="text-[10px] text-slate-500">Track UTMs, gclid, and attribution chain automatically</p>
                      </div>
                      <Switch checked={attributionEnabled} onCheckedChange={(v) => updateConfig("attribution_enabled", v)} data-testid="attribution-toggle" />
                    </div>
                  </div>

                  {/* MA-PPB-FUNNEL-060 + MA-PPB-CONSENT-114: Consent Configuration */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-indigo-400" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-slate-300">
                          Consent Language <span className="text-red-400">*</span>
                        </p>
                        <p className="text-[10px] text-slate-500">Configure TCPA consent text and policy links for the generated funnel</p>
                      </div>
                    </div>
                    <ConsentEditor
                      value={funnelConfig.consent_language || ""}
                      onChange={(html) => updateConfig("consent_language", html)}
                      privacyUrl={funnelConfig.privacy_policy_url || ""}
                      termsUrl={funnelConfig.terms_of_use_url || ""}
                      onPrivacyUrlChange={(url) => updateConfig("privacy_policy_url", url)}
                      onTermsUrlChange={(url) => updateConfig("terms_of_use_url", url)}
                      placeholder="By submitting this form, you agree to be contacted..."
                    />
                    {/* MA-PPB-CONSENT-114: Helper text when empty */}
                    {!(funnelConfig.consent_language || "").trim() && (
                      <p className="text-[10px] text-amber-300 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Consent language is required to generate your funnel.
                      </p>
                    )}
                  </div>
                  </>
                  )}
                </div>

                {/* Generate Funnel Button */}
                <Button
                  onClick={handleGenerateFunnel}
                  disabled={isGenerating || !(funnelConfig.consent_language || "").trim()}
                  className="w-full h-11 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-500/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="generate-funnel-btn"
                >
                  {isGenerating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating Funnel...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" /> Generate Funnel</>
                  )}
                </Button>
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    );
  }

  // Theme for live preview
  const themeData = draft?.builder_data?.theme || {};

  // Node type config for step list
  const NODE_TYPE_CONFIG = {
    question: { icon: MessageSquare, label: "Response", color: "text-indigo-400" },
    response: { icon: MessageSquare, label: "Response", color: "text-indigo-400" },
    input: { icon: Type, label: "Input", color: "text-purple-400" },
    consent: { icon: FileText, label: "Consent", color: "text-amber-400" },
    booking: { icon: Calendar, label: "Booking", color: "text-blue-400" },
    message: { icon: MessageSquare, label: "Message", color: "text-slate-400" },
  };

  // ═══ POST-GENERATION STATE ═══
  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="funnel" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />

      {/* Instance bar */}
      <div className="px-4 pt-3 space-y-2 flex-shrink-0">
        <StalenessIndicator draft={draft} stepKey="funnel" onRegenerate={() => handleGenerateFunnel()} onKeep={() => {
          onFieldChange("builder_data.funnel.messaging_hash", draft?.builder_data?.messaging?.messaging_hash);
        }} isGenerating={isGenerating} />
      </div>

      {/* Two-Panel: Step List (left) + Canvas Preview (right) */}
      <div className="flex-1 flex gap-3 px-4 pb-4 pt-1 min-h-0 overflow-hidden">
        {/* Left: Step list — nested card secondary */}
        <div className="w-[35%] flex flex-col min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03]">
          {/* Scrollable content — steps list or config edit */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {showConfigEdit ? (
              <>
                {/* Config Edit Mode */}
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Configuration</p>
                  <button
                    onClick={() => {
                      // Check if booking changed
                      if (bookingAtOpen !== null && bookingEnabled !== bookingAtOpen) {
                        setShowBookingPrompt(true);
                      } else {
                        setShowConfigEdit(false);
                      }
                    }}
                    className="text-[10px] text-indigo-400 hover:text-indigo-300 font-medium"
                    data-testid="config-done-btn"
                  >
                    Done
                  </button>
                </div>

                {/* Booking prompt */}
                {showBookingPrompt && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-2">
                    <p className="text-[11px] text-amber-200 leading-relaxed">
                      Booking configuration changed. You can regenerate the funnel to apply this change, or manually edit the funnel in Funnel Builder to add/remove the booking step.
                    </p>
                    <div className="flex gap-2 pt-1">
                      <Button onClick={() => { setShowBookingPrompt(false); setShowConfigEdit(false); setShowRegenConfirm(true); }} size="sm" className="h-7 text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300">
                        Regenerate
                      </Button>
                      <Button onClick={() => { setShowBookingPrompt(false); setShowConfigEdit(false); }} variant="ghost" size="sm" className="h-7 text-xs text-slate-400">
                        I'll edit manually
                      </Button>
                    </div>
                  </div>
                )}

                {configLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
                    <span className="ml-2 text-xs text-slate-400">Loading config...</span>
                  </div>
                ) : (
                  <>
                    {/* Booking Step */}
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-indigo-400" />
                          <p className="text-xs font-medium text-slate-300">Booking Step</p>
                        </div>
                        <Switch checked={bookingEnabled} onCheckedChange={(v) => updateConfig("booking_enabled", v)} data-testid="config-booking-toggle" />
                      </div>
                      {bookingEnabled && (
                        <div className="pt-2 border-t border-white/[0.06] space-y-2">
                          <Label className="text-[10px] text-slate-500 uppercase">Calendar</Label>
                          {calendars.length > 0 ? (
                            <Select value={bookingCalendar} onValueChange={(v) => updateConfig("booking_calendar", v)}>
                              <SelectTrigger className="h-8 text-xs bg-slate-900/50 border-slate-700/50" data-testid="config-booking-calendar">
                                <SelectValue placeholder="Select calendar..." />
                              </SelectTrigger>
                              <SelectContent>
                                {calendars.map((cal) => (
                                  <SelectItem key={cal.id} value={cal.calendar_id || cal.id}>
                                    <div className="flex items-center gap-2">
                                      <Calendar className="h-3 w-3 text-slate-400" />
                                      <span>{cal.name || cal.calendar_id}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="p-2 rounded bg-slate-800/30 border border-dashed border-white/10 text-center">
                              <p className="text-[10px] text-slate-500">No calendars connected</p>
                            </div>
                          )}
                          <a href="/app/admin?tab=calendars" target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300">
                            <ExternalLink className="h-2.5 w-2.5" /> Manage in Admin
                          </a>
                        </div>
                      )}
                    </div>

                    {/* TrustedForm */}
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                          <p className="text-xs font-medium text-slate-300">TrustedForm</p>
                        </div>
                        <Switch checked={trustedFormMode !== "off"} onCheckedChange={(v) => updateConfig("trusted_form", v ? "capture_only" : "off")} data-testid="config-trustedform-toggle" />
                      </div>
                      {trustedFormMode !== "off" && (
                        <div className="pt-2 border-t border-white/[0.06] space-y-2">
                          <div className="space-y-1.5">
                            <button onClick={() => updateConfig("trusted_form", "capture_only")}
                              className={cn("w-full p-2 rounded-lg text-left text-xs border transition-all",
                                trustedFormMode === "capture_only" ? "bg-blue-500/10 border-blue-500/30 text-slate-200" : "bg-white/[0.02] border-white/[0.06] text-slate-400 hover:border-white/10"
                              )} data-testid="trustedform-capture-only">
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-blue-400" />
                                <span className="font-medium">Capture Only</span>
                              </div>
                              <p className="text-[10px] text-slate-500 mt-0.5 ml-4">Certificates captured silently. Leads not restricted.</p>
                            </button>
                            <button onClick={() => updateConfig("trusted_form", "force_compliance")}
                              className={cn("w-full p-2 rounded-lg text-left text-xs border transition-all",
                                trustedFormMode === "force_compliance" ? "bg-emerald-500/10 border-emerald-500/30 text-slate-200" : "bg-white/[0.02] border-white/[0.06] text-slate-400 hover:border-white/10"
                              )} data-testid="trustedform-enforce">
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                                <span className="font-medium">Enforce Compliance</span>
                              </div>
                              <p className="text-[10px] text-slate-500 mt-0.5 ml-4">Leads without verified certificate will be quarantined.</p>
                            </button>
                          </div>
                          <a href="/app/admin?tab=trusted-form" target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300">
                            <ExternalLink className="h-2.5 w-2.5" /> Manage in Admin
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Routing */}
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Send className="h-3.5 w-3.5 text-indigo-400" />
                          <p className="text-xs font-medium text-slate-300">Routing</p>
                        </div>
                        <Switch checked={routingEnabled} onCheckedChange={(v) => updateConfig("routing_enabled", v)} data-testid="config-routing-toggle" />
                      </div>
                      {routingEnabled && (
                        <div className="pt-2 border-t border-white/[0.06] space-y-2">
                          {destinations.length > 0 ? (
                            <>
                              <div className="space-y-1.5">
                                <button onClick={() => updateConfig("routing_mode", "default")}
                                  className={cn("w-full p-2 rounded-lg text-left text-xs border transition-all",
                                    routingMode === "default" ? "bg-indigo-500/10 border-indigo-500/30 text-slate-200" : "bg-white/[0.02] border-white/[0.06] text-slate-400 hover:border-white/10"
                                  )} data-testid="routing-mode-default">
                                  <span className="font-medium">Use Default</span>
                                  <p className="text-[10px] text-slate-500 mt-0.5">Route to the tenant's default destination</p>
                                </button>
                                <button onClick={() => updateConfig("routing_mode", "specific")}
                                  className={cn("w-full p-2 rounded-lg text-left text-xs border transition-all",
                                    routingMode === "specific" ? "bg-indigo-500/10 border-indigo-500/30 text-slate-200" : "bg-white/[0.02] border-white/[0.06] text-slate-400 hover:border-white/10"
                                  )} data-testid="routing-mode-specific">
                                  <span className="font-medium">Use Specific Destination</span>
                                  <p className="text-[10px] text-slate-500 mt-0.5">Route to a specific destination for this funnel</p>
                                </button>
                              </div>
                              {routingMode === "specific" && (
                                <Select value={routingDestinationId || ""} onValueChange={(v) => updateConfig("routing_destination_id", v)}>
                                  <SelectTrigger className="h-8 text-xs bg-slate-900/50 border-slate-700/50" data-testid="routing-destination-select">
                                    <SelectValue placeholder="Select destination..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {destinations.map((dest) => (
                                      <SelectItem key={dest.id} value={dest.id}>
                                        <span>{dest.name}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </>
                          ) : (
                            <div className="p-2 rounded bg-slate-800/30 border border-dashed border-white/10 text-center">
                              <p className="text-[10px] text-slate-500">No outbound destinations configured</p>
                            </div>
                          )}
                          <a href="/app/admin?tab=outbound-setup" target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300">
                            <ExternalLink className="h-2.5 w-2.5" /> Manage in Admin
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Data Enrichment */}
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap className="h-3.5 w-3.5 text-amber-400" />
                          <p className="text-xs font-medium text-slate-300">Data Enrichment</p>
                        </div>
                        <Switch checked={dataEnrichmentEnabled} onCheckedChange={(v) => updateConfig("data_enrichment_enabled", v)} data-testid="config-enrichment-toggle" />
                      </div>
                      {dataEnrichmentEnabled && (
                        <div className="pt-2 border-t border-white/[0.06] space-y-2">
                          <div className="grid grid-cols-3 gap-1.5">
                            {[
                              { key: "standard", label: "Standard", desc: "All enrichments" },
                              { key: "strict", label: "Strict", desc: "Collected only" },
                              { key: "minimal", label: "Minimal", desc: "State & timezone" },
                            ].map((mode) => (
                              <button key={mode.key} onClick={() => updateConfig("enrichment_mode", mode.key)}
                                className={cn("p-2 rounded-lg text-center border transition-all",
                                  enrichmentMode === mode.key
                                    ? "border-indigo-500/40 bg-indigo-500/10"
                                    : "border-white/10 bg-white/[0.02] hover:border-white/20"
                                )} data-testid={`enrichment-mode-${mode.key}`}>
                                <span className={cn("text-[10px] font-semibold block", enrichmentMode === mode.key ? "text-indigo-300" : "text-slate-300")}>{mode.label}</span>
                                <span className="text-[9px] text-slate-500 block mt-0.5">{mode.desc}</span>
                              </button>
                            ))}
                          </div>
                          {funnelInstanceId && (
                            <a href={`/app/funnels/${funnelInstanceId}?tab=settings`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300">
                              <ExternalLink className="h-2.5 w-2.5" /> Manage in Funnel Settings
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Attribution */}
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-slate-300">Attribution</p>
                          <p className="text-[10px] text-slate-500">Track UTMs, gclid, and attribution chain</p>
                        </div>
                        <Switch checked={attributionEnabled} onCheckedChange={(v) => updateConfig("attribution_enabled", v)} data-testid="config-attribution-toggle" />
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
              {/* Steps list */}
              {loadingFunnel || isGenerating ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-3 w-24 bg-slate-700/30 rounded mb-3"></div>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/30">
                      <div className="h-4 w-4 bg-slate-700/40 rounded"></div>
                      <div className="flex-1 space-y-1">
                        <div className="h-3 w-3/4 bg-slate-700/40 rounded"></div>
                        <div className="h-2 w-1/2 bg-slate-700/20 rounded"></div>
                      </div>
                      <div className="h-2 w-2 bg-slate-700/30 rounded-full"></div>
                    </div>
                  ))}
                </div>
              ) : funnelNodes.length === 0 ? (
                <div className="text-center py-8 text-xs text-slate-500">
                  No funnel nodes found. Try regenerating.
                </div>
              ) : (
                <>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Steps ({funnelNodes.length})</p>
                  <div className="space-y-1">
                    {funnelNodes.map((node, index) => {
                      const typeConfig = NODE_TYPE_CONFIG[node.node_type] || NODE_TYPE_CONFIG.message;
                      const IconComp = typeConfig.icon;
                      const label = node.metadata?.label || typeConfig.label;
                      const isEntry = index === 0;
                      const isSubmit = node.metadata?.is_submit_step;
                      const isTerminal = node.content?.is_terminal;
                      const isQualified = node.content?.outcome === "qualified";

                      return (
                        <button
                          key={node.id}
                          onClick={() => setActiveStepIndex(index)}
                          className={cn(
                            "w-full p-2 rounded-lg text-left transition-all flex items-center gap-2.5",
                            activeStepIndex === index
                              ? "bg-white/[0.08] border border-indigo-500/40 shadow-sm"
                              : "bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] hover:border-white/[0.1]"
                          )}
                          data-testid={`funnel-step-${index}`}
                        >
                          <span className={cn("w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center flex-shrink-0",
                            activeStepIndex === index ? "bg-indigo-500/25 text-indigo-300" : "bg-white/[0.06] text-slate-500"
                          )}>{index + 1}</span>
                          <IconComp className={cn("h-3.5 w-3.5 flex-shrink-0", typeConfig.color)} />
                          <span className={cn("text-xs truncate flex-1",
                            activeStepIndex === index ? "text-slate-200 font-medium" : "text-slate-400"
                          )}>{label}</span>
                          <div className="flex gap-1 flex-shrink-0">
                            {isEntry && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-400 uppercase">Entry</span>}
                            {isSubmit && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 uppercase">Submit</span>}
                            {isTerminal && isQualified && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 uppercase">End</span>}
                            {isTerminal && !isQualified && node.content?.outcome && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 uppercase">End</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              </>
            )}
          </div>

          {/* Regenerate + Edit + Config — anchored to bottom */}
          <div className="flex-shrink-0 p-3 border-t border-white/[0.06] space-y-2">
              {!showRegenConfirm ? (
                <Button onClick={() => setShowRegenConfirm(true)} disabled={isGenerating || !canGenerate}
                  variant="outline" className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]">
                  <Sparkles className="h-3 w-3 mr-1.5" /> Regenerate Funnel
                </Button>
              ) : (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-2">
                  <p className="text-[11px] text-amber-200">Regenerating will create a new funnel instance.</p>
                  <div className="flex gap-2 pt-1">
                    <Button onClick={handleGenerateFunnel} disabled={isGenerating} size="sm" className="h-7 text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300">
                      {isGenerating ? "Regenerating..." : "Confirm Regenerate"}
                    </Button>
                    <Button onClick={() => setShowRegenConfirm(false)} variant="ghost" size="sm" className="h-7 text-xs text-slate-400">Cancel</Button>
                  </div>
                </div>
              )}
              {funnelInstanceId && (
                <Button
                  variant="outline"
                  className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                  data-testid="open-funnel-builder"
                  onClick={async () => {
                    // MA-261 Part A: Do NOT sync theme/header when opening Builder
                    // Canonical header and appearance are already set during generation
                    // Opening the Builder should NOT overwrite with Offer Name
                    window.open(`/app/funnels/${funnelInstanceId}`, "_blank");
                  }}
                >
                  <ExternalLink className="h-3 w-3 mr-1.5" /> Edit in Funnel Builder
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                data-testid="edit-config-btn"
                onClick={() => { setShowConfigEdit(true); setBookingAtOpen(bookingEnabled); setShowBookingPrompt(false); }}
              >
                <Settings className="h-3 w-3 mr-1.5" /> Edit Configuration
              </Button>
          </div>
        </div>

        {/* Right: Canvas Preview — nested card secondary (same as left) */}
        <div className="w-[65%] min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] overflow-hidden">
          {/* MA-THEME-OVERRIDE-097: AUDIT LAYER 2 - PPB Render Props */}
          {(() => {
            console.log("═══════════════════════════════════════════════════");
            console.log("🔍 RENDER AUDIT LAYER 2: PPB Passing to FunnelLivePreview");
            console.log("funnelAppearance:", JSON.stringify(funnelAppearance, null, 2));
            console.log("themeData:", JSON.stringify(themeData, null, 2));
            console.log("funnelStylingMode:", funnelStylingMode);
            console.log("draft?.builder_data?.theme:", draft?.builder_data?.theme ? "exists" : "missing");
            console.log("═══════════════════════════════════════════════════");
            return null;
          })()}
          
          {/* MA-PPB-LOADING-SKELETON: Show compact skeleton during regeneration */}
          {(isGenerating || loadingFunnel) ? (
            <div className="flex items-center justify-center h-full min-h-[600px]">
              <div className="w-full max-w-[440px]">
                {/* Compact funnel card skeleton - matches LP skeleton pattern */}
                <div className="rounded-2xl border border-slate-700/40 bg-slate-900/50 overflow-hidden shadow-xl opacity-50 pointer-events-none select-none">
                  {/* Header skeleton */}
                  <div className="flex items-center gap-2 px-4 py-3 bg-slate-800/60 border-b border-slate-700/30 animate-pulse">
                    <div className="h-5 w-5 bg-slate-700/50 rounded-full"></div>
                    <div className="h-4 w-36 bg-slate-700/50 rounded"></div>
                  </div>
                  
                  {/* Thread area skeleton */}
                  <div className="px-4 py-4 space-y-3 bg-slate-900/60 animate-pulse" style={{ minHeight: '180px' }}>
                    <div className="flex gap-2 items-start">
                      <div className="h-8 w-8 bg-slate-700/40 rounded-full flex-shrink-0"></div>
                      <div className="flex-1 bg-slate-800/60 rounded-2xl p-3 space-y-2">
                        <div className="h-3 w-4/5 bg-slate-700/40 rounded"></div>
                        <div className="h-3 w-3/5 bg-slate-700/30 rounded"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Response area skeleton */}
                  <div className="px-4 py-4 space-y-2 bg-slate-900/40 border-t border-slate-700/20 animate-pulse" style={{ minHeight: '140px' }}>
                    <div className="h-11 w-full bg-slate-700/40 rounded-xl"></div>
                    <div className="h-11 w-full bg-slate-700/30 rounded-xl"></div>
                  </div>
                  
                  {/* Loading text with divider - matches LP step pattern */}
                  <div className="flex-shrink-0 px-4 py-3 border-t border-slate-700/30">
                    <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {isGenerating ? "Regenerating funnel..." : "Loading funnel..."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <FunnelLivePreview
              nodes={funnelNodes}
              appearance={funnelAppearance}
              header={funnelHeader}
              activeStepIndex={activeStepIndex}
              onStepClick={setActiveStepIndex}
              theme={themeData}
              lpTokens={resolvedLpTokens}
              funnelConfig={funnelConfig}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MA-SCRIPT-INTEGRATION-001: Script Node Type Config (mirrors Funnel Step)
// =============================================================================
const SCRIPT_NODE_OPTION_TYPES = {
  normal: { label: "Response", color: "text-indigo-400" },
  objection: { label: "Objection", color: "text-amber-400" },
  qualifying: { label: "Qualifying", color: "text-emerald-400" },
  close: { label: "Close", color: "text-pink-400" },
};

// =============================================================================
// MA-SCRIPT-INTEGRATION-001: Script Step — Linked Asset Model
// Mirrors Funnel Step pattern: generate real Script → preview nodes → deep link
// =============================================================================
function ScriptStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete }) {
  const [activeNodeIndex, setActiveNodeIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);

  // Core data
  const scriptData = draft?.builder_data?.script || {};
  const scriptInstanceId = scriptData.script_instance_id || null;
  const funnelData = draft?.builder_data?.funnel || {};
  const funnelInstanceId = funnelData.funnel_instance_id || null;
  const messaging = draft?.builder_data?.messaging || {};
  const concepts = draft?.builder_data?.creative?.concepts || [];
  const selectedConcept = concepts.find(c => c.is_selected);
  const canGenerate = !!funnelInstanceId && !!selectedConcept;

  // ═══ Fetch script nodes from real Script instance ═══
  const [scriptNodes, setScriptNodes] = useState([]);
  const [scriptOptions, setScriptOptions] = useState([]);
  const [scriptMeta, setScriptMeta] = useState({});
  const [loadingScript, setLoadingScript] = useState(false);

  const fetchScriptData = useCallback(() => {
    if (!scriptInstanceId) { setScriptNodes([]); setScriptOptions([]); setScriptMeta({}); return; }
    setLoadingScript(true);
    api.get(`/scripts/${scriptInstanceId}/nodes`).then(res => {
      const graphData = res.data?.nodes || [];
      // The response is { nodes: [ { node: {...}, options: [...] }, ... ] }
      const nodes = graphData.map(item => item.node || item);
      const options = graphData.flatMap(item => item.options || []);
      // Sort nodes by sort_order
      nodes.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setScriptNodes(nodes);
      setScriptOptions(options);
    }).catch(err => {
      console.warn("[SCRIPT] Fetch script nodes failed:", err.message);
    });
    // Also fetch script metadata
    api.get(`/scripts/${scriptInstanceId}`).then(res => {
      setScriptMeta(res.data || {});
    }).catch(err => {
      console.warn("[SCRIPT] Fetch script meta failed:", err.message);
    }).finally(() => setLoadingScript(false));
  }, [scriptInstanceId]);

  useEffect(() => { fetchScriptData(); }, [fetchScriptData]);

  // Re-fetch script data when user returns from Script Builder tab
  useEffect(() => {
    if (!scriptInstanceId) return;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchScriptData();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [scriptInstanceId, fetchScriptData]);

  // ═══ Generate Script via ScriptAssist ═══
  const handleGenerateScript = useCallback(async () => {
    if (!canGenerate || isGenerating) return;
    setIsGenerating(true);
    setShowRegenConfirm(false);
    onFieldChange("builder_data.script.generation_status", "generating");

    try {
      const context = draft?.builder_data?.context || {};
      const audience = draft?.builder_data?.audience || {};
      const offer = draft?.builder_data?.offer || {};
      const ppName = draft?.name || "Script";
      const vertical = context.vertical || "";
      const mechanism = selectedConcept?.mechanism || messaging?.unique_mechanism || "";
      const hook = selectedConcept?.hook || messaging?.primary_hook || "";

      // Step 1: Generate AI spec
      const generatePayload = {
        provider: "openai",
        model: "gpt-4o",
        product_type_id: "test_product_type_001",
        script_type_id: "test_script_type_001",
        title: `${ppName} Script`,
        description: `Sales script for ${ppName}. ${offer.core_promise || ""}`,
        goal: offer.core_promise || `Qualify and convert leads for ${vertical || "insurance"}`,
        audience: audience.primary_audience || "Prospective clients",
        tone: getFieldValue(messaging?.tone, "") || "professional",
        conversation_style: "consultative",
        stages: [
          "Opening & Rapport",
          "Qualification",
          "Needs Discovery",
          mechanism ? `Mechanism: ${mechanism}` : "Solution Presentation",
          "Objection Handling",
          "Close & Next Steps",
        ].filter(Boolean),
        objections: ["price", "spouse", "timing", "already_insured", "trust"],
        data_fields: [],
        quote_fields: [],
        // MA-ARCH-142: Message alignment
        message_category: messaging?.message_category || selectedConcept?.hook_type || "",
        message_variant: messaging?.angle || "",
        opening_hook: hook,
        profit_path_context: JSON.stringify({
          vertical,
          channel: context.channel || "",
          platform: context.platform || "",
          offer_name: offer.offer_name || "",
          mechanism,
          audience: audience.primary_audience || "",
          pain: audience.core_problem || "",
        }),
        // MA-ARCH-146: Ad copy fields
        ad_headline: hook,
        ad_primary_text: selectedConcept?.primary_text || "",
        // MA-ARCH-147: Funnel context
        funnel_context: {
          funnel_type: funnelData.funnel_type || "diagnostic",
          qualifying_questions: (funnelData.steps || []).filter(s => s.type === "question" || s.type === "response").map(s => s.headline).filter(Boolean),
          key_qualifiers: [audience.core_problem, audience.trigger_intent].filter(Boolean),
          flow_type: funnelData.funnel_type || "diagnostic",
        },
        // MA-ARCH-148: Vertical
        vertical,
        // MA-KNOWLEDGE-INJECTION-004: Pass profit path ID for knowledge injection
        profit_path_id: draft?.id,
        remember_key: false,
      };

      const specRes = await api.post("/scripts/ai-generate", generatePayload);
      const spec = specRes.data?.spec;

      if (!spec || !spec.nodes || spec.nodes.length === 0) {
        throw new Error("AI returned empty spec");
      }

      // Step 2: Create script from spec
      const createRes = await api.post("/scripts/ai-create", {
        provider: "openai",
        model: "gpt-4o",
        product_type_id: "test_product_type_001",
        script_type_id: "test_script_type_001",
        spec,
        message_category: generatePayload.message_category,
        message_variant: generatePayload.message_variant,
        opening_hook: generatePayload.opening_hook,
      });

      const newScriptId = createRes.data?.script_id;
      if (!newScriptId) throw new Error("Script creation failed — no script_id returned");

      // Store script instance reference
      onFieldChange("builder_data.script.script_instance_id", newScriptId);
      onFieldChange("builder_data.script.generation_status", "complete");
      onFieldChange("builder_data.script.generation_source", "script_assist");
      // Store messaging hash for staleness detection
      const currentHash = draft?.builder_data?.messaging?.messaging_hash;
      if (currentHash) onFieldChange("builder_data.script.messaging_hash", currentHash);

      // Persist to DB: read latest server data, merge script, write back
      try {
        const ppRes = await api.get(`/profit-paths/${draft?.id}`);
        const serverBd = ppRes.data?.builder_data || {};
        await api.patch(`/profit-paths/${draft?.id}`, {
          builder_data: {
            ...serverBd,
            script: {
              ...(serverBd.script || {}),
              script_instance_id: newScriptId,
              generation_status: "complete",
              generation_source: "script_assist",
              messaging_hash: currentHash || null,
            }
          }
        });
      } catch (err) {
        console.warn("[SCRIPT] Persist failed:", err.message);
      }

      toast({ title: "Script generated", description: "Review your script nodes in the preview." });

    } catch (err) {
      console.error("[SCRIPT] Generation failed:", err);
      onFieldChange("builder_data.script.generation_status", "error");
      toast({
        title: "Script generation failed",
        description: err.response?.data?.detail || err.message || "Please try again.",
        variant: "destructive",
      });
    }

    setIsGenerating(false);
    setActiveNodeIndex(0);
  }, [canGenerate, isGenerating, selectedConcept, messaging, funnelData, draft, onFieldChange]);

  // Resolve active node + its options
  const activeNode = scriptNodes[activeNodeIndex] || null;
  const activeNodeOptions = activeNode
    ? scriptOptions.filter(o => o.from_node_id === activeNode.id)
    : [];

  // ═══ PRE-GENERATION STATE ═══
  if (!scriptInstanceId) {
    return (
      <div className="h-full flex flex-col">
        <StepHeader stepId="script" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-5">
            <StalenessIndicator draft={draft} stepKey="script" onRegenerate={() => handleGenerateScript()} onKeep={() => {
              onFieldChange("builder_data.script.messaging_hash", draft?.builder_data?.messaging?.messaging_hash);
            }} isGenerating={isGenerating} />

            {!canGenerate && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-300">Complete your Funnel step first to generate your script.</p>
              </div>
            )}

            {canGenerate && (
              <>
                <div className="space-y-3">
                  <p className="text-xs text-slate-400">Generate a full execution script from your Profit Path context using ScriptAssist.</p>
                  
                  {/* Context preview */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Generation Context</p>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedConcept?.hook && (
                        <div className="text-[10px]">
                          <span className="text-slate-500">Hook:</span>{" "}
                          <span className="text-slate-300">{selectedConcept.hook.slice(0, 50)}...</span>
                        </div>
                      )}
                      {selectedConcept?.mechanism && (
                        <div className="text-[10px]">
                          <span className="text-slate-500">Mechanism:</span>{" "}
                          <span className="text-slate-300">{selectedConcept.mechanism.slice(0, 40)}...</span>
                        </div>
                      )}
                      {messaging?.angle && (
                        <div className="text-[10px]">
                          <span className="text-slate-500">Angle:</span>{" "}
                          <span className="text-slate-300">{getFieldValue(messaging.angle, "").slice(0, 40)}</span>
                        </div>
                      )}
                      {funnelData.funnel_type && (
                        <div className="text-[10px]">
                          <span className="text-slate-500">Funnel:</span>{" "}
                          <span className="text-slate-300">{funnelData.funnel_type}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleGenerateScript}
                  disabled={isGenerating}
                  className="w-full h-11 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-500/20 rounded-lg"
                  data-testid="generate-script-btn"
                >
                  {isGenerating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating Script...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" /> Generate Script</>
                  )}
                </Button>
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    );
  }

  // ═══ POST-GENERATION STATE ═══
  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="script" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />

      {/* Instance bar */}
      <div className="px-4 pt-3 space-y-2 flex-shrink-0">
        <StalenessIndicator draft={draft} stepKey="script" onRegenerate={() => handleGenerateScript()} onKeep={() => {
          onFieldChange("builder_data.script.messaging_hash", draft?.builder_data?.messaging?.messaging_hash);
        }} isGenerating={isGenerating} />
      </div>

      {/* Two-Panel: Node List (left) + Node Preview (right) */}
      <div className="flex-1 flex gap-3 px-4 pb-4 pt-1 min-h-0 overflow-hidden">
        {/* MA-PPB-LOADING-SKELETON: Show skeleton during loading or generation */}
        {(loadingScript || isGenerating) ? (
          <>
            {/* Left: Node list skeleton */}
            <div className="w-[35%] flex flex-col min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] opacity-50 pointer-events-none select-none">
              <div className="flex-1 overflow-y-auto p-3 space-y-3 animate-pulse">
                {/* Header skeleton */}
                <div className="h-3 w-24 bg-slate-700/40 rounded"></div>
                
                {/* Node items skeleton */}
                <div className="space-y-1">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="w-full p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded bg-slate-700/40 flex-shrink-0"></div>
                      <div className="h-3.5 w-3.5 bg-slate-700/30 rounded flex-shrink-0"></div>
                      <div className="h-3 bg-slate-700/40 rounded flex-1"></div>
                      <div className="h-3 w-10 bg-slate-700/30 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action buttons skeleton */}
              <div className="flex-shrink-0 p-3 border-t border-white/[0.06] space-y-2">
                <div className="w-full h-8 bg-slate-700/30 rounded"></div>
                <div className="w-full h-8 bg-slate-700/20 rounded"></div>
                <div className="w-full h-8 bg-slate-700/20 rounded"></div>
              </div>
            </div>

            {/* Right: Node preview skeleton */}
            <div className="w-[65%] min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] overflow-hidden flex flex-col opacity-50 pointer-events-none select-none">
              {/* Preview body skeleton */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 animate-pulse">
                {/* Node header skeleton */}
                <div className="space-y-1">
                  <div className="h-3 w-16 bg-slate-700/40 rounded"></div>
                  <div className="h-4 w-48 bg-slate-700/40 rounded"></div>
                </div>

                {/* Your Reply section skeleton */}
                <div className="space-y-1.5">
                  <div className="h-3 w-20 bg-slate-700/40 rounded"></div>
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                    <div className="h-3 w-full bg-slate-700/40 rounded"></div>
                    <div className="h-3 w-5/6 bg-slate-700/30 rounded"></div>
                    <div className="h-3 w-4/5 bg-slate-700/30 rounded"></div>
                  </div>
                </div>

                {/* Coaching note skeleton */}
                <div className="space-y-1.5">
                  <div className="h-3 w-24 bg-slate-700/40 rounded"></div>
                  <div className="p-3 rounded-lg bg-indigo-500/5 border border-indigo-500/15 space-y-2">
                    <div className="h-3 w-full bg-slate-700/30 rounded"></div>
                    <div className="h-3 w-3/4 bg-slate-700/20 rounded"></div>
                  </div>
                </div>

                {/* Prospect responses skeleton */}
                <div className="space-y-1.5">
                  <div className="h-3 w-32 bg-slate-700/40 rounded"></div>
                  <div className="space-y-1">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="w-full p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center gap-2">
                        <div className="h-3 w-3 bg-slate-700/40 rounded"></div>
                        <div className="h-3 bg-slate-700/40 rounded flex-1"></div>
                        <div className="h-3 w-12 bg-slate-700/30 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Loading text with divider - matches LP/Funnel step pattern */}
              <div className="flex-shrink-0 p-3 border-t border-white/[0.06]">
                <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {isGenerating ? "Generating script..." : "Loading script..."}
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Left: Node list */}
            <div className="w-[35%] flex flex-col min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03]">
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {scriptNodes.length === 0 ? (
                  <div className="text-center py-8 text-xs text-slate-500">
                    No script nodes found. Try regenerating.
                  </div>
                ) : (
                  <>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Nodes ({scriptNodes.length})</p>
                    <div className="space-y-1">
                      {scriptNodes.map((node, index) => {
                        const isStart = node.id === scriptMeta.start_node_id;
                        const isGreeting = node.id === scriptMeta.greeting_node_id || node.node_type === "greeting";
                        const nodeOptions = scriptOptions.filter(o => o.from_node_id === node.id);
                        const hasObjection = nodeOptions.some(o => o.option_type === "objection");
                        const hasClose = nodeOptions.some(o => o.option_type === "close");

                        return (
                          <button
                            key={node.id}
                            onClick={() => setActiveNodeIndex(index)}
                            className={cn(
                              "w-full p-2 rounded-lg text-left transition-all flex items-center gap-2.5",
                              activeNodeIndex === index
                                ? "bg-white/[0.08] border border-indigo-500/40 shadow-sm"
                                : "bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] hover:border-white/[0.1]"
                            )}
                            data-testid={`script-node-${index}`}
                          >
                            <span className={cn("w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center flex-shrink-0",
                              activeNodeIndex === index ? "bg-indigo-500/25 text-indigo-300" : "bg-white/[0.06] text-slate-500"
                            )}>{index + 1}</span>
                            <FileText className={cn("h-3.5 w-3.5 flex-shrink-0",
                              activeNodeIndex === index ? "text-indigo-400" : "text-slate-500"
                            )} />
                            <span className={cn("text-xs truncate flex-1",
                              activeNodeIndex === index ? "text-slate-200 font-medium" : "text-slate-400"
                            )}>{node.title || "Untitled"}</span>
                            <div className="flex gap-1 flex-shrink-0">
                              {isGreeting && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 uppercase">Greeting</span>}
                              {isStart && !isGreeting && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-400 uppercase">Start</span>}
                              {hasObjection && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 uppercase">Obj</span>}
                              {hasClose && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 uppercase">Close</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Action buttons — anchored to bottom */}
              <div className="flex-shrink-0 p-3 border-t border-white/[0.06] space-y-2">
                {!showRegenConfirm ? (
                  <Button onClick={() => setShowRegenConfirm(true)} disabled={isGenerating || !canGenerate}
                    variant="outline" className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                    data-testid="regenerate-script-btn"
                  >
                    <Sparkles className="h-3 w-3 mr-1.5" /> Regenerate Script
                  </Button>
                ) : (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-2">
                    <p className="text-[11px] text-amber-200">Regenerating will create a new script instance.</p>
                    <div className="flex gap-2 pt-1">
                      <Button onClick={handleGenerateScript} disabled={isGenerating} size="sm" className="h-7 text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300">
                        {isGenerating ? "Regenerating..." : "Confirm Regenerate"}
                      </Button>
                      <Button onClick={() => setShowRegenConfirm(false)} variant="ghost" size="sm" className="h-7 text-xs text-slate-400">Cancel</Button>
                    </div>
                  </div>
                )}
                {scriptInstanceId && (
                  <Button
                    variant="outline"
                    className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                    data-testid="open-script-builder"
                    onClick={() => window.open(`/app/scripts/${scriptInstanceId}/edit`, "_blank")}
                  >
                    <ExternalLink className="h-3 w-3 mr-1.5" /> Edit in Script Builder
                  </Button>
                )}
                {scriptInstanceId && (
                  <Button
                    variant="outline"
                    className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                    data-testid="run-script-btn"
                    onClick={() => window.open(`/app/scripts/${scriptInstanceId}/play?sandbox=true`, "_blank")}
                  >
                    <Play className="h-3 w-3 mr-1.5" /> Test Run
                  </Button>
                )}
              </div>
            </div>

            {/* Right: Node Preview */}
            <div className="w-[65%] min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] overflow-hidden flex flex-col">
              {activeNode ? (
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Node Header */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-500 uppercase font-medium">Node {activeNodeIndex + 1}</span>
                      {activeNode.scope === "plan_options" && (
                        <Badge className="h-4 text-[8px] bg-purple-500/20 text-purple-400 border-0">Plan Options</Badge>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-slate-200">{activeNode.title || "Untitled Node"}</h3>
                  </div>

                  {/* Your Reply */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Your Reply</p>
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                      <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
                        {activeNode.your_reply || <span className="text-slate-600 italic">No script text</span>}
                      </p>
                    </div>
                  </div>

                  {/* Coaching Note */}
                  {activeNode.coaching_note && (
                    <div className="space-y-1.5">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Coaching Note</p>
                      <div className="p-3 rounded-lg bg-indigo-500/5 border border-indigo-500/15">
                        <p className="text-xs text-indigo-300/80 leading-relaxed">{activeNode.coaching_note}</p>
                      </div>
                    </div>
                  )}

                  {/* Prospect Responses */}
                  {activeNodeOptions.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Prospect Says ({activeNodeOptions.length})</p>
                      <div className="space-y-1">
                        {activeNodeOptions.map((opt) => {
                          const typeConfig = SCRIPT_NODE_OPTION_TYPES[opt.option_type] || SCRIPT_NODE_OPTION_TYPES.normal;
                          const targetNode = scriptNodes.find(n => n.id === opt.target_node_id);
                          return (
                            <button
                              key={opt.id}
                              onClick={() => {
                                const targetIdx = scriptNodes.findIndex(n => n.id === opt.target_node_id);
                                if (targetIdx >= 0) setActiveNodeIndex(targetIdx);
                              }}
                              className="w-full p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.1] transition-all text-left flex items-center gap-2"
                              data-testid={`script-option-${opt.id}`}
                            >
                              <ArrowRight className="h-3 w-3 text-slate-500 flex-shrink-0" />
                              <span className="text-xs text-slate-300 flex-1 truncate">{opt.prospect_says || "Continue"}</span>
                              {opt.option_type && opt.option_type !== "normal" && (
                                <span className={cn("text-[8px] font-bold px-1.5 py-0.5 rounded uppercase",
                                  opt.option_type === "objection" ? "bg-amber-500/20 text-amber-400" :
                                  opt.option_type === "close" ? "bg-emerald-500/20 text-emerald-400" :
                                  opt.option_type === "qualifying" ? "bg-blue-500/20 text-blue-400" :
                                  "bg-slate-500/20 text-slate-400"
                                )}>
                                  {typeConfig.label}
                                </span>
                              )}
                              {targetNode && (
                                <span className="text-[9px] text-slate-600 truncate max-w-[100px]">{targetNode.title}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-xs text-slate-500">Select a node to preview</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MA-LP-PPB-MINI-010B: Landing Page Step — Mini Builder (Orchestration Layer)
// PPB does not own Landing Pages. It orchestrates them through asset instances.
// Full editing is done in the standalone LP Builder (010C).
// =============================================================================

function LandingPageStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete, onLPContextChanged, resolvedLpTokens }) {
  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="landing_page" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />
      <LPMiniStep draft={draft} onFieldChange={onFieldChange} onLPContextChanged={onLPContextChanged} resolvedLpTokens={resolvedLpTokens} />
    </div>
  );
}



// =============================================================================
// MA-UI-069: Review Step - End-to-End Profit Path Validation & Approval
// Active validation system that audits alignment, surfaces issues, gates approval
// =============================================================================


// AISuggestionsSection removed (MA-PPB-REVIEW-059)


// =============================================================================
// MA-UI-069/070: Review Step
// =============================================================================
const REVIEW_STATUS = {
  HEALTHY: "healthy",
  NEEDS_ATTENTION: "needs_attention",
  INCOMPLETE: "incomplete"
};

// Validation check types
const VALIDATION_TYPES = {
  CREATIVE_FUNNEL: "creative_funnel",
  FUNNEL_SCRIPT: "funnel_script",
  CTA_ALIGNMENT: "cta_alignment",
  MECHANISM_INTEGRITY: "mechanism_integrity"
};


// =============================================================================
// MA-PPB-PUBLISH-064 P2+P3: Publish Controls Component
// =============================================================================
function PublishControls({ draft, publishReadiness, onPublishComplete, lpApiContext }) {
  const [publishState, setPublishState] = useState({
    funnel: null, // null | "publishing" | "success" | "error"
    lp: null,
    funnelError: null,
    lpError: null,
  });

  const funnelId = draft?.builder_data?.funnel?.funnel_instance_id;
  const lpVersionId = lpApiContext?.current_version?.id;
  
  const funnelReadiness = publishReadiness.funnel;
  const lpReadiness = publishReadiness.lp;
  
  const funnelReady = funnelReadiness?.status === "READY" || funnelReadiness?.status === "WARNING";
  const lpReady = lpReadiness?.status === "READY" || lpReadiness?.status === "WARNING";
  
  // P3: Derive explicit publish state per asset
  const funnelPublished = funnelReadiness?.publish_state === "PUBLISHED" || publishState.funnel === "success";
  const lpPublished = lpReadiness?.publish_state === "PUBLISHED" || publishState.lp === "success";
  const bothPublished = funnelPublished && lpPublished;
  
  const bothReady = (funnelReady || funnelPublished) && (lpReady || lpPublished);
  const isPublishing = publishState.funnel === "publishing" || publishState.lp === "publishing";

  // P3: Build live LP URL
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const lpRoutePath = lpReadiness?.route_path || lpApiContext?.current_version?.route_path;
  const liveLpUrl = lpRoutePath ? `${baseUrl}/api/landing-pages/serve/${lpRoutePath}` : null;

  const publishFunnel = useCallback(async () => {
    if (!funnelId) return false;
    // P3: Idempotent — skip if already published
    if (funnelPublished) return true;
    if (!funnelReady) return false;
    
    setPublishState(prev => ({ ...prev, funnel: "publishing", funnelError: null }));
    try {
      await api.post(`/funnels/${funnelId}/publish`);
      setPublishState(prev => ({ ...prev, funnel: "success" }));
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' 
        ? (detail.errors || [detail.message]).join("; ") 
        : detail || err.message || "Publish failed";
      setPublishState(prev => ({ ...prev, funnel: "error", funnelError: msg }));
      return false;
    }
  }, [funnelId, funnelReady, funnelPublished]);

  const publishLP = useCallback(async () => {
    if (!lpVersionId) return false;
    // P3: Idempotent — skip if already published
    if (lpPublished) return true;
    if (!lpReady) return false;
    
    setPublishState(prev => ({ ...prev, lp: "publishing", lpError: null }));
    try {
      await api.post(`/landing-pages/versions/${lpVersionId}/publish`, {
        change_summary: "Published from Profit Path Builder"
      });
      setPublishState(prev => ({ ...prev, lp: "success" }));
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object'
        ? (detail.errors || [detail.message]).join("; ")
        : detail || err.message || "Publish failed";
      setPublishState(prev => ({ ...prev, lp: "error", lpError: msg }));
      return false;
    }
  }, [lpVersionId, lpReady, lpPublished]);

  const publishAll = useCallback(async () => {
    if (bothPublished) return; // P3: Idempotent no-op
    
    let funnelOk = funnelPublished; // Skip if already published
    
    if (!funnelOk && funnelId && funnelReady) {
      funnelOk = await publishFunnel();
    }
    
    if (funnelOk && !lpPublished && lpVersionId && lpReady) {
      await publishLP();
    }
    
    onPublishComplete?.();
  }, [publishFunnel, publishLP, funnelId, lpVersionId, funnelReady, lpReady, funnelPublished, lpPublished, bothPublished, onPublishComplete]);

  return (
    <div className="space-y-3" data-testid="publish-controls">
      {/* P3: Post-Publish Confirmation Banner */}
      {bothPublished && (
        <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30" data-testid="publish-success-banner">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-300">Profit Path is Live</span>
          </div>
          <div className="space-y-1.5 ml-6">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider w-12">Funnel</span>
              <span className="text-xs text-slate-300">Published</span>
              {funnelReadiness?.published_at && (
                <span className="text-[10px] text-slate-500">{new Date(funnelReadiness.published_at).toLocaleDateString()}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider w-12">LP</span>
              <span className="text-xs text-slate-300">Published</span>
              {lpReadiness?.published_at && (
                <span className="text-[10px] text-slate-500">{new Date(lpReadiness.published_at).toLocaleDateString()}</span>
              )}
            </div>
            {liveLpUrl && (
              <a
                href={liveLpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 mt-1"
                data-testid="live-lp-link"
              >
                <ExternalLink className="h-3 w-3" />
                View Live Landing Page
              </a>
            )}
          </div>
        </div>
      )}

      {/* Publish State Indicators (when not both published) */}
      {!bothPublished && (funnelPublished || lpPublished) && (
        <div className="space-y-1">
          {funnelPublished && (
            <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-emerald-500/5 border border-emerald-500/15">
              <CheckCircle className="h-3 w-3 text-emerald-400" />
              <span className="text-[11px] text-emerald-300">Funnel is live</span>
            </div>
          )}
          {lpPublished && (
            <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-emerald-500/5 border border-emerald-500/15">
              <CheckCircle className="h-3 w-3 text-emerald-400" />
              <span className="text-[11px] text-emerald-300">Landing Page is live</span>
              {liveLpUrl && (
                <a href={liveLpUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-400 hover:text-indigo-300 ml-auto">
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Publish buttons (hidden when both are published) */}
      {!bothPublished && (
        <>
          <div className="flex gap-2">
            <Button
              onClick={publishFunnel}
              disabled={(!funnelReady && !funnelPublished) || isPublishing || funnelPublished}
              variant="outline"
              size="sm"
              className={cn(
                "flex-1 h-8 text-[11px] gap-1.5 transition-all",
                funnelPublished
                  ? "border-emerald-500/30 text-emerald-400"
                  : funnelReady
                    ? "border-slate-700/50 text-slate-300 hover:border-indigo-500/30 hover:text-indigo-300"
                    : "border-slate-700/30 text-slate-600 cursor-not-allowed"
              )}
              data-testid="publish-funnel-btn"
            >
              {publishState.funnel === "publishing" && <Loader2 className="h-3 w-3 animate-spin" />}
              {funnelPublished && <CheckCircle className="h-3 w-3" />}
              {funnelPublished ? "Funnel Live" : "Publish Funnel"}
            </Button>
            
            <Button
              onClick={publishLP}
              disabled={(!lpReady && !lpPublished) || isPublishing || lpPublished}
              variant="outline"
              size="sm"
              className={cn(
                "flex-1 h-8 text-[11px] gap-1.5 transition-all",
                lpPublished
                  ? "border-emerald-500/30 text-emerald-400"
                  : lpReady
                    ? "border-slate-700/50 text-slate-300 hover:border-indigo-500/30 hover:text-indigo-300"
                    : "border-slate-700/30 text-slate-600 cursor-not-allowed"
              )}
              data-testid="publish-lp-btn"
            >
              {publishState.lp === "publishing" && <Loader2 className="h-3 w-3 animate-spin" />}
              {lpPublished && <CheckCircle className="h-3 w-3" />}
              {lpPublished ? "LP Live" : "Publish LP"}
            </Button>
          </div>

          <Button
            onClick={publishAll}
            disabled={!bothReady || isPublishing}
            size="sm"
            className={cn(
              "w-full h-9 text-xs font-medium rounded-md transition-all duration-150 ease-out",
              bothReady && !isPublishing
                ? cn(
                    "bg-gradient-to-b from-indigo-500 to-indigo-600",
                    "shadow-md shadow-indigo-500/25",
                    "text-white border-0",
                    "hover:from-indigo-400 hover:to-indigo-500",
                    "hover:-translate-y-px",
                    "active:translate-y-0 active:shadow-sm"
                  )
                : "bg-slate-800/50 text-slate-500 border border-slate-700/30 cursor-not-allowed"
            )}
            data-testid="publish-all-btn"
          >
            {isPublishing ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Publishing...</>
            ) : (
              "Publish All"
            )}
          </Button>
        </>
      )}

      {/* Error messages */}
      {publishState.funnelError && (
        <p className="text-[10px] text-red-400 px-1" data-testid="publish-funnel-error">
          Funnel: {publishState.funnelError}
        </p>
      )}
      {publishState.lpError && (
        <p className="text-[10px] text-red-400 px-1" data-testid="publish-lp-error">
          LP: {publishState.lpError}
        </p>
      )}
    </div>
  );
}


function ReviewStepPanel({ draft, onFieldChange, onNext, isLastStep, isStepComplete, lpApiContext }) {
  const navigate = useNavigate();
  const { isActivated } = useActivation();

  // MA-PPB-PUBLISH-064 P1: Publish Readiness State
  const [publishReadiness, setPublishReadiness] = useState({ funnel: null, lp: null, loading: false });

  // Mark review as visited on mount — drives step completion check
  useEffect(() => {
    if (!draft?.builder_data?.review_visited) {
      onFieldChange("builder_data.review_visited", true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // MA-PPB-PUBLISH-064 P1: Evaluate publish readiness on mount + when key data changes
  useEffect(() => {
    const funnelId = draft?.builder_data?.funnel?.funnel_instance_id;
    const lpVersionId = lpApiContext?.current_version?.id;
    
    if (!funnelId && !lpVersionId) return;
    
    let cancelled = false;
    const evaluate = async () => {
      setPublishReadiness(prev => ({ ...prev, loading: true }));
      
      const results = { funnel: null, lp: null, loading: false };
      
      try {
        if (funnelId) {
          const funnelRes = await api.get(`/funnels/${funnelId}/publish-readiness`);
          if (!cancelled) results.funnel = funnelRes.data;
        }
      } catch (err) {
        if (!cancelled) results.funnel = { status: "BLOCKED", issues: [{ field: "api", message: "Could not evaluate funnel readiness" }], warnings: [] };
      }
      
      try {
        if (lpVersionId) {
          const lpRes = await api.get(`/landing-pages/versions/${lpVersionId}/publish-readiness`);
          if (!cancelled) results.lp = lpRes.data;
        }
      } catch (err) {
        if (!cancelled) results.lp = { status: "BLOCKED", issues: [{ field: "api", message: "Could not evaluate LP readiness" }], warnings: [] };
      }
      
      if (!cancelled) setPublishReadiness(results);
    };
    
    evaluate();
    return () => { cancelled = true; };
  }, [draft?.builder_data?.funnel?.funnel_instance_id, lpApiContext?.current_version?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================================================
  // MA-UI-075: Use centralized getFieldValue for all biased field access
  // ==========================================================================

  // ==========================================================================
  // MA-UI-070: Severity Constants
  // ==========================================================================
  const SEVERITY = {
    CRITICAL: "critical",  // Blocks approval (red)
    HIGH: "high",          // Major risk (orange)
    WARNING: "warning"     // Optimization (yellow)
  };

  // ==========================================================================
  // MA-UI-070: Impact Statements for Issues (Why It Matters)
  // ==========================================================================
  const ISSUE_IMPACTS = {
    // Step completion impacts
    context_incomplete: "Without business context, attribution and War Room grouping are impossible",
    foundation_incomplete: "Without a clear objective, the entire strategy lacks direction",
    audience_incomplete: "Messaging cannot resonate without defined audience understanding",
    offer_incomplete: "No clear value proposition means weak conversion potential",
    messaging_incomplete: "Inconsistent messaging creates confusion and drops engagement",
    creative_incomplete: "No creative direction means ad execution will be unfocused",
    funnel_incomplete: "Missing funnel steps break the user journey before conversion",
    script_incomplete: "No delivery system means the strategy cannot be executed",
    // Alignment impacts
    mechanism_not_defined: "The core differentiator is missing, reducing competitive advantage",
    mechanism_not_reflected: "Script doesn't explain the value mechanism, losing persuasion power",
    funnel_type_mismatch: "Mismatched funnel type may confuse users and reduce conversions",
    funnel_cta_misaligned: "Generic CTA doesn't match the promised value, breaking trust",
    script_cta_misaligned: "Script close doesn't align with promised action, reducing conversions",
    mechanism_inconsistent: "Inconsistent messaging creates doubt and reduces credibility",
    // hook_mismatch: removed (MA-PPB-REVIEW-059) — false positive by design
  };

  // ==========================================================================
  // MA-UI-069/070: getReviewStatus() - Completion Audit Engine with Severity
  // MA-UI-075: Use getFieldValue for all biased field access
  // ==========================================================================
  const getReviewStatus = useCallback(() => {
    const blockingIssues = [];
    const warnings = [];
    const strengths = [];
    
    // Get all step data
    const foundation = draft?.builder_data?.foundation || {};
    const audience = draft?.builder_data?.audience || {};
    const offer = draft?.builder_data?.offer || {};
    const messaging = draft?.builder_data?.messaging || {};
    const creative = draft?.builder_data?.creative || {};
    const funnel = draft?.builder_data?.funnel || {};
    const script = draft?.builder_data?.script || {};
    
    const concepts = creative?.concepts || [];
    const selectedConcept = concepts.find(c => c.is_selected);
    const funnelSteps = funnel?.steps || [];
    const scriptInstanceId = script?.script_instance_id || null;
    
    // ==========================================================================
    // Step Completion Checks — MA-PPB-STATE-058: Unified via checkStepCompletion()
    // ==========================================================================
    const stepCompletion = {};
    for (const id of ["context", "foundation", "audience", "offer", "messaging", "creative", "funnel", "script", "landing_page"]) {
      stepCompletion[id] = checkStepCompletion(id, draft, lpApiContext);
    }
    
    // Check step completions - MA-UI-070: Added impact and severity
    if (!stepCompletion.context) blockingIssues.push({ step: "context", message: "Context step is incomplete", impact: ISSUE_IMPACTS.context_incomplete, severity: SEVERITY.CRITICAL, type: "completion" });
    if (!stepCompletion.foundation) blockingIssues.push({ step: "foundation", message: "Foundation step is incomplete", impact: ISSUE_IMPACTS.foundation_incomplete, severity: SEVERITY.CRITICAL, type: "completion" });
    if (!stepCompletion.audience) blockingIssues.push({ step: "audience", message: "Audience step is incomplete", impact: ISSUE_IMPACTS.audience_incomplete, severity: SEVERITY.CRITICAL, type: "completion" });
    if (!stepCompletion.offer) blockingIssues.push({ step: "offer", message: "Offer step is incomplete", impact: ISSUE_IMPACTS.offer_incomplete, severity: SEVERITY.CRITICAL, type: "completion" });
    if (!stepCompletion.messaging) blockingIssues.push({ step: "messaging", message: "Messaging step is incomplete", impact: ISSUE_IMPACTS.messaging_incomplete, severity: SEVERITY.CRITICAL, type: "completion" });
    if (!stepCompletion.creative) blockingIssues.push({ step: "creative", message: "No creative concept selected", impact: ISSUE_IMPACTS.creative_incomplete, severity: SEVERITY.CRITICAL, type: "completion" });
    if (!stepCompletion.funnel) blockingIssues.push({ step: "funnel", message: "Funnel step is incomplete", impact: ISSUE_IMPACTS.funnel_incomplete, severity: SEVERITY.CRITICAL, type: "completion" });
    if (!stepCompletion.script) blockingIssues.push({ step: "script", message: "Script step is incomplete", impact: ISSUE_IMPACTS.script_incomplete, severity: SEVERITY.CRITICAL, type: "completion" });
    
    // MA-PPB-PUBLISH-064 P0.4: Booking publish-readiness check
    // Check both PPB config (pre-gen) and funnel instance config (post-gen patched state)
    const hasBookingCalendar = funnel.config?.booking_calendar || funnel.booking_calendar_patched;
    if (funnel.config?.booking_enabled && !hasBookingCalendar) {
      blockingIssues.push({ step: "funnel", message: "Booking is enabled but no calendar is selected — required for publish", severity: SEVERITY.CRITICAL, type: "publish_readiness" });
    }
    
    // ==========================================================================
    // Alignment Checks
    // ==========================================================================
    
    // A. Creative ↔ Funnel Alignment
    let creativeFunnelAligned = true;
    if (selectedConcept && funnel.funnel_instance_id) {
      // Check funnel type matches hook_type
      const hookType = selectedConcept.hook_type || selectedConcept.hook_structure || "";
      const expectedFunnelType = {
        "question": "diagnostic",
        "warning": "risk_reduction",
        "contrarian": "belief_break",
        "scenario": "transformation"
      }[hookType] || "diagnostic";
      
      if (funnel.funnel_type && funnel.funnel_type !== expectedFunnelType) {
        warnings.push({ step: "funnel", message: `Funnel type (${funnel.funnel_type}) may not match hook type (${hookType})`, impact: ISSUE_IMPACTS.funnel_type_mismatch, severity: SEVERITY.HIGH, type: "alignment" });
      }
      
      // Check mechanism exists
      if (!funnel.mechanism) {
        blockingIssues.push({ step: "funnel", message: "Funnel mechanism not defined", impact: ISSUE_IMPACTS.mechanism_not_defined, severity: SEVERITY.CRITICAL, type: "mechanism" });
        creativeFunnelAligned = false;
      }
      
      strengths.push("Creative concept selected and funnel generated");
    } else if (!selectedConcept) {
      creativeFunnelAligned = false;
    }
    
    // B. Funnel ↔ Script Alignment
    let funnelScriptAligned = true;
    if (funnel.funnel_instance_id && scriptInstanceId) {
      strengths.push("Script generated from funnel & messaging context via ScriptAssist");
    } else if (!scriptInstanceId) {
      funnelScriptAligned = false;
    }
    
    // C. CTA Alignment
    let ctaAligned = true;
    const executionIntent = funnel.execution_intent || selectedConcept?.execution_intent || "";
    const funnelCtaStep = funnelSteps.find(s => s.type === "cta");
    
    if (executionIntent && funnelCtaStep?.cta_label) {
      const ctaLower = funnelCtaStep.cta_label.toLowerCase();
      const intentLower = executionIntent.toLowerCase();
      const isGenericCta = ctaLower.includes("book") || ctaLower.includes("call") || ctaLower.includes("get started");
      
      if (isGenericCta && (intentLower.includes("reveal") || intentLower.includes("prevent") || intentLower.includes("expose"))) {
        warnings.push({ step: "funnel", message: "Funnel CTA may not align with execution intent", impact: ISSUE_IMPACTS.funnel_cta_misaligned, severity: SEVERITY.HIGH, type: "cta" });
        ctaAligned = false;
      }
    }
    
    if (ctaAligned && executionIntent) {
      strengths.push("CTA aligned with execution intent");
    }
    
    // NEW: Landing Page alignment check (MA-ARCH-177)
    // MA-LP-PROD-029 P2-5: Read from LP API context (single source of truth)
    // MA-PPB-LP-055: Support sections[] format with flat fallback
    const lpSnapshot = lpApiContext?.current_version?.snapshot_json || {};
    const lpSections = lpSnapshot.sections || [];
    const lpSec = (type) => lpSections.find(s => s.type === type)?.data || {};
    const lpHeroData = lpSec("hero") || lpSnapshot.hero || {};
    const lpHasContent = !!(lpHeroData.headline?.trim());
    let landingPageAligned = false;
    
    if (lpHasContent) {
      landingPageAligned = true;
      strengths.push("Landing page generated from strategy");
      
      // Check hero CTA alignment with funnel CTA
      const funnelCtaLabel = funnelSteps.find(s => s.type === "cta")?.cta_label || "";
      const lpHeroCta = lpHeroData.cta_label || "";
      if (funnelCtaLabel && lpHeroCta && funnelCtaLabel.toLowerCase() !== lpHeroCta.toLowerCase()) {
        warnings.push({ step: "landing_page", message: "Landing page hero CTA differs from funnel CTA", impact: 3, severity: SEVERITY.WARNING, type: "cta" });
      }
    } else {
      warnings.push({ step: "landing_page", message: "Landing page not yet generated", impact: 5, severity: SEVERITY.HIGH, type: "alignment" });
    }
    
    // D. Mechanism Integrity
    let mechanismIntegrity = true;
    const creativeMechanism = selectedConcept?.mechanism || "";
    const messagingMechanism = messaging?.unique_mechanism || "";
    const funnelMechanism = funnel.mechanism || "";
    const scriptMechanism = script.mechanism || "";
    
    if (creativeMechanism && messagingMechanism) {
      // Check mechanism consistency across layers
      const mechValues = [creativeMechanism, messagingMechanism, funnelMechanism, scriptMechanism].filter(Boolean);
      const uniqueMechs = new Set(mechValues.map(m => m.toLowerCase().split(" ")[0]));
      
      if (uniqueMechs.size > 1) {
        warnings.push({ step: "messaging", message: "Mechanism may vary across layers", impact: ISSUE_IMPACTS.mechanism_inconsistent, severity: SEVERITY.WARNING, type: "mechanism" });
      } else if (mechValues.length >= 3) {
        strengths.push("Mechanism consistent across all layers");
        mechanismIntegrity = true;
      }
    }
    
    // E. Hook Alignment — REMOVED (MA-PPB-REVIEW-059)
    // Strict string equality between messaging.primary_hook and selectedConcept.hook
    // was structurally flawed: the Creative AI is explicitly instructed to generate
    // different phrasing from the messaging hook, guaranteeing false positives.
    // Meaningful alignment is already covered by Mechanism Integrity (D),
    // Creative → Funnel (A), and CTA Alignment (C).
    
    // ==========================================================================
    // Calculate Overall Status - MA-UI-070: Updated logic
    // ==========================================================================
    const completedSteps = Object.values(stepCompletion).filter(Boolean).length;
    const totalSteps = Object.keys(stepCompletion).length;
    
    // MA-UI-070: Only CRITICAL issues block approval
    const criticalIssues = blockingIssues.filter(i => i.severity === SEVERITY.CRITICAL);
    const highIssues = [...blockingIssues.filter(i => i.severity === SEVERITY.HIGH), ...warnings.filter(w => w.severity === SEVERITY.HIGH)];
    const warningIssues = warnings.filter(w => w.severity === SEVERITY.WARNING);
    
    let overallStatus = REVIEW_STATUS.HEALTHY;
    if (criticalIssues.length > 0) {
      overallStatus = REVIEW_STATUS.INCOMPLETE;
    } else if (highIssues.length > 0 || warningIssues.length > 0) {
      overallStatus = REVIEW_STATUS.NEEDS_ATTENTION;
    }
    
    // MA-UI-070: Alignment checks with severity mapping
    const alignmentChecks = {
      creativeFunnel: { aligned: creativeFunnelAligned, severity: SEVERITY.HIGH },
      funnelScript: { aligned: funnelScriptAligned, severity: SEVERITY.HIGH },
      ctaAlignment: { aligned: ctaAligned, severity: SEVERITY.CRITICAL },
      mechanismIntegrity: { aligned: mechanismIntegrity, severity: SEVERITY.CRITICAL }
    };
    
    // MA-UI-070: Ready for execution flag
    const readyForExecution = criticalIssues.length === 0;
    
    return {
      overallStatus,
      readyForExecution,
      criticalIssues,
      highIssues,
      warningIssues,
      blockingIssues,
      warnings,
      strengths,
      stepCompletion,
      alignmentChecks
    };
  }, [draft, lpApiContext, SEVERITY, ISSUE_IMPACTS]);

  const reviewStatus = getReviewStatus();
  const { overallStatus, readyForExecution, criticalIssues, highIssues, warningIssues, blockingIssues, warnings, strengths, stepCompletion, alignmentChecks } = reviewStatus;

  // Get summaries for display
  const foundation = draft?.builder_data?.foundation || {};
  const audience = draft?.builder_data?.audience || {};
  const offer = draft?.builder_data?.offer || {};
  const messaging = draft?.builder_data?.messaging || {};
  const creative = draft?.builder_data?.creative || {};
  const funnel = draft?.builder_data?.funnel || {};
  const script = draft?.builder_data?.script || {};
  const selectedConcept = creative?.concepts?.find(c => c.is_selected);

  // Navigate to failing step
  const goToStep = (stepId) => {
    // This would update the activeStep in the parent component
    // For now, we'll use a custom event or callback
    const event = new CustomEvent('navigateToStep', { detail: { step: stepId } });
    window.dispatchEvent(event);
  };

  // Approve profit path
  const handleApprove = () => {
    onFieldChange("builder_data.review.status", "approved");
    onFieldChange("builder_data.review.approved_at", new Date().toISOString());
    onFieldChange("status", "active");
  };

  // MA-UI-070: Get status badge styles - Updated labels
  const getStatusBadge = () => {
    if (overallStatus === REVIEW_STATUS.HEALTHY) {
      return { icon: "", text: "Healthy", className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" };
    }
    if (overallStatus === REVIEW_STATUS.NEEDS_ATTENTION) {
      return { icon: "⚠", text: "Needs Attention", className: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" };
    }
    return { icon: "❌", text: "Not Ready", className: "bg-red-500/20 text-red-300 border-red-500/30" };
  };

  // MA-UI-070: Get severity badge styles
  const getSeverityBadge = (severity) => {
    if (severity === "critical") {
      return { text: "Critical", className: "bg-red-500/20 text-red-400 border-red-500/30" };
    }
    if (severity === "high") {
      return { text: "High", className: "bg-orange-500/20 text-orange-400 border-orange-500/30" };
    }
    return { text: "Warning", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" };
  };

  const statusBadge = getStatusBadge();

  return (
    <div className="h-full flex flex-col">
      <StepHeader stepId="review" draft={draft} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} onApprove={handleApprove} approveDisabled={criticalIssues.length > 0 || !isActivated} />

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* =================================================================
              A. Status Header - MA-UI-070: System Readiness (no percentage)
          ================================================================= */}
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Badge className={cn("h-7 text-sm px-3 border", statusBadge.className)}>
                  {statusBadge.icon ? `${statusBadge.icon} ` : ""}{statusBadge.text}
                </Badge>
              </div>
              {/* MA-UI-070: Ready for Execution indicator */}
              <div className="text-right">
                <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Ready for Execution</span>
                <span className={cn(
                  "text-sm font-medium",
                  readyForExecution ? "text-emerald-400" : "text-red-400"
                )}>
                  {readyForExecution ? "Yes" : "No"}
                </span>
              </div>
            </div>
            
            {/* MA-UI-070: Visual status bar (not percentage-based) */}
            <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
              <div 
                className={cn(
                  "h-full transition-all duration-500",
                  overallStatus === REVIEW_STATUS.HEALTHY ? "bg-emerald-500 w-full" :
                  overallStatus === REVIEW_STATUS.NEEDS_ATTENTION ? "bg-yellow-500 w-3/4" : "bg-red-500 w-1/4"
                )}
              />
            </div>
            
            {/* Status Message — only show for non-healthy states */}
            {overallStatus !== REVIEW_STATUS.HEALTHY && (
            <p className={cn(
              "text-xs mt-2",
              overallStatus === REVIEW_STATUS.NEEDS_ATTENTION ? "text-yellow-400" : "text-red-400"
            )}>
              {overallStatus === REVIEW_STATUS.NEEDS_ATTENTION
                ? `${highIssues.length + warningIssues.length} issue(s) found - review before approval`
                : `${criticalIssues.length} critical issue(s) must be resolved`}
            </p>
            )}
          </div>

          {/* =================================================================
              B. System Health Cards - MA-UI-070: With Severity Tiers
          ================================================================= */}
          <div>
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
              System Health
            </h4>
            <div className="space-y-2">
              {[
                { key: "mechanismIntegrity", label: "Mechanism Integrity", icon: Layers, color: "text-indigo-400", passText: "Mechanism consistent across layers", failText: "Mechanism may vary across steps" },
                { key: "ctaAlignment", label: "CTA Alignment", icon: MousePointerClick, color: "text-indigo-400", passText: "CTA matches execution intent", failText: "CTA may drift from intent" },
                { key: "creativeFunnel", label: "Creative → Funnel", icon: Sparkles, color: "text-indigo-400", passText: "Funnel derived from creative concept", failText: "Creative not properly linked to funnel" },
                { key: "funnelScript", label: "Funnel → Script", icon: FileText, color: "text-indigo-400", passText: "Script maps to funnel structure", failText: "Script not properly aligned with funnel" },
              ].map(({ key, label, icon: Icon, color, passText, failText }) => {
                const check = alignmentChecks[key];
                const passing = check.aligned;
                return (
                  <div key={key} className={cn(
                    "p-2 rounded-lg border flex items-start gap-2",
                    passing ? "bg-slate-800/30 border-slate-700/30" : check.severity === "critical" ? "bg-red-500/5 border-red-500/20" : "bg-orange-500/5 border-orange-500/20"
                  )} data-testid={`health-${key}`}>
                    <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", passing ? color : "text-red-400")} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-300">{label}</p>
                      <p className="text-[11px] text-slate-400">
                        {passing ? passText : failText}
                      </p>
                    </div>
                    {passing
                      ? <Check className="h-3 w-3 text-indigo-400 shrink-0 mt-0.5" />
                      : <Badge className={cn("h-4 text-[8px] border shrink-0", getSeverityBadge(check.severity).className)}>
                          {getSeverityBadge(check.severity).text}
                        </Badge>
                    }
                  </div>
                );
              })}
            </div>
          </div>

          {/* =================================================================
              C. Full Profit Path Summary
          ================================================================= */}
          <div>
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
              Profit Path Summary
            </h4>
            <div className="space-y-2">
              {[
                { id: "context", label: "Context", icon: Building2, color: "text-teal-400",
                  text: draft?.builder_data?.context?.workspace?.trim() ? `${draft.builder_data.context.workspace} — ${draft.builder_data.context.campaign_name || ""}` : null },
                { id: "foundation", label: "Foundation", icon: Compass, color: "text-blue-400",
                  text: getFieldValue(foundation?.strategic_objective, "").trim() || null },
                { id: "audience", label: "Audience", icon: Users, color: "text-emerald-400",
                  text: audience?.primary_audience?.trim() || null },
                { id: "offer", label: "Offer", icon: Gift, color: "text-amber-400",
                  text: offer?.offer_name?.trim() || null },
                { id: "messaging", label: "Messaging", icon: MessageSquare, color: "text-indigo-400",
                  text: messaging?.angle?.trim() ? `${messaging.angle} — ${messaging.unique_mechanism || ""}` : null },
                { id: "creative", label: "Creative", icon: Image, color: "text-pink-400",
                  text: selectedConcept?.hook?.trim() ? `"${selectedConcept.hook}"` : null },
                { id: "funnel", label: "Funnel", icon: Filter, color: "text-amber-400",
                  text: stepCompletion.funnel
                    ? `${funnel?.steps?.length || ""}${funnel?.steps?.length ? "-step " : ""}Funnel generated`
                    : null },
                { id: "script", label: "Script", icon: FileText, color: "text-purple-400",
                  text: stepCompletion.script ? "Script generated" : null },
                { id: "landing_page", label: "Landing Page", icon: Globe, color: "text-indigo-400",
                  text: stepCompletion.landing_page
                    ? (() => {
                        const secs = lpApiContext?.current_version?.snapshot_json?.sections || [];
                        const h = secs.find(s => s.type === "hero")?.data || {};
                        return h.headline?.trim() || "Landing page generated";
                      })()
                    : null },
              ].map(({ id, label, icon: Icon, color, text }) => {
                const complete = stepCompletion[id];
                return (
                  <div key={id} className={cn(
                    "p-2 rounded-lg border flex items-start gap-2",
                    complete ? "bg-slate-800/30 border-slate-700/30" : "bg-red-500/5 border-red-500/20"
                  )} data-testid={`summary-row-${id}`}>
                    <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", complete ? color : "text-red-400")} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-300">{label}</p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {text || (complete ? "Configured" : "Not defined")}
                      </p>
                    </div>
                    {complete
                      ? <Check className="h-3 w-3 text-indigo-400 shrink-0" />
                      : <span className="text-[10px] text-red-400 shrink-0">Missing</span>
                    }
                  </div>
                );
              })}
            </div>
          </div>

          {/* =================================================================
              D. Issues Panel - MA-UI-070: With Severity + Impact
          ================================================================= */}
          {(criticalIssues.length > 0 || highIssues.length > 0 || warningIssues.length > 0) && (
            <div>
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Issues & Warnings
              </h4>
              
              {/* Critical Issues (blocks approval) */}
              {criticalIssues.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-red-400 uppercase font-medium mb-2">
                    Critical Issues ({criticalIssues.length}) — Blocks Approval
                  </p>
                  <div className="space-y-1.5">
                    {criticalIssues.map((issue, idx) => (
                      <div 
                        key={idx}
                        className="p-2 rounded-lg bg-red-500/10 border border-red-500/20"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge className="h-4 text-[8px] bg-red-500/20 text-red-400 border-red-500/30 border">
                              CRITICAL
                            </Badge>
                            <span className="text-xs text-red-300">{issue.message}</span>
                          </div>
                          <button
                            onClick={() => goToStep(issue.step)}
                            className="text-[10px] text-red-400 hover:text-red-300 underline flex-shrink-0"
                            data-testid={`go-to-${issue.step}`}
                          >
                            Go to {issue.step}
                          </button>
                        </div>
                        {/* MA-UI-070: Impact statement */}
                        {issue.impact && (
                          <p className="text-[10px] text-red-400/70 ml-4 mt-1">
                            → {issue.impact}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* High Priority Issues */}
              {highIssues.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-orange-400 uppercase font-medium mb-2">
                    High Priority ({highIssues.length})
                  </p>
                  <div className="space-y-1.5">
                    {highIssues.map((issue, idx) => (
                      <div 
                        key={idx}
                        className="p-2 rounded-lg bg-orange-500/10 border border-orange-500/20"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge className="h-4 text-[8px] bg-orange-500/20 text-orange-400 border-orange-500/30 border">
                              HIGH
                            </Badge>
                            <span className="text-xs text-orange-300">{issue.message}</span>
                          </div>
                          <button
                            onClick={() => goToStep(issue.step)}
                            className="text-[10px] text-orange-400 hover:text-orange-300 underline flex-shrink-0"
                          >
                            Review {issue.step}
                          </button>
                        </div>
                        {/* MA-UI-070: Impact statement */}
                        {issue.impact && (
                          <p className="text-[10px] text-orange-400/70 ml-4 mt-1">
                            → {issue.impact}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Warnings (optimization) */}
              {warningIssues.length > 0 && (
                <div>
                  <p className="text-[10px] text-yellow-400 uppercase font-medium mb-2">
                    Optimization Opportunities ({warningIssues.length})
                  </p>
                  <div className="space-y-1.5">
                    {warningIssues.map((warning, idx) => (
                      <div 
                        key={idx}
                        className="p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge className="h-4 text-[8px] bg-yellow-500/20 text-yellow-400 border-yellow-500/30 border">
                              WARNING
                            </Badge>
                            <span className="text-xs text-yellow-300">{warning.message}</span>
                          </div>
                          <button
                            onClick={() => goToStep(warning.step)}
                            className="text-[10px] text-yellow-400 hover:text-yellow-300 underline flex-shrink-0"
                          >
                            Review {warning.step}
                          </button>
                        </div>
                        {/* MA-UI-070: Impact statement */}
                        {warning.impact && (
                          <p className="text-[10px] text-yellow-400/70 ml-4 mt-1">
                            → {warning.impact}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Strengths (when healthy or near-healthy) */}
          {strengths.length > 0 && criticalIssues.length === 0 && (
            <div>
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Strengths
              </h4>
              <div className="space-y-1">
                {strengths.map((strength, idx) => (
                  <div 
                    key={idx}
                    className="p-2 rounded-lg bg-slate-800/30 border border-slate-700/30 flex items-center gap-2"
                  >
                    <Zap className="h-3 w-3 text-emerald-400 shrink-0" />
                    <span className="text-xs text-slate-300">{strength}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Suggestions section removed (MA-PPB-REVIEW-059) — self-contained, no downstream dependencies */}

          {/* =================================================================
              D2. Publish Readiness — MA-PPB-PUBLISH-064 P1
          ================================================================= */}
          <div>
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
              Publish Readiness
            </h4>
            
            {publishReadiness.loading ? (
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                <span className="text-xs text-slate-400">Evaluating publish readiness...</span>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Funnel Readiness */}
                {publishReadiness.funnel ? (
                  <div className="p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/30" data-testid="funnel-publish-readiness">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-300">Funnel</span>
                      <Badge className={cn(
                        "h-4 text-[8px] border",
                        publishReadiness.funnel.publish_state === "PUBLISHED" ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30" :
                        publishReadiness.funnel.publish_state === "READY" ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30" :
                        "bg-slate-500/20 text-slate-400 border-slate-500/30"
                      )}>
                        {publishReadiness.funnel.publish_state || "DRAFT"}
                      </Badge>
                    </div>
                    {publishReadiness.funnel.issues?.map((issue, i) => (
                      <p key={i} className="text-[10px] text-red-400 mt-1">{issue.message}</p>
                    ))}
                    {publishReadiness.funnel.warnings?.map((w, i) => (
                      <p key={i} className="text-[10px] text-amber-400 mt-1">{w.message}</p>
                    ))}
                    {publishReadiness.funnel.publish_state === "PUBLISHED" && (
                      <p className="text-[10px] text-indigo-400 mt-1">Live</p>
                    )}
                    {publishReadiness.funnel.publish_state === "READY" && publishReadiness.funnel.issues?.length === 0 && (
                      <p className="text-[10px] text-indigo-400 mt-1">Ready to publish</p>
                    )}
                  </div>
                ) : (
                  <div className="p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/30">
                    <span className="text-xs text-slate-500">Funnel not generated yet</span>
                  </div>
                )}

                {/* LP Readiness */}
                {publishReadiness.lp ? (
                  <div className="p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/30" data-testid="lp-publish-readiness">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-300">Landing Page</span>
                      <Badge className={cn(
                        "h-4 text-[8px] border",
                        publishReadiness.lp.publish_state === "PUBLISHED" ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30" :
                        publishReadiness.lp.publish_state === "READY" ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30" :
                        "bg-slate-500/20 text-slate-400 border-slate-500/30"
                      )}>
                        {publishReadiness.lp.publish_state || "DRAFT"}
                      </Badge>
                    </div>
                    {publishReadiness.lp.issues?.map((issue, i) => (
                      <p key={i} className="text-[10px] text-red-400 mt-1">{issue.message}</p>
                    ))}
                    {publishReadiness.lp.warnings?.map((w, i) => (
                      <p key={i} className="text-[10px] text-amber-400 mt-1">{w.message}</p>
                    ))}
                    {publishReadiness.lp.publish_state === "PUBLISHED" && (
                      <p className="text-[10px] text-indigo-400 mt-1">Live</p>
                    )}
                    {publishReadiness.lp.publish_state === "READY" && publishReadiness.lp.issues?.length === 0 && (
                      <p className="text-[10px] text-indigo-400 mt-1">Ready to publish</p>
                    )}
                  </div>
                ) : (
                  <div className="p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/30">
                    <span className="text-xs text-slate-500">Landing page not generated yet</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* =================================================================
              E. Publish Orchestration — MA-PPB-PUBLISH-064 P2
          ================================================================= */}
          <div className="pt-4 border-t border-slate-700/30 space-y-3">
            {/* Activation gate */}
            {!isActivated && criticalIssues.length === 0 && (
              <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 mb-2" data-testid="activation-gate-message">
                <p className="text-xs text-amber-300 font-medium mb-1">System Activation Required</p>
                <p className="text-[10px] text-amber-300/70">Profit Path approval is disabled until all required system capabilities are configured.</p>
                <button
                  onClick={() => navigate("/activation")}
                  className="text-[10px] text-amber-400 hover:text-amber-300 underline mt-1"
                  data-testid="activation-gate-link"
                >
                  Go to System Activation
                </button>
              </div>
            )}
            
            {criticalIssues.length > 0 && criticalIssues[0] && (
              <Button
                onClick={() => goToStep(criticalIssues[0].step)}
                variant="outline"
                className="w-full h-9 text-xs border-slate-700/50 text-slate-400 hover:text-slate-300"
                data-testid="return-to-step-btn"
              >
                Return to {criticalIssues[0].step.charAt(0).toUpperCase() + criticalIssues[0].step.slice(1)} Step
              </Button>
            )}
            
            {/* Publish Controls — MA-PPB-PUBLISH-064 P2 */}
            <PublishControls
              draft={draft}
              publishReadiness={publishReadiness}
              onPublishComplete={() => {
                // Re-evaluate readiness after publish
                const funnelId = draft?.builder_data?.funnel?.funnel_instance_id;
                const lpVersionId = lpApiContext?.current_version?.id;
                const reEvaluate = async () => {
                  setPublishReadiness(prev => ({ ...prev, loading: true }));
                  const results = { funnel: null, lp: null, loading: false };
                  try {
                    if (funnelId) {
                      const r = await api.get(`/funnels/${funnelId}/publish-readiness`);
                      results.funnel = r.data;
                    }
                  } catch {}
                  try {
                    if (lpVersionId) {
                      const r = await api.get(`/landing-pages/versions/${lpVersionId}/publish-readiness`);
                      results.lp = r.data;
                    }
                  } catch {}
                  setPublishReadiness(results);
                };
                reEvaluate();
              }}
              lpApiContext={lpApiContext}
            />
            
            <Button
              disabled
              variant="outline"
              className="w-full h-9 text-xs border-slate-700/30 text-slate-600 cursor-not-allowed"
            >
              Export (Coming Soon)
            </Button>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

// =============================================================================
// Center Panel: Placeholder Step (for non-implemented steps)
// =============================================================================
function PlaceholderStepPanel({ step }) {
  const stepConfig = BUILDER_STEPS.find((s) => s.id === step);
  const Icon = stepConfig?.icon || Layers;

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 border-b border-white/[0.06] flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={cn("h-4 w-4", stepConfig?.color || "text-indigo-400")} />
            <h2 className="text-sm font-medium text-slate-200">
              {stepConfig?.label || "Step"}
            </h2>
          </div>
          <Badge className="h-5 text-[10px] bg-slate-500/20 text-slate-400 border-0 px-2">
            Coming Soon
          </Badge>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="h-full flex flex-col items-center justify-center text-center">
          <div
            className={cn(
              "w-16 h-16 rounded-2xl flex items-center justify-center mb-4",
              stepConfig?.bgColor || "bg-slate-800"
            )}
          >
            <Icon className={cn("h-8 w-8", stepConfig?.color || "text-slate-500")} />
          </div>
          <h3 className="text-lg font-medium text-slate-300 mb-2">
            {stepConfig?.label} Configuration
          </h3>
          <p className="text-sm text-slate-500 max-w-md mb-4">
            {stepConfig?.description}
          </p>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Sparkles className="h-3 w-3" />
            <span>Phase 2B+: Coming Soon</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Shared Step Header Component
// =============================================================================
function StepHeader({ stepId, draft, onNext, isLastStep, isStepComplete, description, onApprove, approveDisabled }) {
  const stepConfig = BUILDER_STEPS.find((s) => s.id === stepId);
  const Icon = stepConfig?.icon || Building2;
  const status = draft?.status || "draft";
  const isApproved = !!draft?.builder_data?.review?.approved_at;
  const displayStatus = isApproved ? "Active" : status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <div className={cn("px-4 py-3 border-b border-white/[0.06] flex-shrink-0", !description && "pb-4")}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-4 w-4", stepConfig?.color || "text-slate-400")} />
          <h2 className="text-sm font-medium text-slate-200">{stepConfig?.label}</h2>
        </div>
        <div className="flex items-center gap-2 min-h-[24px]">
          <Badge className={cn(
            "h-5 text-[10px] border-0 px-2 transition-all duration-300",
            isApproved
              ? "bg-emerald-500/20 text-emerald-300"
              : "bg-amber-500/20 text-amber-300"
          )}>
            {displayStatus}
          </Badge>
          {!isLastStep && onNext && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onNext}
              disabled={!isStepComplete}
              className={cn(
                "h-6 px-2.5 text-[11px] gap-1 transition-colors",
                isStepComplete
                  ? "text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/15"
                  : "text-slate-600 cursor-not-allowed"
              )}
              data-testid={`step-next-btn-${stepId}`}
            >
              Next
              <ChevronRight className="h-3 w-3" />
            </Button>
          )}
          {onApprove && !isApproved && (
            <Button
              size="sm"
              onClick={onApprove}
              disabled={approveDisabled}
              className={cn(
                "h-6 px-3 text-[11px] font-medium rounded-md",
                "transition-all duration-150 ease-out",
                !approveDisabled
                  ? cn(
                      "bg-gradient-to-b from-indigo-500 to-indigo-600",
                      "shadow-sm shadow-indigo-500/25",
                      "text-white border-0",
                      "hover:from-indigo-400 hover:to-indigo-500",
                      "hover:shadow-md hover:shadow-indigo-500/30",
                      "hover:-translate-y-px",
                      "active:translate-y-0 active:shadow-sm"
                    )
                  : "bg-slate-800/50 text-slate-500 border border-slate-700/30 cursor-not-allowed"
              )}
              data-testid="approve-profit-path-btn"
            >
              Approve Profit Path
            </Button>
          )}
        </div>
      </div>
      {description && (
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      )}
    </div>
  );
}

// =============================================================================
// Center Panel: Work Area Router
// =============================================================================
function BuilderWorkArea({ step, draft, onFieldChange, onNext, getStepCompletion, resolvedLpTokens, onLPContextChanged, lpApiContext, tenantBrandConfig }) {
  const stepIndex = BUILDER_STEPS.findIndex((s) => s.id === step);
  const isLastStep = stepIndex === BUILDER_STEPS.length - 1;
  const isStepComplete = getStepCompletion ? getStepCompletion(step) : false;

  if (step === "context") {
    return <ContextStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />;
  }
  if (step === "foundation") {
    return <FoundationStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} tenantBrandConfig={tenantBrandConfig} />;
  }
  if (step === "audience") {
    return <AudienceStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />;
  }
  if (step === "offer") {
    return <OfferStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />;
  }
  if (step === "messaging") {
    return <MessagingStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />;
  }
  if (step === "creative") {
    return <CreativeStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />;
  }
  if (step === "funnel") {
    return <FunnelStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} resolvedLpTokens={resolvedLpTokens} tenantBrandConfig={tenantBrandConfig} />;
  }
  if (step === "script") {
    return <ScriptStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} />;
  }
  if (step === "landing_page") {
    return <LandingPageStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} onLPContextChanged={onLPContextChanged} resolvedLpTokens={resolvedLpTokens} />;
  }
  if (step === "review") {
    return <ReviewStepPanel draft={draft} onFieldChange={onFieldChange} onNext={onNext} isLastStep={isLastStep} isStepComplete={isStepComplete} lpApiContext={lpApiContext} />;
  }
  return <PlaceholderStepPanel step={step} />;
}

// =============================================================================
// Right Rail: Context Panel (MA-UI-061-A / MA-UI-062 - Synced with Draft)
// =============================================================================
function BuilderContextPanel({ draft, activeStep, lpApiContext }) {
  // MA-UI-075: Use centralized getFieldValue for all biased field access

  // MA-PPB-STATE-058: Single source of truth for step completion.
  // Delegates to checkStepCompletion() so sidebar, context panel, and work area all agree.
  const getCompletion = useCallback(() => {
    const stepIds = ["context", "foundation", "audience", "offer", "messaging", "creative", "funnel", "script", "landing_page", "review"];
    const results = {};
    for (const id of stepIds) {
      results[id] = checkStepCompletion(id, draft, lpApiContext);
    }
    return results;
  }, [draft, lpApiContext]);

  // Generate audience summary
  const getAudienceSummary = useCallback(() => {
    const audience = draft?.builder_data?.audience;
    if (!audience?.primary_audience?.trim()) return null;
    
    const parts = [];
    if (audience.primary_audience?.trim()) {
      parts.push(audience.primary_audience.trim());
    }
    if (audience.core_problem?.trim()) {
      parts.push(`facing: ${audience.core_problem.trim()}`);
    }
    return parts.join(" — ");
  }, [draft]);

  // Generate offer summary (MA-UI-063)
  const getOfferSummary = useCallback(() => {
    const offer = draft?.builder_data?.offer;
    if (!offer?.offer_name?.trim()) return null;
    
    const parts = [];
    if (offer.offer_name?.trim()) {
      parts.push(offer.offer_name.trim());
    }
    if (offer.core_promise?.trim()) {
      parts.push(offer.core_promise.trim());
    }
    if (offer.primary_cta?.trim()) {
      parts.push(`CTA: ${offer.primary_cta.trim()}`);
    }
    return parts.join(" — ");
  }, [draft]);

  // Generate messaging summary (MA-UI-064 / MA-UI-065-A / MA-UI-065-B)
  const getMessagingSummary = useCallback(() => {
    const messaging = draft?.builder_data?.messaging;
    if (!messaging?.angle?.trim()) return null;
    
    // Build main line: Angle — Mechanism — Enemy — Hook
    const mainParts = [];
    if (messaging.angle?.trim()) {
      mainParts.push(messaging.angle.trim());
    }
    if (messaging.unique_mechanism?.trim()) {
      mainParts.push(messaging.unique_mechanism.trim());
    }
    if (messaging.message_enemy?.trim()) {
      mainParts.push(messaging.message_enemy.trim());
    }
    if (messaging.primary_hook?.trim()) {
      mainParts.push(messaging.primary_hook.trim());
    }
    
    // Build summary with Tone on separate line
    let summary = mainParts.join(" — ");
    if (getFieldValue(messaging.tone, "").trim()) {
      const toneVal = getFieldValue(messaging.tone, "");
      const toneLabel = COMMUNICATION_TONES.find(t => t.value === toneVal)?.label || toneVal;
      summary += `\nTone: ${toneLabel}`;
    }
    return summary;
  }, [draft]);

  // Generate creative summary (MA-UI-066-E: Updated format for Funnel handoff)
  const getCreativeSummary = useCallback(() => {
    const concepts = draft?.builder_data?.creative?.concepts || [];
    const selectedConcept = concepts.find(c => c.is_selected);
    if (!selectedConcept) return null;
    
    const messaging = draft?.builder_data?.messaging;
    const toneLabel = COMMUNICATION_TONES.find(t => t.value === getFieldValue(messaging?.tone, ""))?.label || getFieldValue(messaging?.tone, "");
    
    return {
      angle: selectedConcept.angle || selectedConcept.angle_variant || "",
      hook: selectedConcept.hook || "",
      primaryDriver: selectedConcept.primary_driver || "",
      tone: toneLabel,
      executionIntent: selectedConcept.execution_intent || ""
    };
  }, [draft]);

  // Generate funnel summary (MA-UI-067-A: Updated with Type and Execution Intent)
  const getFunnelSummary = useCallback(() => {
    const funnelData = draft?.builder_data?.funnel;
    if (!funnelData?.funnel_type || !funnelData?.steps?.length) return null;
    
    const funnelTypeLabels = {
      diagnostic: "Diagnostic",
      risk_reduction: "Risk Reduction",
      belief_break: "Belief Break",
      transformation: "Transformation"
    };
    
    const concepts = draft?.builder_data?.creative?.concepts || [];
    const selectedConcept = concepts.find(c => c.is_selected);
    const hookType = selectedConcept?.hook_type || selectedConcept?.hook_structure || "";
    const emotionalStart = selectedConcept?.emotional_start || "";
    const emotionalEnd = selectedConcept?.emotional_end || "";
    
    // MA-UI-067-A: Get mechanism and execution_intent from funnel data (locked from generation)
    const mechanism = funnelData.mechanism || selectedConcept?.mechanism || "";
    const executionIntent = funnelData.execution_intent || selectedConcept?.execution_intent || "";
    
    return {
      hookType: hookType,
      funnelType: funnelTypeLabels[funnelData.funnel_type] || funnelData.funnel_type,
      stepCount: funnelData.steps.length,
      mechanism: mechanism,
      executionIntent: executionIntent,
      emotionalStart: emotionalStart,
      emotionalEnd: emotionalEnd
    };
  }, [draft]);

  // Generate script summary (MA-SCRIPT-INTEGRATION-001: Script linked asset)
  const getScriptSummary = useCallback(() => {
    const scriptData = draft?.builder_data?.script;
    if (!scriptData?.script_instance_id) return null;
    
    return {
      scriptInstanceId: scriptData.script_instance_id,
      generationSource: scriptData.generation_source || "script_assist",
    };
  }, [draft]);

  const completion = getCompletion();
  const completedCount = Object.values(completion).filter(Boolean).length;
  const audienceSummary = getAudienceSummary();
  const offerSummary = getOfferSummary();
  const messagingSummary = getMessagingSummary();
  const creativeSummary = getCreativeSummary();
  const funnelSummary = getFunnelSummary();
  const scriptSummary = getScriptSummary();

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header - EXACT: FunnelPropertiesPanel */}
      <div className="px-5 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-indigo-400" />
          <h2 className="text-sm font-semibold text-slate-100">Context</h2>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-5">
          {/* MA-UI-076-V3: Context Summary (at the very top of right rail) */}
          {(draft?.builder_data?.context?.workspace?.trim() || draft?.builder_data?.context?.campaign_name?.trim()) && (
            <div className="mb-6">
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Context Summary
              </h4>
              <div className="p-3 bg-teal-950/20 rounded-lg border border-teal-500/20 space-y-2">
                {draft.builder_data.context.workspace?.trim() && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">Workspace</span>
                    <span className="text-xs text-teal-300 text-right truncate max-w-[160px]">{draft.builder_data.context.workspace}</span>
                  </div>
                )}
                {draft.builder_data.context.vertical?.trim() && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">Vertical</span>
                    <span className="text-xs text-teal-300 text-right truncate max-w-[160px]">{draft.builder_data.context.vertical}</span>
                  </div>
                )}
                {draft.builder_data.context.channel?.trim() && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">Channel</span>
                    <span className="text-xs text-teal-300 text-right truncate max-w-[160px]">
                      {CHANNEL_OPTIONS.find(c => c.value === draft.builder_data.context.channel)?.label || draft.builder_data.context.channel}
                    </span>
                  </div>
                )}
                {draft.builder_data.context.platform?.trim() && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">Platform</span>
                    <span className="text-xs text-teal-300 text-right truncate max-w-[160px]">
                      {PLATFORM_OPTIONS.find(p => p.value === draft.builder_data.context.platform)?.label || draft.builder_data.context.platform}
                    </span>
                  </div>
                )}
                {draft.builder_data.context.campaign_name?.trim() && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">Campaign</span>
                    <span className="text-xs text-teal-300 text-right truncate max-w-[160px]">{draft.builder_data.context.campaign_name}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MA-UI-074: Entry Profile Section (Read-Only Display) */}
          {draft?.builder_data?.entry_profile && (
            <div className="mb-6">
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Entry Profile
              </h4>
              <div className="space-y-2 p-3 bg-indigo-950/20 rounded-lg border border-indigo-500/20">
                {/* Goal */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">Goal</span>
                  <Badge variant="outline" className="text-[10px] border-indigo-500/30 text-indigo-300 px-1.5 py-0">
                    {draft.builder_data.entry_profile.goal?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </Badge>
                </div>
                {/* Offer Type */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">Offer Type</span>
                  <Badge variant="outline" className="text-[10px] border-indigo-500/30 text-indigo-300 px-1.5 py-0">
                    {draft.builder_data.entry_profile.offer_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </Badge>
                </div>
                {/* Conversion Style */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">Conversion</span>
                  <Badge variant="outline" className="text-[10px] border-indigo-500/30 text-indigo-300 px-1.5 py-0">
                    {draft.builder_data.entry_profile.conversion_style?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </Badge>
                </div>
                {/* Traffic Temperature (if set) */}
                {draft.builder_data.entry_profile.traffic_temperature && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">Audience</span>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px] px-1.5 py-0",
                        draft.builder_data.entry_profile.traffic_temperature === "cold" && "border-blue-500/30 text-blue-300",
                        draft.builder_data.entry_profile.traffic_temperature === "warm" && "border-amber-500/30 text-amber-300",
                        draft.builder_data.entry_profile.traffic_temperature === "hot" && "border-red-500/30 text-red-300"
                      )}
                    >
                      {draft.builder_data.entry_profile.traffic_temperature?.replace(/\b\w/g, c => c.toUpperCase())}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Profit Path Info Section */}
          <div className="mb-6">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
              Profit Path
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500">Name</label>
                <div className="mt-1 text-sm text-slate-200 font-medium">
                  {draft?.name || "Untitled Profit Path"}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Status</label>
                <div className="mt-1">
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-xs border-slate-700",
                      draft?.status === "active" ? "text-emerald-400 border-emerald-700" : "text-slate-400"
                    )}
                  >
                    {(draft?.status || "draft").charAt(0).toUpperCase() + (draft?.status || "draft").slice(1)}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Vertical</label>
                <div className="mt-1 text-sm text-slate-300">
                  {draft?.builder_data?.context?.vertical || "Not set"}
                </div>
              </div>
            </div>
          </div>

          {/* Completion Section */}
          <div className="mb-6">
            <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
              Completion ({completedCount}/{BUILDER_STEPS.length})
            </h4>
            <div className="space-y-2">
              {BUILDER_STEPS.map((step) => {
                const isComplete = completion[step.id];
                const isActive = step.id === "context" || step.id === "foundation" || step.id === "audience" || step.id === "offer" || step.id === "messaging" || step.id === "creative" || step.id === "funnel" || step.id === "script" || step.id === "landing_page" || step.id === "review"; // All steps now active
                return (
                  <div key={step.id} className="flex items-center gap-2">
                    <div 
                      className={cn(
                        "w-2 h-2 rounded-full",
                        isComplete ? "bg-emerald-400" : isActive ? "bg-amber-400" : "bg-slate-700"
                      )} 
                    />
                    <span 
                      className={cn(
                        "text-xs",
                        isComplete ? "text-emerald-400" : isActive ? "text-slate-300" : "text-slate-600"
                      )}
                    >
                      {step.label}
                    </span>
                    {isComplete && <Check className="h-3 w-3 text-emerald-400" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Foundation Summary (when filled) - MA-UI-075: Use getFieldValue */}
          {getFieldValue(draft?.builder_data?.foundation?.strategic_objective, "").trim() && (
            <div className="mb-6">
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Foundation Summary
              </h4>
              <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                <p className="text-xs text-slate-400 line-clamp-3">
                  {getFieldValue(draft.builder_data.foundation.strategic_objective, "")}
                </p>
              </div>
            </div>
          )}

          {/* Audience Summary (when filled) - MA-UI-062: Truncate to 3 lines max */}
          {audienceSummary && (
            <div className="mb-6">
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Audience Summary
              </h4>
              <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                <p className="text-xs text-slate-400 line-clamp-3">
                  {audienceSummary}
                </p>
              </div>
            </div>
          )}

          {/* Offer Summary (when filled) - MA-UI-063: Truncate to 3 lines max */}
          {offerSummary && (
            <div className="mb-6">
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Offer Summary
              </h4>
              <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                <p className="text-xs text-slate-400 line-clamp-3">
                  {offerSummary}
                </p>
              </div>
            </div>
          )}

          {/* Messaging Summary (when filled) - MA-UI-064 / MA-UI-065-B: Truncate to 3 lines max */}
          {messagingSummary && (
            <div className="mb-6">
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Messaging Summary
              </h4>
              <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                <p className="text-xs text-slate-400 line-clamp-3 whitespace-pre-line">
                  {messagingSummary}
                </p>
              </div>
            </div>
          )}

          {/* Creative Summary (when a concept is selected) - MA-UI-066-E: Funnel handoff format */}
          {creativeSummary && (
            <div className="mb-6">
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Creative Summary
              </h4>
              <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30 space-y-2">
                {/* Line 1: [Angle] — [Hook] — [Primary Driver] */}
                <div>
                  <p className="text-xs text-slate-300 line-clamp-2">
                    {[
                      creativeSummary.angle,
                      creativeSummary.hook ? `"${creativeSummary.hook}"` : null,
                      creativeSummary.primaryDriver
                    ].filter(Boolean).join(" — ")}
                  </p>
                </div>
                {/* Line 2: Tone */}
                {creativeSummary.tone && (
                  <p className="text-xs text-slate-500">Tone: {creativeSummary.tone}</p>
                )}
              </div>
            </div>
          )}

          {/* Funnel Summary (MA-UI-067-A: Updated format with Type and Execution Intent first) */}
          {funnelSummary && (
            <div>
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Funnel Summary
              </h4>
              <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30 space-y-2">
                {/* Type */}
                <div>
                  <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Type</span>
                  <p className="text-xs text-amber-300 font-medium">
                    {funnelSummary.funnelType}
                  </p>
                </div>
                {/* Execution Intent */}
                {funnelSummary.executionIntent && (
                  <div>
                    <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Execution Intent</span>
                    <p className="text-xs text-indigo-300 line-clamp-2">{funnelSummary.executionIntent}</p>
                  </div>
                )}
                {/* Flow */}
                <div>
                  <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Flow</span>
                  <p className="text-xs text-slate-400">
                    Hook → Discovery → Mechanism → Insight → CTA
                  </p>
                </div>
                {/* Mechanism */}
                {funnelSummary.mechanism && (
                  <div>
                    <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Mechanism</span>
                    <p className="text-xs text-slate-300 line-clamp-1">{funnelSummary.mechanism}</p>
                  </div>
                )}
                {/* Emotional Path */}
                {funnelSummary.emotionalStart && funnelSummary.emotionalEnd && (
                  <div>
                    <span className="text-[10px] text-slate-500 uppercase block mb-0.5">Emotional Path</span>
                    <p className="text-xs text-purple-300">
                      {funnelSummary.emotionalStart} → {funnelSummary.emotionalEnd}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Script Summary (MA-UI-068: when script is generated) */}
          {scriptSummary && (
            <div className="mb-6">
              <h4 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-3">
                Script
              </h4>
              <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30 space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-indigo-400" />
                  <span className="text-xs text-slate-300">Linked Script Instance</span>
                  <Badge className="h-4 text-[8px] bg-indigo-500/20 text-indigo-400 border-0 ml-auto">
                    {scriptSummary.generationSource === "script_assist" ? "ScriptAssist" : "Generated"}
                  </Badge>
                </div>
                <button
                  onClick={() => window.open(`/app/scripts/${scriptSummary.scriptInstanceId}/edit`, "_blank")}
                  className="text-[10px] text-indigo-400 hover:text-indigo-300 hover:underline"
                >
                  Open in Script Builder
                </button>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// =============================================================================
// Main Builder Component
// =============================================================================
export default function ProfitPathBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // MA-RBAC-BATCH3-FRONTEND-PERMISSION-INFRA-PHASE4-115B: Component-level protection
  // Defense-in-depth: Block read-only users from rendering builder
  const { hasFullAccess, isLoading: permissionsLoading } = useModuleAccessLevel("profit_paths");
  
  const [activeStep, setActiveStep] = useState("context");
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const markDirty = useCallback((val) => {
    isDirtyRef.current = val;
    setIsDirty(val);
  }, []);

  // MA-UI-072: Read entry_profile from navigation state
  const entryProfile = location.state?.entry_profile || null;

  // MA-UI-061-A: Local draft state (single source of truth)
  const [localDraft, setLocalDraft] = useState(null);

  // Enable editor mode (hides large gradient header)
  useEditorMode(true);

  // Determine if this is a new profit path or editing existing
  const isNewPath = id === "new";
  
  // MA-RBAC-BATCH3-FRONTEND-PERMISSION-INFRA-PHASE4-115B: Component-level guard
  // Redirect if user doesn't have full access (defense-in-depth)
  useEffect(() => {
    if (!permissionsLoading && !hasFullAccess) {
      navigate(`/app/profit-paths${isNewPath ? '' : `/${id}`}`, { replace: true });
    }
  }, [permissionsLoading, hasFullAccess, navigate, isNewPath, id]);

  // ==========================================================================
  // Data Fetching: Load existing Profit Path
  // ==========================================================================
  const { data: profitPath, isLoading, error } = useQuery({
    queryKey: ["profit-path", id],
    queryFn: async () => {
      if (isNewPath) return null;
      const res = await api.get(`/profit-paths/${id}`);
      
      // MA-THEME-OVERRIDE-097: AUDIT LAYER 1 - API Response
      console.log("═══════════════════════════════════════════════════");
      console.log("🔍 RENDER AUDIT LAYER 1: API RESPONSE");
      console.log("builder_data.theme:", JSON.stringify(res.data?.builder_data?.theme, null, 2));
      console.log("builder_data.theme_source:", res.data?.builder_data?.theme_source);
      console.log("builder_data.foundation.theme_config:", JSON.stringify(res.data?.builder_data?.foundation?.theme_config, null, 2));
      console.log("═══════════════════════════════════════════════════");
      
      return res.data;
    },
    enabled: !isNewPath,
    retry: false,
  });

  // Initialize local draft from fetched data or empty state
  // MA-UI-072: Apply entry profile biasing for new paths
  useEffect(() => {
    if (isNewPath && !localDraft) {
      const emptyDraft = createEmptyDraft();
      // Apply entry profile biasing if available
      const biasedDraft = entryProfile 
        ? applyEntryProfileBias(emptyDraft, entryProfile)
        : emptyDraft;
      setLocalDraft(biasedDraft);
    } else if (profitPath && !localDraft) {
      // Hydrate local draft from API response
      setLocalDraft({
        id: profitPath.id,
        name: profitPath.name || "",
        vertical: profitPath.vertical || "",
        status: profitPath.status || "draft",
        active_landing_page_version_id: profitPath.active_landing_page_version_id || null,
        builder_data: {
          context: profitPath.builder_data?.context || {
            workspace: "",
            vertical: "",
            channel: "",
            platform: "",
            campaign_name: "",
            campaign_ref: "",
          },
          foundation: profitPath.builder_data?.foundation || {
            strategic_objective: "",
            core_strategy: "",
          },
          audience: profitPath.builder_data?.audience || {
            primary_audience: "",
            situation: "",
            core_problem: "",
            emotional_driver: "",
            awareness_stage: "",
            trigger_event: "",
          },
          offer: profitPath.builder_data?.offer || {
            offer_name: "",
            offer_type: "",
            core_promise: "",
            primary_benefit: "",
            differentiation: "",
            trust_proof: "",
            primary_cta: "",
            urgency: "",
          },
          messaging: profitPath.builder_data?.messaging || {
            angle: "",
            thesis: "",
            unique_mechanism: "",
            message_enemy: "",
            primary_hook: "",
            scroll_stopper: "",
            primary_emotion: "",
            emotional_outcome: "",
            emotional_narrative: "",
            tone: "",
            voice_notes: "",
          },
          creative: profitPath.builder_data?.creative || {
            concepts: [],
            generation_status: "idle",
          },
          funnel: profitPath.builder_data?.funnel || {
            funnel_type: null,
            steps: [],
            generation_status: "idle",
            source_concept_id: null,
          },
          script: profitPath.builder_data?.script || {
            script_instance_id: null,
            generation_status: null,
            generation_source: null,
          },
          // MA-LP-PROD-029 P2-5: LP content comes from LP API only — not builder_data.
          // Landing page data is fetched by LPMiniStep from the LP API.
          theme: profitPath.builder_data?.theme || null,
        },
      });
    }
  }, [isNewPath, profitPath, localDraft, entryProfile]);

  // MA-UI-069: Listen for navigateToStep events from Review panel
  useEffect(() => {
    const handleNavigateToStep = (event) => {
      const { step } = event.detail;
      if (step && BUILDER_STEPS.some(s => s.id === step)) {
        setActiveStep(step);
      }
    };
    
    window.addEventListener('navigateToStep', handleNavigateToStep);
    return () => window.removeEventListener('navigateToStep', handleNavigateToStep);
  }, []);

  // MA-LP-THEME-037: Fetch tenant brand config on mount — single source of token resolution.
  // This ensures ALL steps (Funnel, LP, etc.) have correct accent tokens from the start.
  // Stored in React state to trigger re-renders when tokens change.
  const [resolvedLpTokens, setResolvedLpTokens] = useState(null);
  const [tenantBrandConfig, setTenantBrandConfig] = useState(null);

  // MA-THEME-OVERRIDE-024: Effective Theme Resolver with styling_mode precedence
  // MA-THEME-OVERRIDE-FIX-091: Fixed to resolve theme locally when stored theme missing
  // Precedence:
  // 1. Custom override (styling_mode = "custom") → resolve from theme_config
  // 2. Brand inheritance (styling_mode = "inherit_brand" or no config) → use brand theme
  const getEffectiveTheme = useCallback(() => {
    const stylingMode = localDraft?.builder_data?.foundation?.theme_config?.styling_mode;
    
    // Priority 1: Custom theme override
    if (stylingMode === "custom") {
      // First check for resolved theme from backend
      if (localDraft?.builder_data?.theme?.tokens) {
        return localDraft.builder_data.theme;
      }
      
      // MA-THEME-OVERRIDE-FIX-091: If resolved theme not present, resolve locally
      // This fixes the state sync issue where theme_config exists but theme doesn't
      const themeConfig = localDraft?.builder_data?.foundation?.theme_config;
      if (themeConfig) {
        const { resolveTheme } = require('@/lib/themeEngine');
        const resolved = resolveTheme({
          preset: themeConfig.preset || 'professional',
          anchor_color: themeConfig.anchor_color,
          mode: themeConfig.mode,
        });
        if (resolved?.tokens) {
          return resolved;
        }
      }
    }
    
    // Priority 2: Brand default theme (for inherit_brand or no config)
    if (tenantBrandConfig?.default_theme?.tokens) {
      return tenantBrandConfig.default_theme;
    }
    
    // Ultimate fallback: null (will use DEFAULT_LP_THEME downstream)
    return null;
  }, [localDraft?.builder_data?.foundation?.theme_config, localDraft?.builder_data?.theme, tenantBrandConfig]);

  // MA-THEME-OVERRIDE-151: Use shared utility (imported at top)
  // Replaced inline function with shared utility from themeAdapters.js

  // MA-LP-PROD-029 P2-5: LP context state — single source of truth for LP data.
  // Populated by LPMiniStep via callback. Replaces builder_data.landing_page reads.
  const [lpApiContext, setLpApiContext] = useState(null);
  const handleLPContextChanged = useCallback((ctx) => {
    setLpApiContext(ctx);
  }, []);

  // MA-PPB-LP-055: Pre-fetch LP context on load so Review & sidebar don't depend on LP step visit
  useEffect(() => {
    const profitPathId = localDraft?.id;
    if (!profitPathId || profitPathId === "new") return;
    api.get(`/landing-pages/instance-for-profit-path/${profitPathId}`)
      .then(res => { if (res.data) setLpApiContext(res.data); })
      .catch(() => {});
  }, [localDraft?.id]);

  useEffect(() => {
    async function fetchBrandConfig() {
      try {
        const res = await api.get("/brand-config");
        if (res.data) {
          setTenantBrandConfig(res.data);
          // MA-THEME-PHASE7: Use canonical theme from brand config when available
          if (res.data.default_theme?.tokens) {
            setActiveBrand(res.data.default_theme);
          } else {
            const theme = extractLPTheme({ apiBrandConfig: res.data });
            setActiveBrand(theme);
          }
          // MA-THEME-OVERRIDE-020 Phase 1: Use Profit Path theme if present, else brand theme
          // This will be updated again when localDraft changes (see separate effect below)
          setResolvedLpTokens(getActiveTokens());
        }
      } catch {
        // Non-critical — DEFAULT_LP_THEME applies
      }
    }
    fetchBrandConfig();
  }, []);

  // MA-THEME-OVERRIDE-020 Phase 1: Update resolvedLpTokens when Profit Path theme changes
  useEffect(() => {
    const effectiveTheme = getEffectiveTheme();
    if (effectiveTheme?.tokens) {
      // Convert Profit Path canonical theme to LP tokens format
      const ppTokens = convertThemeToLpTokens(effectiveTheme);
      if (ppTokens) {
        setResolvedLpTokens(ppTokens);
      } else {
        // Fallback to brand-derived tokens
        setResolvedLpTokens(getActiveTokens());
      }
    } else if (tenantBrandConfig) {
      // No Profit Path theme, use brand default
      setResolvedLpTokens(getActiveTokens());
    }
  }, [localDraft?.builder_data?.theme, tenantBrandConfig, getEffectiveTheme]);

  // ==========================================================================
  // Save Mutation: Create or Update Profit Path
  // MA-UI-075: Normalize biased fields before API calls
  // ==========================================================================
  const saveMutation = useMutation({
    mutationFn: async (draftData) => {
      // MA-UI-075: Collect source counts for internal tracking (future AI/optimization)
      const sourceCounts = collectSourceCounts(draftData.builder_data);
      console.log("[MA-UI-075] Field source counts:", sourceCounts);
      
      // MA-THEME-OVERRIDE-097: Log exact save payload for debugging
      console.log("═══════════════════════════════════════════════════");
      console.log("🔍 SAVE PAYLOAD DEBUG - BEFORE NORMALIZATION");
      console.log("theme_config:", JSON.stringify(draftData?.builder_data?.foundation?.theme_config, null, 2));
      console.log("═══════════════════════════════════════════════════");
      
      // MA-UI-075: Normalize data before sending to API (convert {value, source} → raw values)
      const normalizedData = {
        ...draftData,
        builder_data: normalizeForAPI(draftData.builder_data),
      };
      
      // MA-THEME-OVERRIDE-097: Log after normalization
      console.log("═══════════════════════════════════════════════════");
      console.log("🔍 SAVE PAYLOAD DEBUG - AFTER NORMALIZATION");
      console.log("theme_config:", JSON.stringify(normalizedData?.builder_data?.foundation?.theme_config, null, 2));
      console.log("Full payload being sent to API:", JSON.stringify(normalizedData, null, 2));
      console.log("═══════════════════════════════════════════════════");
      
      // MA-UI-076-V3: Remove internal tracking fields before API call
      // _source_counts is for internal use only, not persisted
      delete normalizedData._source_counts;
      
      if (isNewPath) {
        // Create new profit path
        const res = await api.post("/profit-paths", normalizedData);
        return res.data;
      } else {
        // Update existing profit path
        const res = await api.patch(`/profit-paths/${id}`, normalizedData);
        return res.data;
      }
    },
    onSuccess: (data) => {
      setSaveStatus("saved");
      markDirty(false);
      setTimeout(() => setSaveStatus("idle"), 2000);
      
      // MA-THEME-OVERRIDE-097: Update localDraft with resolved theme from backend
      console.log("═══════════════════════════════════════════════════");
      console.log("💾 SAVE SUCCESS - Updating localDraft");
      console.log("Response builder_data.theme:", data?.builder_data?.theme?.name);
      console.log("Response builder_data.theme_source:", data?.builder_data?.theme_source);
      console.log("═══════════════════════════════════════════════════");
      
      // Update localDraft with response data (includes resolved theme from backend)
      if (data?.builder_data) {
        setLocalDraft(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            id: data.id || prev.id,  // Update ID if new path
            builder_data: {
              ...prev.builder_data,
              // MA-THEME-OVERRIDE-097: Critical - Update with resolved theme from backend
              theme: data.builder_data.theme,
              theme_source: data.builder_data.theme_source,
              // Keep other builder_data fields from prev (user's local edits)
              foundation: data.builder_data.foundation || prev.builder_data.foundation,
              context: data.builder_data.context || prev.builder_data.context,
              audience: data.builder_data.audience || prev.builder_data.audience,
              offer: data.builder_data.offer || prev.builder_data.offer,
              messaging: data.builder_data.messaging || prev.builder_data.messaging,
              creative: data.builder_data.creative || prev.builder_data.creative,
              funnel: data.builder_data.funnel || prev.builder_data.funnel,
              script: data.builder_data.script || prev.builder_data.script,
            }
          };
        });
        
        console.log("✅ localDraft updated with resolved theme");
      }
      
      // If this was a new path, navigate to edit mode
      if (isNewPath && data?.id) {
        navigate(`/app/profit-paths/${data.id}/builder`, { replace: true });
      }
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ["profit-paths"] });
      queryClient.invalidateQueries({ queryKey: ["profit-path", id] });
      
      toast({
        title: "Saved",
        description: isNewPath ? "Profit path created" : "Changes saved",
      });
      
      // MA-303 Phase 1: Sync theme to linked funnel on Foundation Step save
      // Only sync if funnel exists (don't sync for new PPs without generated funnel)
      const linkedFunnelId = data?.builder_data?.funnel?.funnel_instance_id;
      if (linkedFunnelId) {
        console.log("[MA-303][Phase1] Detected linked funnel, syncing theme...");
        console.log("[MA-303][Phase1] Funnel ID:", linkedFunnelId);
        
        // MA-304 SYNC FIX: Use finalTheme (canonical theme with complete tokens)
        // finalTheme is the single source of truth computed by backend theme_resolver
        const effectiveTheme = (() => {
          // Priority 1: MA-304 canonical finalTheme (complete with tokens + computed formats)
          if (data?.builder_data?.finalTheme?.tokens) {
            console.log("[MA-304][SYNC] Using PP finalTheme:", data.builder_data.finalTheme.name || data.builder_data.finalTheme.theme_id);
            return data.builder_data.finalTheme;
          }
          
          // Priority 2: Brand default theme (fallback)
          if (tenantBrandConfig?.default_theme?.tokens) {
            console.log("[MA-304][SYNC] Fallback to brand theme");
            return tenantBrandConfig.default_theme;
          }
          
          // Fallback: null (should not happen after save, but safety check)
          console.warn("[MA-304][SYNC] No finalTheme found to sync");
          return null;
        })();
        
        if (effectiveTheme?.tokens) {
          console.log("[MA-303][Phase1] Effective theme:", {
            theme_id: effectiveTheme.theme_id || effectiveTheme.name,
            mode: effectiveTheme.mode,
            has_tokens: true
          });
          
          // Compute appearance from theme
          const funnelAppearance = {
            ...themeToFunnelAppearance(effectiveTheme),
            iconBackgroundColor: effectiveTheme.tokens.primary || effectiveTheme.tokens.accent_primary,
          };
          
          console.log("[MA-303][Phase1] Computed appearance:", {
            threadColor: funnelAppearance.threadColor,
            containerColor: funnelAppearance.containerColor,
            userColor: funnelAppearance.userColor,
            botTextColor: funnelAppearance.botTextColor
          });
          
          // MA-304 SYNC FIX: Sync both theme + finalTheme to funnel
          // P4-R2C: versioned, serialized against the other draft writes, and
          // reported. The `.catch` here read "Don't block UI - this is a
          // non-critical enhancement" and swallowed the error, so a theme that
          // failed to reach the funnel looked identical to one that arrived.
          // It still does not block the UI -- it just no longer claims success.
          saveFunnelDraftVersioned(
            linkedFunnelId,
            {
              appearance: funnelAppearance,
              builder_data: {
                theme: effectiveTheme,  // Keep for backward compatibility
                finalTheme: data?.builder_data?.finalTheme  // MA-304: Canonical theme
              },
              styling_mode: data?.builder_data?.foundation?.theme_config?.styling_mode || "inherit_brand"
            },
            { ...draftSyncOptions("appearance") }
          ).then((result) => {
            if (!result.ok) {
              console.error("[MA-304][SYNC] finalTheme sync failed:",
                            result.conflict ? "version conflict" : result.error?.message);
              return;
            }
            // MA-303B FIX: Invalidate cache to force Canvas re-fetch
            queryClient.invalidateQueries(['funnel', linkedFunnelId]);
            
            console.log("[MA-304][SYNC] finalTheme synced to funnel successfully");
            toast({
              title: "Theme synced",
              description: "Funnel appearance updated",
            });
          });
        } else {
          console.warn("[MA-303][Phase1] No effective theme found, skipping sync");
        }
      } else {
        console.log("[MA-303][Phase1] No linked funnel detected, skipping theme sync");
      }
      
      // MA-304 Extension: Sync theme to linked Landing Page draft
      // MA-304 FIX: Use landing_page_id (actual LP ID), fallback to legacy instance_id
      console.log("[MA-304][LP-SYNC] landing_page_id from response:", data?.landing_page_id);
      console.log("[MA-304][LP-SYNC] landing_page_instance_id from response:", data?.landing_page_instance_id);
      const linkedLpId = data?.landing_page_id || data?.landing_page_instance_id;
      console.log("[MA-304][LP-SYNC] chosen linkedLpId:", linkedLpId);
      
      if (linkedLpId) {
        console.log("[MA-304][LP-SYNC] Detected linked LP, syncing theme...");
        console.log("[MA-304][LP-SYNC] LP ID:", linkedLpId);
        
        if (data?.builder_data?.finalTheme) {
          api.put(`/landing-pages/${linkedLpId}/draft`, {
            final_theme: data.builder_data.finalTheme
          })
          .then(() => {
            console.log("[MA-304][LP-SYNC] ✅ finalTheme synced to LP draft successfully");
            toast({
              title: "Landing page theme synced",
              description: "Preview will reflect new theme",
            });
          })
          .catch((err) => {
            console.error("[MA-304][LP-SYNC] ❌ LP sync failed:", err.message);
            // Don't block UI - non-critical
          });
        } else {
          console.warn("[MA-304][LP-SYNC] No finalTheme to sync");
        }
      } else {
        console.log("[MA-304][LP-SYNC] No linked LP detected, skipping sync");
      }
    },
    onError: (err) => {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
      
      // MA-UI-076-V3: Handle Pydantic validation errors (array format)
      let errorMessage = "Please try again";
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation errors are arrays of objects with 'msg' field
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
      }
      
      toast({
        title: "Save failed",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // ==========================================================================
  // Field Change Handler (supports nested paths like "builder_data.foundation.strategic_objective")
  // ==========================================================================
  // MA-UI-074: When user edits a biased field, update source to "user"
  // This ensures user edits permanently override entry bias
  // ==========================================================================
  const handleFieldChange = useCallback((path, value) => {
    setLocalDraft((prev) => {
      if (!prev) return prev;
      
      const parts = path.split(".");
      if (parts.length === 1) {
        // Top-level field
        return { ...prev, [path]: value };
      }
      
      // Nested field (e.g., builder_data.foundation.strategic_objective)
      const newDraft = { ...prev };
      let current = newDraft;
      
      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        current[part] = { ...current[part] };
        current = current[part];
      }
      
      const fieldName = parts[parts.length - 1];
      const currentValue = current[fieldName];
      
      // MA-PPB-MSG-054: Biased field normalization on write.
      // When a value is written (by user edit or AI), always store the canonical
      // runtime type (string). The biased wrapper must not persist after mutation.
      if (currentValue && typeof currentValue === "object" && ("source" in currentValue || "value" in currentValue)) {
        // Previous value was a biased/tracked field — unwrap: store raw value only
        current[fieldName] = value;
      } else {
        current[fieldName] = value;
      }
      
      return newDraft;
    });
    markDirty(true);
  }, [markDirty]);

  // ==========================================================================
  // Save Handler
  // ==========================================================================
  const handleSave = useCallback(() => {
    if (!localDraft) return;
    setSaveStatus("saving");
    saveMutation.mutate(localDraft);
  }, [localDraft, saveMutation]);

  // ==========================================================================
  // Next Step Handler (Save + Advance)
  // ==========================================================================
  const handleNext = useCallback(() => {
    const currentIndex = BUILDER_STEPS.findIndex((s) => s.id === activeStep);
    if (currentIndex < BUILDER_STEPS.length - 1) {
      // Only save if there are unsaved changes AND the data is ready for the API
      // For new paths, require at least a name before saving (name is required by the API)
      const canSave = isDirty && localDraft && (!isNewPath || localDraft.name?.trim());
      if (canSave) {
        setSaveStatus("saving");
        saveMutation.mutate(localDraft);
      }
      
      // MA-PPB-AUTO-APPROVE: When leaving the Funnel step, auto-approve all
      // AI-generated funnel steps so the Funnel Builder review gate is satisfied
      if (activeStep === "funnel") {
        const funnelData = localDraft?.builder_data?.funnel || {};
        const funnelId = funnelData.funnel_instance_id;
        const isAIGenerated = funnelData.generation_status === "complete";
        if (funnelId && isAIGenerated) {
          api.post(`/ai-builder/funnels/${funnelId}/mark-reviewed`, { node_ids: "all" })
            // P4-R2C-PROD-FIX-001 (#796): the approval advances the funnel's
            // `draft_version`, so the SHARED cache must take the committed
            // value or the next versioned draft write reports a conflict the
            // operator caused themselves and cannot act on. The funnel's nodes
            // are not part of this page's `localDraft` (that is builder_data),
            // so there is no unreconciled content to rebase here.
            .then((res) => {
              const committed = res?.data?.draft_version;
              if (Number.isInteger(committed)) {
                adoptFunnelDraftVersion(funnelId, committed);
              }
              console.log("[MA-PPB-AUTO-APPROVE] All funnel steps auto-approved");
            })
            .catch((err) => console.warn("[MA-PPB-AUTO-APPROVE] Failed to auto-approve:", err));
        }
      }
      
      // Advance to next step
      setActiveStep(BUILDER_STEPS[currentIndex + 1].id);
    }
  }, [activeStep, localDraft, saveMutation, isDirty, isNewPath]);

  // ==========================================================================
  // Unsaved Changes Guard
  // ==========================================================================
  const blocker = useBlocker(() => isDirtyRef.current);

  // Browser beforeunload handler (back/forward/close/reload)
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // ==========================================================================
  // Navigation
  // ==========================================================================
  const handleBack = () => {
    navigate("/app/profit-paths");
  };

  // Loading state
  if (!isNewPath && isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Error state
  if (!isNewPath && error) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4">
        <p className="text-slate-400">Failed to load profit path</p>
        <Button variant="outline" onClick={handleBack}>
          Back to Profit Paths
        </Button>
      </div>
    );
  }

  // Waiting for draft initialization
  // MA-RBAC-BATCH3-FRONTEND-PERMISSION-INFRA-PHASE4-115B: Show loading while permissions resolve
  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="text-sm text-slate-200/70">Loading permissions...</p>
        </div>
      </div>
    );
  }
  
  // MA-RBAC-BATCH3-FRONTEND-PERMISSION-INFRA-PHASE4-115B: Don't render if no access
  // useEffect above handles redirect
  if (!hasFullAccess) {
    return null;
  }

  if (!localDraft) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="profit-path-builder">
      {/* Glass Action Bar - EXACT MATCH: FunnelDetailsPage PageActionBar (UI LOCKED) */}
      <div 
        className="h-12 flex items-center justify-between px-4 mx-3 mt-3 flex-shrink-0 rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl"
        data-testid="page-action-bar"
      >
        {/* Left: Back + Document Name */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={handleBack}
                  data-testid="builder-back-btn"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Back to Profit Paths</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="flex items-center gap-1.5 min-w-0 max-w-[240px]">
            <span className="text-sm text-slate-100 truncate">
              {localDraft.name || "New Profit Path"}
            </span>
          </div>
        </div>
        
        {/* Center: Tab Selector (UI LOCKED) */}
        <div className="flex items-center gap-3">
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 flex-1 justify-end">
          {/* Save Status Indicator */}
          {saveStatus !== "idle" && (
            <div className="flex items-center gap-1 text-xs text-slate-400 mr-1">
              {saveStatus === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
              {saveStatus === "saved" && <Check className="h-3 w-3 text-green-400" />}
              {saveStatus === "error" && <span className="text-red-400">Error</span>}
              <span className={
                saveStatus === "saved" ? "text-green-400" : 
                saveStatus === "error" ? "text-red-400" : ""
              }>
                {saveStatus === "saving" ? "Saving..." : 
                 saveStatus === "saved" ? "Saved" : ""}
              </span>
            </div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs border-white/10"
            onClick={handleSave}
            disabled={saveMutation.isPending}
            data-testid="builder-save-draft-btn"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Save className="h-3 w-3 mr-1" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* 3-Column Layout - EXACT: VisualFunnelBuilder (UI LOCKED) */}
      <div className="flex-1 flex gap-3 p-3 overflow-hidden">
        {/* Left Card - Steps Panel */}
        <div
          className={cn(
            "w-[320px] min-w-[320px] flex-shrink-0 flex flex-col overflow-hidden",
            "rounded-2xl border border-white/[0.08]",
            "bg-gradient-to-b from-white/[0.04] to-white/[0.02]",
            "backdrop-blur-xl"
          )}
        >
          <BuilderStepsPanel
            steps={BUILDER_STEPS}
            activeStep={activeStep}
            onStepClick={setActiveStep}
            draft={localDraft}
            lpApiContext={lpApiContext}
          />
        </div>

        {/* Center Card - Work Area */}
        <div
          className={cn(
            "flex-1 min-w-[480px] flex flex-col overflow-hidden",
            "rounded-2xl border border-white/[0.08]",
            "bg-gradient-to-b from-white/[0.04] to-white/[0.02]",
            "backdrop-blur-xl",
            "shadow-[0_0_60px_-12px_rgba(99,102,241,0.15)]"
          )}
        >
          <BuilderWorkArea 
            step={activeStep} 
            draft={localDraft} 
            onFieldChange={handleFieldChange}
            onNext={handleNext}
            getStepCompletion={(stepId) => checkStepCompletion(stepId, localDraft, lpApiContext)}
            resolvedLpTokens={resolvedLpTokens}
            onLPContextChanged={handleLPContextChanged}
            lpApiContext={lpApiContext}
            tenantBrandConfig={tenantBrandConfig}
          />
        </div>

        {/* Right Card - Context Panel */}
        <div
          className={cn(
            "w-[340px] min-w-[340px] flex-shrink-0 flex flex-col overflow-hidden",
            "rounded-2xl border border-white/[0.08]",
            "bg-gradient-to-b from-white/[0.04] to-white/[0.02]",
            "backdrop-blur-xl"
          )}
        >
          <BuilderContextPanel draft={localDraft} activeStep={activeStep} lpApiContext={lpApiContext} />
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={blocker.state === "blocked"}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              You have unsaved changes. Would you like to save before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => blocker.reset()}
              className="bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="ghost"
              onClick={() => {
                markDirty(false);
                blocker.proceed();
              }}
              className="text-slate-400 hover:text-slate-300"
            >
              Discard
            </Button>
            <AlertDialogAction
              onClick={() => {
                if (localDraft) {
                  setSaveStatus("saving");
                  saveMutation.mutate(localDraft, {
                    onSuccess: () => {
                      markDirty(false);
                      blocker.proceed();
                    },
                    onError: () => {
                      blocker.proceed();
                    },
                  });
                } else {
                  blocker.proceed();
                }
              }}
              className="bg-indigo-600 hover:bg-indigo-500 text-white"
            >
              Save & Exit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
